import { html, render, nothing } from 'lit-html';
import { createBulkApplyController } from 'b2b-plus/bulk-apply-controller';
import flatpickr from 'flatpickr';
import {
  stylesheetLinksTemplate,
  emptyInventoryTemplate,
  variantHeaderThTemplate,
  productDetailsTemplate,
  filterBarTemplate,
  cartBulkApplyControlsTemplate,
  productTableLayoutTemplate,
  inventoryRowTemplate,
  addShipmentButtonTemplate,
  optionTabsTemplate,
  orderSummaryWrapperTemplate,
  orderSummaryGridTemplate,
  orderSummaryBarTemplate,
  summaryTabsTemplate,
  cartViewToggleTemplate,
  manualSaveBarTemplate,
  cartIssueListTemplate,
  summaryPriceLabelFromCents,
} from 'b2b-plus/product-table-templates';
import {
  getInventory,
  getInventoryFilterData,
  inventoryLinesForVariant,
  resolveThresholdColor,
  resolveInventoryDateWindow,
  toISODate,
  startOfLocalDay,
  addCalendarDays,
  parseISODateLocal,
  shipWindowEndBounds,
  clampShipWindowEnd,
  getShipWindowLengths,
  isSingleDayShipWindow,
  analyzeSavedDeliveryWindow,
  isDeliveryWindowPast,
  isAtsImmediateOrderType,
  atsImmediatePickerEnd,
  getAtsImmediatePickerHorizonDays,
} from 'b2b-plus/inventory';
import {
  formatMoney,
  initTooltips,
  toastNotification,
  copyToClipboard,
} from 'b2b-plus/ui';
import {
  clearCart,
  getProductRecord,
  saveProductRecord,
  migrateProductRecord,
  removeShipmentRow,
  combinationKey,
  resolveCartComboForDomKey,
  optionStorageKey,
  isPrebookOrderType,
  optionLabel,
  inferSplitOptionIndex,
  splitOptionDisplayName,
  groupVariantsByOption,
  variantIdsForOption,
  flattenProductRecordToLineItems,
  findMatchedInventoryLine,
  b2bPlusString,
  getProductJSON,
  enrichProductJSONWithMsrp,
  enrichProductJSONWithSeasonPrices,
  attachVariantNetsuiteMetafields,
  getSharedShipmentDates,
  getSharedShipmentDateForTypeSync,
  setSharedShipmentDate,
  updateDeliveryWindowsForOrderTypeGroup,
  rollLockedShipmentWindowsToToday,
  lockedHorizonWindowForCombo,
  ensureOrderContext,
  LOCKED_SHIPMENT_WINDOWS_ROLLED_EVENT,
} from 'b2b-plus/utils';
import {
  computeRowVariantCorrections,
  computeDeliveryWindowCorrection,
  canonicalizeLiveState,
  computeSiblingCapCorrections,
  isInventoryDataReady,
  joinDeliveryWindow,
  formatDeliveryWindowDisplay,
  resolveOptionGroups,
  emptyLiveRow,
  emptyLiveCombo,
  liveTabHasAnyQty,
  clearLiveStatesInPlace,
  computeVariantsWithInventory,
  computeVariantsWithStock,
  displayDateFlatpickrOptions,
  buildOptionImageMap,
  inventoryDateStrsFromRow,
  buildRecordForTab,
  buildLiveTabFromRecord,
  buildSummaryRowsFromLiveState,
  amendLiveStatesDeliveryWindowsByOrderTypeGroup,
  defaultPrefillDeliveryDates,
  getMergedInventoryRows,
  computeCombinationCaps,
  computePooledInventory,
  foldQuantityRuleMaxIntoCaps,
  resolveInventoryPoolingOptions,
  buildQuantityRuleMap,
  effectiveMaxQty,
  hasExistingSavedCart,
  parseCartBulkApplyMeta,
  buildAddShipmentCombinations,
  makeSummaryColumnLabeler,
  normalizeOrderTypeGroup,
  isPrePopulatedShipmentDateEnabled,
  isLockedShipmentWindowEnabled,
  isAutoHorizonEndShipmentEnabled,
  parseDeliveryWindowParts,
  usesShipmentStartSeasonPrice,
  seasonPricesFromVariants,
  seasonalCentsForStartDate,
  unitCentsForInventorySelection,
  isPastDateAutoAdjustType,
  PAST_DATE_ADJUSTED_KEY,
  formatDisplayDeliveryWindow,
  formatDisplayWindowString,
} from 'b2b-plus/product-table-sdk';
import { CART_ISSUE_TYPES, cartIssueCellStatus } from 'b2b-plus/cart-sdk';

/** Safe `window.b2bPlus.config` for collection / modal contexts where config may load late. */
function b2bConfig() {
  return window.b2bPlus?.config ?? {};
}

/**
 * Normalised quantity-rule (catalog min/max) options from config. Disabled by
 * default — `config.quantityRule.enable` opts in. `enforceMin` / `enforceMax`
 * default to true so the single `enable` flag turns on both halves.
 * @returns {{ enabled: boolean, enforceMin: boolean, enforceMax: boolean }}
 */
function quantityRuleOptions() {
  const c = b2bConfig().quantityRule ?? {};
  return {
    enabled:    c.enable === true,
    enforceMin: c.enforceMin !== false,
    enforceMax: c.enforceMax !== false,
  };
}

/**
 * Normalised pooled-inventory options from `config.inventory.pooling`.
 * Disabled by default; `enabled` (the `pooled_inventory` switch) opts in.
 * @returns {{ enabled: boolean, acrossSeason: boolean, byType: boolean }}
 */
function inventoryPoolingOptions() {
  return resolveInventoryPoolingOptions(b2bConfig().inventory);
}

/**
 * Module-level dependency bundle the product-table-sdk render-pipeline
 * helpers (`getMergedInventoryRows`, `computeCombinationCaps`,
 * `inventoryDateStrsFromRow`) need. Built once so call sites don't have to
 * assemble the same object each render. SDK functions destructure only the
 * keys they use; extras are ignored.
 */
const PT_SDK_DEPS = {
  resolveInventoryDateWindow,
  linesForVariant: inventoryLinesForVariant,
  toISODate,
  combinationKey,
  isPrebookOrderType,
  // Date helpers used by `defaultPrefillDeliveryDates`.
  parseISODateLocal,
  addCalendarDays,
  clampShipWindowEnd,
  // Split-option grouping used by `resolveOptionGroups`.
  inferSplitOptionIndex,
  groupVariantsByOption,
};

/** Inventory qty div-table; matches `[data-b2b-plus-inventory-tbody]` inside the `[data-b2b-plus-inventory-table]` wrapper. */
const INVENTORY_TABLE_BODY_SELECTOR = '[data-b2b-plus-inventory-table] [data-b2b-plus-inventory-tbody]';

/**
 * Write text into a node lit-html may also own.
 * `textContent` deletes the comment marker in front of a text binding, and the
 * next full `render()` (cart-record hydrate after adding a shipment) throws
 * "Cannot set properties of null (setting 'data')".
 * @param {Element|null|undefined} el
 * @param {string|number} value
 */
function setSlotText(el, value) {
  if (!el) return;
  const text = value == null ? '' : String(value);
  for (const node of el.childNodes) {
    if (node.nodeType !== Node.COMMENT_NODE) continue;
    const next = node.nextSibling;
    if (next?.nodeType === Node.TEXT_NODE) {
      next.data = text;
      return;
    }
  }
  el.textContent = text;
}

/** Stable unique id for an added shipment row (avoids same-ms collisions). */
function newShipmentRowId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `shipment-${crypto.randomUUID()}`;
  }
  return `shipment-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Count distinct inventory types (Immediate / Future / Prebook …) that hold a
 * positive qty across the given v2 cart records. Combos with no qty are ignored.
 * @param {object[]} records
 * @returns {number}
 */
function countCommittedInventoryTypes(records) {
  const types = new Set();
  for (const record of records) {
    for (const combo of Object.values(record ?? {})) {
      const hasQty = (combo?.rows ?? []).some((row) =>
        Object.values(row?.byVariantId ?? {}).some((qty) => Number(qty) > 0));
      if (hasQty) types.add(String(combo?.type ?? '').trim().toLowerCase());
    }
  }
  return types.size;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Section index — `class B2BPlusProductTable` is long. This map points at the
 * region each concern lives in. Search for the method name to jump there.
 *
 *   Public API surface
 *     fields: productJSON, inventoryData, cartData, cartRecord, isCart
 *     methods: setPrefetchedData, applyDeliveryDate, getBulkSourceSnapshot,
 *              applyCartBulkSnapshot, syncCartBulkApplyFromHost, validate
 *
 *   Lifecycle
 *     connectedCallback, disconnectedCallback, initializeProductData
 *
 *   Render pipeline
 *     #renderAndInit, #productTableRootTemplate, #variantHeaderTemplates,
 *     #inventoryDataToRowTemplates, #appendRowTemplateBatches,
 *     #buildCombinationCaps (merged-rows + caps math lives in
 *     b2b-plus/product-table-sdk as getMergedInventoryRows /
 *     computeCombinationCaps / inventoryDateStrsFromRow / inventoryRowKey)
 *
 *   Tab management (PDP / quickview split-option tabs)
 *     #initOptionTabHandler, #initOptionSelectListener, #switchOptionTab,
 *     #prewarmTabRecords, #initSelectedOption,
 *     #storageHandle, #storageHandleFor, #tabSwitchAbort, #lifetimeAbort
 *
 *   liveState (the post-migration single source of truth, see b2b-docs/13)
 *     #liveStates, #liveStateFor, #currentLiveState,
 *     #initLiveStateFromRecord, #buildCurrentRecord (delegates to
 *     buildRecordForTab in product-table-sdk),
 *     #buildCurrentRecordFromDom
 *
 *   Validation pass (runs after every render; PDP/quickview only)
 *     Rules live in b2b-plus/product-table-sdk (canonicalizeLiveState,
 *     computeRowVariantCorrections, computeDeliveryWindowCorrection,
 *     computeSiblingCapCorrections). The class hosts the coordinator and the
 *     apply layer:
 *     #validateRestoredData (coordinator), #resolveRowRefs,
 *     #snapshotRowVariantsForValidation,
 *     #applyRowVariantCorrections, #applyDeliveryWindowCorrections,
 *     #applySiblingCapCorrections, #localizeError,
 *     #persistValidatedRecord
 *
 *   Input wiring (per row + per control)
 *     #initImageSliders, #initDatePickers, #initQtyInputs,
 *     #initNoteToggles, #initAtcButton, #initSummaryClearBtn,
 *     #initSummaryTabHandler, #initOrderSummarySync, #initCartViewToggle,
 *     #setCartViewMode, #initAutoSave, #updateSiblingCaps, #validateQtyInput
 *
 *   Shipment rows (add / insert / remove)
 *     #initAddShipmentButton, #addShipmentRow, #insertShipmentRow,
 *     #wireRowBehavior, #initRemoveShipmentRows, #wireRemoveShipmentRow
 *
 *   Order summary (gated on `enableOrderSummary`; suppressed entirely when off)
 *     #renderOrderSummary — sync: paints filter tabs into [data-summary-tabs-root],
 *                          grid into [data-summary-grid-root], bar into
 *                          [data-summary-bar-root] (PDP/quickview)
 *     #buildSummaryRowsFromLiveState, #buildSummaryRowsFromDom, #orderSummaryOpts
 *     Filter tabs (PDP/quickview, `enableProductTableSplit`): #summaryFilterValue
 *       gates which option group the grid shows; #initSummaryTabHandler switches it.
 *     Clear handler (#initSummaryClearBtn) dispatches to #clearAllSummarySelections
 *       (All tab) or #clearSummaryOptionValue (a value tab); both reset to All.
 *     clearCartLockConflictSelections zeros every row whose type cart-lock
 *       disabled, so those quantities leave the summary.
 *     ATC / view-toggle handlers stay parent-owned (see Input wiring).
 *
 *   Controller host interfaces (behaviour factored to sibling modules)
 *     #buildBulkApplyHost → b2b-plus-bulk-apply.js
 *     #buildFiltersHost   → b2b-plus-filters-controller.js
 *
 *   Pure helpers extracted to sibling modules — referenced by import only:
 *     analyzeSavedDeliveryWindow        → b2b-plus/inventory
 *     inferSplitOptionIndex             → b2b-plus/utils
 *     groupVariantsByOption             → b2b-plus/utils
 *     variantIdsForOption               → b2b-plus/utils
 *     resolveCartComboForDomKey         → b2b-plus/utils (via cart-storage)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * @interface B2BPlusProductTablePublicAPI
 *
 * Public contract exposed by `<b2b-plus-product-table>`.
 * `B2BPlusCart` and any per-store integration code may read the fields below and call
 * the marked methods. Everything else is private implementation detail.
 *
 * Call sequence for cart usage:
 *   1. `setPrefetchedData(data)` — inject before inserting the element into the DOM
 *   2. (insert element — `connectedCallback` runs)
 *   3. `applyDeliveryDate()`, `getBulkSourceSnapshot()`, `applyCartBulkSnapshot()`,
 *      `syncCartBulkApplyFromHost()` — callable any time after connection
 *   4. `validate()` — called by cart during checkout orchestration
 *
 * @property {object|null}   productJSON    Product JSON parsed from embedded <script>
 * @property {object|null}   inventoryData  Live inventory API response
 * @property {object[]}      cartData       Flat line-item array for external consumers
 * @property {object}        cartRecord     v2 nested IDB record
 * @property {boolean}       isCart         true when rendered inside the cart page (set from data-is-cart)
 */
class B2BPlusProductTable extends HTMLElement {

  // ── Public data fields (read by parent components) ──────────────────────────
  productJSON   = null;
  inventoryData = null;
  cartData      = [];   // flat view of cartRecord for external consumers
  /** @type {object} v2 nested record: { [combinationKey]: { …meta, rows: [] } } */
  cartRecord    = {};
  /** Set by `<b2b-plus-cart>` via `data-is-cart`; true when rendered inside the cart page. Initialised in connectedCallback; do not read before the element is connected. */
  isCart        = false;

  // ── Private fields ───────────────────────────────────────────────────────────
  /**
   * Per-combination inventory caps from the live API response.
   * { [combinationKeyStr]: { [variantId]: number|null } }
   * null value = Prebook (no cap).  Never persisted to IDB.
   * @type {Map<string, object>}
   */
  #combinationCaps = new Map();
  /**
   * Pooled-inventory scratch state. Populated only when
   * `config.inventory.pooling.enabled` is on (else all remain empty/null and the
   * element behaves exactly as before).
   *   #rawCombinationCaps — physical per-combo caps BEFORE pooling (null = disabled).
   *   #mergedRows         — merged inventory rows from the last cap build (for live recompute).
   *   #poolByCombo        — comboKey → Set<comboKey> of co-pooled windows.
   *   #consolidatedMap    — comboKey → Set<variantId> that borrowed pooled stock.
   * @type {Map<string, object>|null}
   */
  #rawCombinationCaps = null;
  #mergedRows = [];
  #poolByCombo = new Map();
  #consolidatedMap = new Map();
  #liveStates = new Map();
  /**
   * In-flight `getProductRecord()` read for the PDP path, stashed by
   * `initializeProductData()` so the first render doesn't wait on it — see
   * `#applyPendingCartRecord()`. `null` once consumed (or when there's nothing to await).
   * @type {Promise<object>|null}
   */
  #pendingCartRecordPromise = null;
  /**
   * Option values (the split-option key, '' for non-split) that currently have a
   * saved, non-empty order in IndexedDB. Tracks SAVED state across every tab —
   * not in-progress live edits — so the "Add to Order" / "Update Order" label
   * reads "Update" whenever ANY colour already has a saved order, even when the
   * tab on screen is empty. Updated on load, prewarm, tab switch, save, and clear.
   * @type {Set<string>}
   */
  #savedOrderValues = new Set();

  /**
   * Memoised per-variant catalog quantity-rule map ({ variantId: { min, max } })
   * derived from `productJSON.quantity_rules` + config. Empty when the feature is
   * disabled or no rules are present. Built once productJSON is available.
   * @type {Record<string, { min: number|null, max: number|null }>|null}
   */
  #quantityRules = null;

  /** Injected via `setPrefetchedData()` before connect (cart / quick-view). */
  #prefetchedData = null;
  /** @type {boolean} */
  #inventoryFetchFailed = false;
  /** @type {number|null} */
  #debounceTimer        = null;
  /** @type {Promise<void>|null} Most recent auto-save write, awaited by flushPendingSave. */
  #pendingSavePromise   = null;
  /**
   * Bumped when structural cart mutations (e.g. remove-shipment) must invalidate
   * in-flight DOM-based auto-saves so they cannot resurrect a deleted row.
   * @type {number}
   */
  #saveEpoch            = 0;
  /** @type {number|null} requestAnimationFrame id coalescing rapid order-summary repaints. */
  #summaryRenderRaf     = null;
  /**
   * Bumped at the start of every `#renderAndInit`. An in-flight cart-lock pass
   * from the previous render bails so it cannot splice DOM into the tree the
   * cart-record hydrate is about to render.
   * @type {number}
   */
  #cartLockGen          = 0;
  /**
   * Serialises cart-lock conflict clears so a second lock pass (the clear
   * itself dispatches `b2b:cart-saved`) cannot persist a stale snapshot over
   * the one that just zeroed the disabled rows.
   * @type {Promise<void>}
   */
  #cartLockPersistChain = Promise.resolve();
  /** @type {Function|null} */
  #beforeUnloadHandler  = null;
  /** @type {Array<{target: EventTarget, type: string, handler: Function}>} */
  #listeners            = [];
  /** @type {ShadowRoot|B2BPlusProductTable} Open shadow when attached (see connectedCallback). */
  #shadow;
  /** @type {Map<HTMLInputElement, object>|undefined} input → flatpickr instance; set in #initDatePickers. */
  #flatpickrInstances;
  /** @type {Function|undefined} Cleanup fn returned by initTooltips. */
  #cleanupTooltips;
  /** @type {boolean} Tracks whether the user has changed any qty input. */
  #userHasInteracted = false;
  /** @type {Function|undefined} Bound updateButtonState fn set in #initAtcButton. */
  #updateButtonState;
  /** @type {Function|undefined} Bound auto-save fn set in #initAutoSave. */
  #onAutoSave;
  /**
   * @type {boolean|null} Memoised manual-save mode (cart + config.cart.autoUpdateQuantities === false).
   * null = not yet resolved. When true, qty/date/note edits are staged behind a
   * "Save changes" button instead of auto-persisting to IndexedDB. See #isManualSave.
   */
  #manualSaveMode = null;
  /** @type {boolean} Manual-save mode only: unsaved edits pending a "Save changes" click. */
  #isDirty = false;
  /**
   * @type {string|null} Active cart date-filter window ('YYYY-MM-DD' or 'start to end'),
   * or null on the "All" tab. Set by <b2b-plus-cart> via setActiveDateFilter so the
   * manual-save bar can warn that edits are scoped to a single delivery window.
   */
  #activeDateFilter = null;
  /** @type {string|null} Active option value for product table split tab filtering. null = feature disabled or not yet set. */
  #selectedOptionValue = null;
  /**
   * @type {string|null} Active order-summary filter tab (PDP / quickview only).
   * null/'' = the "All" tab — summary shows every option group. A non-empty
   * value filters the summary grid to that split-option value and flips the
   * Clear button to per-value mode. Independent of `#selectedOptionValue`
   * (which drives the inventory matrix), so filtering the summary never
   * re-renders the matrix.
   */
  #summaryFilterValue = null;
  /** @type {string[]} Stylesheet hrefs stored after connectedCallback resolves them, reused on option tab switch. */
  #stylesheetHrefs = [];
  /** @type {Function|null} Document-level handler for `b2b:option-selected`; set once in connectedCallback, removed in disconnectedCallback. */
  #optionSelectHandler = null;
  /** @type {string[]} Ordered visible variant IDs for the most recent full render; drives order summary columns. */
  #renderedVariantIds = [];
  /** @type {'summary'|'edit'} Active cart view mode; only relevant when `this.isCart && enableOrderSummary`. */
  #cartViewMode = 'summary';
  /**
   * Cart-only: this product's slice of the cart's checkout issues (numbered cart-wide),
   * set by `<b2b-plus-cart>` via {@link setCartIssues}. Re-pinned after every render.
   * @type {object[]}
   */
  #cartIssues = [];
  /** @type {Map<number, Element>} Issue number → element a "jump to issue" scrolls to. */
  #cartIssueTargets = new Map();
  /** @type {ReturnType<typeof createBulkApplyController>} */
  #bulkApply;
  /**
   * Guards against re-entrant shared-shipment-date sync when amending sibling
   * pickers / other tables after a user date commit.
   * @type {boolean}
   */
  #sharedDateSyncing = false;
  /**
   * Resolves when the first full render completes in connectedCallback (inventory
   * rows are in the DOM). Lets the cart await lazily-paginated tables before a
   * cart-wide bulk apply, so unrendered line items are no longer skipped.
   * Recreated on every connect because pagination can disconnect/reconnect.
   * @type {Promise<void>|null}
   */
  #firstRenderPromise = null;
  /** @type {(() => void)|null} Resolver for {@link #firstRenderPromise}. */
  #firstRenderResolve = null;
  /**
   * Aborted in disconnectedCallback so any in-flight prewarm or tab-switch IDB
   * read can drop its result rather than mutate state on a detached element.
   * @type {AbortController|null}
   */
  #lifetimeAbort = null;
  /**
   * Per-tab-switch controller. Aborted at the start of each new switch so a
   * prior switch whose IDB read is still in flight stops applying its result
   * (would otherwise overwrite `cartRecord` with the wrong tab's data).
   * @type {AbortController|null}
   */
  #tabSwitchAbort = null;

  #liveStateFor(optionValue) {
    return this.#liveStates.get(optionValue ?? '') ?? null;
  }

  get #currentLiveState() {
    return this.#liveStateFor(this.#selectedOptionValue);
  }

  /**
   * Called by `<b2b-plus-cart>` to refresh this table's bulk-apply UI state.
   * Rebuilds the bar when option metadata changes; toggles `hidden` when only `active` changes.
   * @returns {void}
   */
  // @public
  syncCartBulkApplyFromHost() { return this.#bulkApply?.syncFromHost(); }

  /**
   * @returns {{ active: boolean, departments: string[], pairs: { type?: string, season?: string }[], similarSizeOptionLabel: string }}
   */
  #readCartBulkApplyMetaForTemplate() {
    return parseCartBulkApplyMeta(
      this.dataset.cartBulkDepartments,
      this.dataset.cartBulkPairs,
      this.dataset.cartSimilarSizeLabel || b2bPlusString('bulkApplySizeOptionFallback'),
      this.dataset.cartBulkApplyActive !== 'false',
    );
  }

  /**
   * Register a DOM event listener and track it for removal in disconnectedCallback.
   * Replaces bare `addEventListener` calls throughout the component so that every
   * listener on shadow-DOM elements (and their closures capturing `this`) is
   * explicitly released when the element leaves the DOM — preventing memory leaks
   * during IntersectionObserver-driven pagination.
   * @param {EventTarget} target
   * @param {string} type
   * @param {Function} handler
   */
  /**
   * @param {EventTarget} target
   * @param {string}      type
   * @param {Function}   handler
   * @param {{ capture?: boolean }} [opts]
   */
  #on(target, type, handler, opts) {
    const capture = opts?.capture === true;
    target.addEventListener(type, handler, capture);
    this.#listeners.push({ target, type, handler, capture });
  }

  /**
   * Drop listeners bound inside this shadow tree before a second `#renderAndInit`.
   *
   * Cart hydrate (`#applyPendingCartRecord`) re-renders without going through the
   * option-tab teardown. lit-html reuses the same "Add Shipment" button, so a
   * second `#initAddShipmentButton` stacked another click handler on it. That
   * handler toggles the picker open and the first toggles it shut in the same
   * click, so the button looks dead once the cart already has items (the same
   * moment cart-lock is showing). Window/document listeners stay — they are not
   * re-bound by the render.
   */
  #releaseShadowListeners() {
    const shadow = this.#shadow;
    if (!shadow) return;
    const keep = [];
    for (const entry of this.#listeners) {
      const { target, type, handler, capture } = entry;
      const inShadow = target === shadow || (target instanceof Node && shadow.contains(target));
      if (inShadow) target.removeEventListener(type, handler, capture === true);
      else keep.push(entry);
    }
    this.#listeners = keep;
  }

  /**
   * `<tbody>` of the qty grid (`data-b2b-plus-inventory-table` only).
   * Excludes product-details tables and arbitrary HTML under the same host.
   * @returns {HTMLTableSectionElement|null}
   */
  #inventoryTbody() {
    return this.#shadow.querySelector(INVENTORY_TABLE_BODY_SELECTOR);
  }

  /**
   * SKU / barcode copy in variant header tooltips.
   * Must use **capture** on the shadow root: `initTooltips` / `[data-tooltip-content]`
   * call `stopPropagation` on inner clicks, so bubble listeners on `#shadow` never run.
   */
  #initCopyTriggers() {
    this.#on(
      this.#shadow,
      'click',
      (e) => {
        const trigger = e.target?.closest?.('.b2b-copy-trigger[data-b2b-copy]');
        if (!trigger) return;
        e.preventDefault();
        copyToClipboard(String(trigger.dataset.b2bCopy ?? ''), trigger);
      },
      { capture: true },
    );
    this.#on(
      this.#shadow,
      'keydown',
      (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const trigger = e.target?.closest?.('.b2b-copy-trigger[data-b2b-copy]');
        if (!trigger) return;
        e.preventDefault();
        copyToClipboard(String(trigger.dataset.b2bCopy ?? ''), trigger);
      },
      { capture: true },
    );
  }

  /** Append lit-html `<tr>` templates to `tbody` in batches (non-blocking main thread). */
  async #appendRowTemplateBatches(tbody, rowTemplates, batchSize) {
    // Yield to the browser between batches. requestIdleCallback defers until the
    // browser is idle so rendering doesn't compete with user input; the 50 ms
    // timeout prevents indefinite delay on a busy main thread. Falls back to
    // setTimeout(0) on browsers that don't support requestIdleCallback.
    const yieldToMain = () => new Promise((resolve) =>
      typeof requestIdleCallback !== 'undefined'
        ? requestIdleCallback(resolve, { timeout: 50 })
        : setTimeout(resolve, 0),
    );

    for (let i = 0; i < rowTemplates.length; i += batchSize) {
      const chunk = rowTemplates.slice(i, i + batchSize);
      const temp = document.createElement('div');
      render(html`${chunk}`, temp);
      while (temp.firstChild) tbody.appendChild(temp.firstChild);
      if (i + batchSize < rowTemplates.length) {
        await yieldToMain();
      }
    }
  }

  /**
   * Lifecycle callback — parses product data and renders the table on mount.
   * @returns {Promise<void>}
   */
  async connectedCallback() {
    // Read dataset flags here (not in constructor) so programmatically created
    // elements have time to have their attributes set before connectedCallback fires.
    this.showProductDetails  = this.dataset.showProductDetails === 'true';
    this.showInventoryFilter = this.dataset.showInventoryFilter === 'true';
    this.showActionButton    = this.dataset.showActionButton !== 'false';
    // Hidden by default; enabled on cart page by <b2b-plus-cart> when config.cart.enableRemoveButton is true.
    this.showRemoveButton    = this.dataset.showRemoveButton === 'true';
    /** Set by `<b2b-plus-cart>` via `data-is-cart`; templates receive this for cart-only markup/CSS hooks. */
    this.isCart              = this.dataset.isCart === 'true';

    // When nested inside b2b-plus-cart the parent shadow handles scoping —
    // render directly into light DOM and skip stylesheet injection.
    const noShadow = this.hasAttribute('data-no-shadow');
    // `open` keeps markup/CSS scoped but allows DevTools and theme scripts to use `element.shadowRoot`.
    this.#shadow = noShadow ? this : this.attachShadow({ mode: 'open' });

    // Fresh lifetime controller per connect. IntersectionObserver-driven
    // pagination disconnects then reconnects the same element, so a prior
    // abort must not leak forward.
    this.#lifetimeAbort?.abort();
    this.#lifetimeAbort = new AbortController();

    // Fresh readiness promise per connect; resolved once the first render
    // completes (or on error) so awaiters never hang. See #firstRenderPromise.
    this.#firstRenderPromise = new Promise((resolve) => { this.#firstRenderResolve = resolve; });

    try {
      // When a host calls setPrefetchedData() BEFORE inserting the element into the
      // DOM (the required contract — see quick-view.js), the data is already
      // present the moment connectedCallback fires and we can proceed immediately.
      //
      // When no prefetch is provided (Liquid-rendered tables), yield one macrotask
      // so the HTML parser can finish inserting `<script data-product-json>` child
      // nodes before we attempt to read them.
      if (!this.#prefetchedData) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      this.productJSONElement = this.querySelector('script[data-product-json]');
      await this.initializeProductData();

      this.#stylesheetHrefs = !noShadow && this.dataset.stylesheet
        ? this.dataset.stylesheet.split(',').map((h) => h.trim()).filter(Boolean)
        : [];

      this.#buildCombinationCaps();
      // Register before first render so a post-inventory first-visible swatch
      // click (external strip or in-table tabs) is not missed.
      this.#initOptionSelectListener();
      this.#on(window, LOCKED_SHIPMENT_WINDOWS_ROLLED_EVENT, this.#onLockedShipmentWindowsRolled);
      await this.#renderAndInit(this.#stylesheetHrefs);

      // Patches in the real cart-record quantities (PDP path only — see
      // initializeProductData) if they weren't already in hand for the render above.
      // Still inside this try/finally so whenReady() (the cart page's bulk-apply gate)
      // keeps meaning "quantities are loaded," not just "grid painted."
      await this.#applyPendingCartRecord(this.#stylesheetHrefs);

      // Hydrate non-active option tabs from IDB so the consolidated order summary
      // and ATC reflect every option's saved data, not just the visited ones.
      // Runs after the active tab is rendered to keep first paint fast.
      const prewarmSignal = this.#lifetimeAbort?.signal;
      this.#prewarmTabRecords(prewarmSignal)
        .then(() => {
          if (prewarmSignal?.aborted) return;
          // Lock may already be applied. Other colours hydrate after that pass,
          // so drop any conflicting quantities they just restored.
          const lockedType = String(this.dataset.cartLockedType ?? '').trim();
          if (!lockedType) return;
          return this.clearCartLockConflictSelections(lockedType);
        })
        .then(() => {
          if (prewarmSignal?.aborted) return;
          // Now that non-active tabs are hydrated, light up their qty flags and
          // flip the ATC label to "Update Order" if any colour has a saved order.
          this.#refreshOptionTabQtyFlags();
          this.#refreshAtcLabel();
          if (b2bConfig().enableOrderSummary !== false) this.#renderOrderSummary();
        })
        .catch(() => {});
    } catch (err) {
      console.error('b2b-plus-product-table: connectedCallback failed', err);
      const msg = b2bPlusString('inventoryUnavailable') || 'Unable to load product table.';
      render(
        html`<div class="b2b-plus-empty-state b2b-plus-empty-state--error" role="alert">${msg}</div>`,
        this.#shadow,
      );
    } finally {
      // Always release awaiters, even on render failure, so the cart's bulk-apply
      // wait can never hang on a single broken table.
      this.#firstRenderResolve?.();
    }
  }

  /**
   * Resolves once the first full render has placed inventory rows in the DOM.
   * Returns an already-resolved promise when no render is pending. Used by
   * `<b2b-plus-cart>` to await lazily-paginated tables before a cart-wide bulk apply.
   * @returns {Promise<void>}
   */
  // @public
  whenReady() {
    return this.#firstRenderPromise ?? Promise.resolve();
  }

  /**
   * Render the table template into the shadow root and run all init methods.
   * Called on first connect and again on every option tab switch.
   * @param {string[]} stylesheetHrefs
   * @returns {Promise<void>}
   */
  async #renderAndInit(stylesheetHrefs) {
    const BATCH_SIZE = 20;
    const cartLockGen = ++this.#cartLockGen;
    // Hydrate re-render reuses DOM nodes. Clear the previous shadow listeners
    // first so controls like "Add Shipment" are not double-bound.
    this.#releaseShadowListeners();

    // Compute split-option-filtered productJSON once so row body cells and header cells
    // always reference the same variant set — prevents column count mismatch.
    const rawProductJSON        = this.productJSON;
    const enableProductTableSplit = b2bConfig().enableProductTableSplit !== false;
    const { optionGroups, isSplit } = resolveOptionGroups(rawProductJSON, {
      enableSplit: enableProductTableSplit,
      splitOption: b2bConfig().productTableSplitOption ?? '',
    }, PT_SDK_DEPS);
    // optionGroups already holds the filtered variant lists — reuse instead of re-filtering.
    const activeProductJSON = (isSplit && this.#selectedOptionValue)
      ? { ...rawProductJSON, variants: optionGroups.get(this.#selectedOptionValue) ?? [] }
      : rawProductJSON;

    // Set #renderedVariantIds up front (it's also reasserted in #productTableRootTemplate)
    // so the pre-render liveState seed below picks up the active tab's variant ids. Without
    // this it would default to the stale value ([] on first paint), seeding the tab with an
    // empty variantIds array — which makes buildSummaryRowsFromLiveState yield zero rows and
    // the order summary never renders for products that have saved draft data.
    this.#renderedVariantIds = (activeProductJSON?.variants ?? []).map((v) => String(v.id));

    // Seed liveState from the (migrated) cartRecord before rendering so the
    // template can pre-fill inputs on the first paint. Guard: only for PDP
    // (isCart reads from DOM), only when cartRecord has data (fresh products
    // stay null so the post-render #buildCurrentRecordFromDom fallback still
    // runs), and only when this tab hasn't been seeded yet (tab-switch reuse).
    if (!this.isCart && Object.keys(this.cartRecord ?? {}).length && !this.#currentLiveState) {
      this.cartRecord = migrateProductRecord(this.cartRecord) ?? {};
      this.#initLiveStateFromRecord(this.cartRecord, this.#selectedOptionValue);
    }

    // PDP/quickview pre-fills from #currentLiveState; cart pre-fills from this.cartRecord
    // directly (handled inside #inventoryDataToRowTemplates). No liveState is stored for cart.
    const rowTemplates = this.#inventoryDataToRowTemplates(activeProductJSON, this.inventoryData, this.#currentLiveState);
    const needsBatch   = rowTemplates.length > BATCH_SIZE;

    const emptyOrMainTpl = this.#productTableRootTemplate({ stylesheetHrefs, rowTemplates, needsBatch, optionGroups, activeProductJSON, enableProductTableSplit });
    render(emptyOrMainTpl, this.#shadow);

    // Apply cart-view mode immediately after render to prevent both panels from flashing
    // visible before JS init runs. Must happen before any other init that reads view state.
    const enableOrderSummary = b2bConfig().enableOrderSummary !== false;
    if (this.isCart && enableOrderSummary) this.#setCartViewMode(this.#cartViewMode);

    if (needsBatch && rowTemplates.length) {
      const tbody = this.#inventoryTbody();
      if (tbody) await this.#appendRowTemplateBatches(tbody, rowTemplates, BATCH_SIZE);
    }
    this.#initImageSliders();
    // Filters — optional behaviour; only fetched/evaluated when a filter UI renders.
    if (this.showInventoryFilter || this.showActionButton) {
      import('b2b-plus/filters-controller')
        .then(({ createFilterController }) => createFilterController(this.#buildFiltersHost()).init())
        .catch(() => {});
    }
    this.#initDatePickers();
    this.#initQtyInputs();
    if (b2bConfig().enableItemNotes) this.#initNoteToggles();
    this.#initAtcButton();
    if (enableOrderSummary) {
      // The action bar's `[data-atc-btn]` button is bound by `#initAtcButton` —
      // no separate summary-ATC wiring is needed.
      if (!this.isCart) {
        this.#initSummaryClearBtn();
        this.#initSummaryTabHandler();
      }
      this.#initOrderSummarySync();
      if (this.isCart) this.#initCartViewToggle();
    }
    this.#initRemoveButton();
    this.#initRemoveShipmentRows();
    this.#initAddShipmentButton();
    this.#initAutoSave();
    this.#initManualSaveBar();
    this.#validateRestoredData();
    // Cart-only: flag delivery dates that are now in the past (a working order the
    // buyer left sitting for a week). The date itself is KEPT — checkout validation
    // blocks until it is re-picked. #validateRestoredData returns early on the cart.
    this.#flagPastCartDeliveryDates();
    // Cart-only: #validateRestoredData (and its SDK cap clamp) bails out early on
    // the cart, so a saved order whose quantities now exceed available stock would
    // render the raw over-cap value. Clamp those down to the cap here so the line
    // totals / prices and the checkout payload use the in-stock quantity.
    this.#clampCartQtysToMax();
    // Per-input clamp above can't see cross-shipment sums; reconcile rows sharing
    // a combination against the summed cap so a stale over-sum (e.g. 60+60 vs a
    // cap of 100) is corrected on load, not just on the next keystroke.
    this.#reconcileCartSiblingCaps();
    // Fresh PDP (no prior IDB save): cartRecord is empty so #validateRestoredData returned early
    // without seeding #liveStates. Bootstrap from the rendered DOM so input writes and
    // #buildCurrentRecord() have a valid live state to work with.
    if (!this.isCart && !this.#currentLiveState) {
      this.cartRecord = this.#buildCurrentRecordFromDom();
      this.#initLiveStateFromRecord(this.cartRecord, this.#selectedOptionValue);
    }
    // Existing drafts were saved before each variant had its own window. Copy
    // the dates off the rendered inputs so the next save (and cart checkout)
    // does not keep sending the bucket's single start date.
    this.#syncImmediateVariantWindows();
    // Render the initial order summary from DOM now that #validateRestoredData has populated inputs.
    if (enableOrderSummary) this.#renderOrderSummary();
    this.#initProductLineTotalsSync();
    this.#initCopyTriggers();
    this.#cleanupTooltips = initTooltips(this.#shadow);
    if (this.isCart) {
      this.#bulkApply = createBulkApplyController(this.#buildBulkApplyHost());
      this.#bulkApply.init();
    }
    this.#initOptionTabHandler();
    this.#initOptionTabQtyFlags();
    this.#syncExternalOptionSelector();
    // Cart lock — optional module; silently skipped when not in the import map.
    import('b2b-plus/cart-lock')
      .then(({ applyCartLockToProductTable, cartLockConfig }) => {
        if (cartLockGen !== this.#cartLockGen) return;
        if (cartLockConfig?.enabled === false) return;
        applyCartLockToProductTable(this, () => cartLockGen === this.#cartLockGen).catch(() => {});
      })
      .catch(() => {});
    if (this.isCart) {
      this.#initCartIssueListeners();
      this.#renderCartIssues();
    }
  }

  #initOptionTabHandler() {
    this.#on(this.#shadow, 'click', (e) => {
      const tab = e.target.closest('[data-b2b-option-tab]');
      if (!tab) return;
      const value = tab.dataset.b2bOptionTab;
      if (!value) return;
      document.dispatchEvent(
        new CustomEvent('b2b:option-selected', {
          bubbles: true,
          detail: { value, productHandle: this.productJSON?.handle ?? '' },
        }),
      );
    });
  }

  /**
   * Push the option values that are unavailable to the external server-rendered
   * `<b2b-plus-option-selector>` (the PDP "regular options" strip), which has no
   * inventory data of its own. Two attributes mirror the in-table three-state
   * split: `hidden-options` (no inventory records at all → the selector omits the
   * swatch) and `disabled-options` (records exist but nothing orderable → sold-out
   * swatch). Setting attributes (rather than firing an event) avoids a race: the
   * selector exists in the light DOM at parse time, so the values are applied
   * whenever this runs, regardless of upgrade timing.
   */
  #syncExternalOptionSelector() {
    const handle = this.productJSON?.handle ?? '';
    const { optionGroups, isSplit } = resolveOptionGroups(this.productJSON, {
      enableSplit: true,
      splitOption: b2bConfig().productTableSplitOption ?? '',
    }, PT_SDK_DEPS);
    if (!isSplit) return;

    // null = inventory unknown (payload missing/failed) → leave all enabled.
    const variantsWithInventory = computeVariantsWithInventory(
      this.productJSON?.variants ?? [],
      this.inventoryData?.inventories,
      inventoryLinesForVariant,
    );
    if (variantsWithInventory === null) return;
    const variantsWithStock = computeVariantsWithStock(
      this.productJSON?.variants ?? [],
      this.inventoryData?.inventories,
      inventoryLinesForVariant,
      isPrebookOrderType,
    );

    const hidden   = [];
    const disabled = [];
    for (const [value, variants] of optionGroups) {
      const vs = variants ?? [];
      if (!vs.some((v) => variantsWithInventory.has(String(v.id)))) {
        hidden.push(value);
      } else if (variantsWithStock && !vs.some((v) => variantsWithStock.has(String(v.id)))) {
        disabled.push(value);
      }
    }

    let target = null;
    document.querySelectorAll('b2b-plus-option-selector').forEach((el) => {
      if (el.getAttribute('product-handle') === handle) target = el;
    });
    target = target ?? document.querySelector('b2b-plus-option-selector');
    if (target) {
      target.setAttribute('hidden-options', JSON.stringify(hidden));
      target.setAttribute('disabled-options', JSON.stringify(disabled));
    }
  }

  /**
   * Keep the option tabs' `data-has-qty` flag live without a full re-render.
   * The tabs themselves only re-render on tab switch, but quantities change on
   * every keystroke — so this delegated listener refreshes the flags after each
   * qty edit. The per-input listeners in `#initQtyInputs` write live state in
   * the target phase, before this shadow-delegated handler runs in the bubble
   * phase, so `#liveStates` is already current here. Only meaningful when the
   * product is split into tabs.
   */
  #initOptionTabQtyFlags() {
    if (b2bConfig().enableProductTableSplit === false) return;
    // When the order summary is on, #renderOrderSummary already refreshes the
    // flags on every qty mutation. Only wire a dedicated listener when it's off.
    if (b2bConfig().enableOrderSummary !== false) return;
    const refresh = (e) => {
      if (!e.target.closest('[data-qty-input]')) return;
      this.#refreshOptionTabQtyFlags();
    };
    this.#on(this.#shadow, 'input', refresh);
    this.#on(this.#shadow, 'change', refresh);
  }

  /**
   * Toggle `data-has-qty` on every option tab button from current live state.
   * Cheap DOM-attribute pass — no template re-render, so it is safe to call on
   * every keystroke and after the cross-tab prewarm hydrates other tabs.
   */
  #refreshOptionTabQtyFlags() {
    const tabs = this.#shadow.querySelectorAll('[data-b2b-option-tab]');
    if (!tabs.length) return;
    tabs.forEach((btn) => {
      const liveTab = this.#liveStateFor(btn.dataset.b2bOptionTab);
      if (liveTab && liveTabHasAnyQty(liveTab)) btn.setAttribute('data-has-qty', '');
      else btn.removeAttribute('data-has-qty');
    });
  }

  /**
   * Midnight / day-change: locked pickers already show today+N on a fresh
   * render. Cart tables remount via `<b2b-plus-cart>`; PDP/quickview must
   * rewrite the open pickers and persist so ATC/summary stay aligned.
   */
  #onLockedShipmentWindowsRolled = () => {
    if (this.isCart) return;
    const tbody = this.#inventoryTbody();
    if (!tbody) return;
    tbody.querySelectorAll('[data-delivery-date-picker]').forEach((input) => {
      const tr = input.closest('[data-b2b-plus-inventory-row]');
      if (!isLockedShipmentWindowEnabled(tr?.dataset.rowType ?? '')) return;
      this.#applyLockedShipmentWindow(input);
    });
    this.#validateRestoredData();
    if (b2bConfig().enableOrderSummary !== false) this.#renderOrderSummary();
  };

  // Registered once; survives option-switch cleanup; removed only in disconnectedCallback.
  // Body is a thin event filter — the actual switch work lives in `#switchOptionTab`
  // so it can be reasoned about (and one day exercised) without dispatching events.
  #initOptionSelectListener() {
    if (b2bConfig().enableProductTableSplit === false) return;
    if (this.#optionSelectHandler) return;
    this.#optionSelectHandler = (e) => {
      const { value, productHandle } = e.detail ?? {};
      if (!value || productHandle !== (this.productJSON?.handle ?? '')) return;
      if (value === this.#selectedOptionValue) return;
      // Fire-and-forget: handler errors are caught inside `#switchOptionTab`.
      this.#switchOptionTab(value);
    };
    document.addEventListener('b2b:option-selected', this.#optionSelectHandler);
  }

  /**
   * Switch the active option tab. Self-contained orchestration: superseding
   * controller, snapshot of the leaving tab, save + prune, IDB read for the
   * incoming tab, teardown, re-render. Every async stage is gated on the
   * controller's `signal` so a newer switch can abandon this one mid-flight.
   *
   * @param {string} value - the option tab to activate (must differ from current)
   * @returns {Promise<void>}
   */
  async #switchOptionTab(value) {
    // Supersede any prior switch still in flight. Its post-await abort checks
    // will see `signal.aborted` and drop their results instead of overwriting
    // state for the new active tab.
    this.#tabSwitchAbort?.abort();
    this.#tabSwitchAbort = new AbortController();
    const { signal } = this.#tabSwitchAbort;

    const leavingOptionValue = this.#selectedOptionValue;

    // Cart: flush any pending debounced save (or staged manual edits) before teardown
    if (this.isCart && this.#debounceTimer !== null) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
      this.#onAutoSave?.();
    } else if (this.isCart && this.#isManualSave() && this.#isDirty) {
      this.#onAutoSave?.();
      this.#isDirty = false;
    }

    // PDP/quickview: do NOT persist on tab switch. Every option tab's edits stay
    // in `#liveStates` (in-memory) until the user clicks "Add to Order", which
    // commits all populated tabs (see `#initAtcButton`). This keeps switching
    // colours from writing partial drafts to IDB. Prune the leaving tab only
    // when it holds no quantities, so the Map stays tidy without discarding work.
    const leavingTab = this.#liveStateFor(leavingOptionValue);
    if (leavingTab) {
      // Before teardown: sync picker → liveState, and fill a default window on
      // qty rows that never got a buyer-selected date (DOM is about to go away).
      // Active-tab ATC still requires an explicit date when prefill is off.
      this.#ensureLeavingTabDeliveryWindows(leavingTab);
      if (!liveTabHasAnyQty(leavingTab)) this.#liveStates.delete(leavingOptionValue ?? '');
    }

    this.#selectedOptionValue = value;
    const incomingRecord = await getProductRecord(this.#storageHandle);
    if (signal.aborted) return; // newer switch has taken over; do not apply this read.
    this.cartRecord = incomingRecord;
    this.cartData   = flattenProductRecordToLineItems(this.cartRecord);
    this.#trackSavedOrder(value, incomingRecord);

    this.#cleanupTooltips?.();
    this.#flatpickrInstances?.forEach((fp) => fp.destroy());
    this.#listeners.forEach(({ target, type, handler, capture }) =>
      target.removeEventListener(type, handler, capture === true));
    this.#listeners = [];
    if (this.#beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.#beforeUnloadHandler);
      this.#beforeUnloadHandler = null;
    }

    this.#buildCombinationCaps();
    // Use render(nothing) instead of replaceChildren() — manually removing the shadow's
    // children detaches lit-html's internal Comment marker nodes, causing the next render()
    // call to throw ("Cannot read properties of null (reading 'insertBefore')") and leaving
    // the shadow permanently empty. render(nothing) clears content while keeping markers live.
    render(nothing, this.#shadow);
    try {
      await this.#renderAndInit(this.#stylesheetHrefs);
    } catch (err) {
      console.error('b2b-plus: option tab switch re-render failed', err);
    }
  }

  disconnectedCallback() {
    // Cancel any in-flight prewarm or tab-switch IDB reads. The reads themselves
    // run to completion (IDB has no abort), but the post-await checks see
    // `signal.aborted` and drop their results instead of mutating a detached element.
    this.#lifetimeAbort?.abort();
    this.#lifetimeAbort = null;
    this.#tabSwitchAbort?.abort();
    this.#tabSwitchAbort = null;

    this.#cleanupTooltips?.();
    this.#flatpickrInstances?.forEach((fp) => fp.destroy());

    // Cancel any pending auto-save timer and remove the beforeunload flush
    // handler. Both must be cleaned up when the element leaves the DOM
    // (e.g. during IntersectionObserver pagination) to prevent memory leaks
    // and stale event listeners accumulating across page-scroll batches.
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = null;
    if (this.#summaryRenderRaf !== null) {
      cancelAnimationFrame(this.#summaryRenderRaf);
      this.#summaryRenderRaf = null;
    }
    if (this.#beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.#beforeUnloadHandler);
      this.#beforeUnloadHandler = null;
    }
    if (this.#optionSelectHandler) {
      document.removeEventListener('b2b:option-selected', this.#optionSelectHandler);
      this.#optionSelectHandler = null;
    }

    // Remove every tracked DOM listener. Without this, closures that capture
    // `this` keep the component alive in memory even after it is removed from
    // the DOM — a progressive leak during IntersectionObserver pagination where
    // product table elements are recycled as the user scrolls.
    this.#listeners.forEach(({ target, type, handler, capture }) => {
      target.removeEventListener(type, handler, capture === true);
    });
    this.#listeners = [];
  }

  /**
   * Programmatically apply a delivery date range to all or filtered rows.
   * Called by `<b2b-plus-cart>` when the user clicks the multi-apply "Apply" button.
   *
   * For each row date-picker in this table:
   *  - If `scope` is `'all'`, the picker is always updated.
   *  - Otherwise only pickers whose current "selectedStart to selectedEnd" matches `scope` are updated.
   *  - The end date is clamped to the row's own `data-row-end` limit.
   *  - Rows whose start falls outside the row's allowed window are skipped.
   *
   * @param {string} startISO - Start date YYYY-MM-DD
   * @param {string} endISO   - End date YYYY-MM-DD
   * @param {string} [scope]  - `'all'` or a `"YYYY-MM-DD to YYYY-MM-DD"` delivery window to match
   * @param {object} [options]
   * @param {string|null} [options.orderTypeGroup] - When set, only rows whose type normalises to this group
   * @param {HTMLElement|null} [options.excludeInput] - Skip this picker (the one the user just edited)
   * @param {boolean} [options.requireExistingDate=false] - Only update pickers that already have a date
   * @param {boolean} [options.onlyBlank=false] - Only update pickers with no date yet (seed shared default)
   * @returns {boolean} True when at least one picker was updated
   */
  // @public
  applyDeliveryDate(startISO, endISO, scope = 'all', options = {}) {
    const {
      orderTypeGroup = null,
      excludeInput = null,
      requireExistingDate = false,
      onlyBlank = false,
    } = options ?? {};
    const inv = this.#inventoryTbody();
    const pickers = inv ? [...inv.querySelectorAll('[data-delivery-date-picker]')] : [];
    let anyUpdated = false;

    for (const picker of pickers) {
      if (excludeInput && picker === excludeInput) continue;

      // Scope filter: only update pickers whose current selection matches.
      if (scope !== 'all') {
        const currentWindow =
          picker.dataset.selectedStart && picker.dataset.selectedEnd
            ? picker.dataset.selectedStart === picker.dataset.selectedEnd
              ? picker.dataset.selectedStart
              : `${picker.dataset.selectedStart} to ${picker.dataset.selectedEnd}`
            : '';
        if (currentWindow !== scope) continue;
      }

      // Apply when the row has any quantity entered OR already has a date prefilled.
      // - hasQty: user has ordered at least one variant in this row.
      // - hasDate: the row already carries a delivery date (e.g. restored from IDB or
      //   previously set) — multi-apply should update it even if qty is still blank.
      const tr     = picker.closest('[data-b2b-plus-inventory-row]');
      if (orderTypeGroup) {
        const rowGroup = normalizeOrderTypeGroup(tr?.dataset.rowType ?? '');
        if (rowGroup !== orderTypeGroup) continue;
      }
      if (isLockedShipmentWindowEnabled(tr?.dataset.rowType ?? '')) continue;
      const hasQty  = [...(tr?.querySelectorAll('[data-qty-input]') ?? [])].some(
        (inp) => Number(inp.value) > 0,
      );
      const hasDate = !!(picker.dataset.selectedStart && picker.dataset.selectedEnd);
      if (onlyBlank && hasDate) continue;
      if (requireExistingDate && !hasDate) continue;
      if (!onlyBlank && !hasQty && !hasDate) continue;

      const rowStart = parseISODateLocal(picker.dataset.rowStart);
      const rowEnd   = parseISODateLocal(picker.dataset.rowEnd);
      const selStart = parseISODateLocal(startISO);
      if (!selStart) continue;

      const errEl = picker.parentElement?.querySelector('[data-date-error]');

      // Selected date is outside this row's allowed window — show error.
      // When `multiApplyKeepDateOnError` is true, keep the existing date in the picker;
      // otherwise clear it (legacy behaviour).
      if ((rowStart && selStart < rowStart) || (rowEnd && selStart > rowEnd)) {
        const keepDate = window.b2bPlus?.config?.multiApplyKeepDateOnError === true;
        if (!keepDate) {
          picker.dataset.selectedStart = '';
          picker.dataset.selectedEnd   = '';
          const fp = this.#flatpickrInstances?.get(picker);
          if (fp) {
            fp.clear(false);
          } else {
            picker.value = '';
          }
        }
        picker.classList.add('b2b-plus-date-picker--error');
        if (errEl) {
          errEl.textContent = b2bPlusString('dateOutOfRangeError') || 'Date not available for this row';
          errEl.hidden = false;
        }
        anyUpdated = true;
        continue;
      }

      // Clamp end date to the row's allowed range.
      const selEnd       = parseISODateLocal(endISO);
      const effectiveEnd = rowEnd && selEnd && selEnd > rowEnd ? rowEnd : selEnd;
      let effectiveEndISO = effectiveEnd ? toISODate(effectiveEnd) : endISO;

      const rowType = tr?.dataset.rowType ?? '';
      if (isAutoHorizonEndShipmentEnabled(rowType)) {
        const horizonEnd = atsImmediatePickerEnd(rowEnd, selStart);
        effectiveEndISO = horizonEnd ? toISODate(horizonEnd) : startISO;
      }

      picker.dataset.selectedStart = startISO;
      picker.dataset.selectedEnd   = effectiveEndISO;

      const fp = this.#flatpickrInstances?.get(picker);
      if (fp) {
        if (startISO === effectiveEndISO || isAutoHorizonEndShipmentEnabled(rowType)) {
          fp.setDate(startISO, false);
        } else {
          fp.setDate([startISO, effectiveEndISO], false);
        }
      }
      this.#setDatePickerDisplay(picker, startISO, effectiveEndISO);

      const liveRow = this.#currentLiveState?.combos[tr?.dataset.combinationKey]?.rows[tr?.dataset.rowId];
      if (liveRow) {
        liveRow.deliveryWindow = joinDeliveryWindow(startISO, effectiveEndISO);
        liveRow.hasDateError   = false;
      }

      picker.classList.remove('b2b-plus-date-picker--error');
      if (errEl) errEl.hidden = true;

      anyUpdated = true;
    }

    // PDP/quickview: inactive option tabs are not in the DOM. Mirror the same
    // shared-date amend into `#liveStates` so previously added variants and the
    // order summary stay in sync with the committed window.
    if (!this.isCart && orderTypeGroup && this.#liveStates.size) {
      const nextWindow = joinDeliveryWindow(startISO, endISO);
      if (nextWindow) {
        const liveUpdated = amendLiveStatesDeliveryWindowsByOrderTypeGroup(
          this.#liveStates,
          orderTypeGroup,
          nextWindow,
          {
            onlyIfHasDate: requireExistingDate,
            onlyBlank,
          },
        );
        if (liveUpdated) anyUpdated = true;
      }
    }

    if (anyUpdated) {
      this.#userHasInteracted = true;
      this.#updateButtonState?.();
      this.#triggerSave({ debounce: false });
      this.#renderOrderSummary();
    }
    return anyUpdated;
  }

  /**
   * Apply a shared shipment date to mounted pickers of one inventory-type group.
   * Used after cart-wide IDB amend so open product tables stay in sync.
   * @param {string} startISO
   * @param {string} endISO
   * @param {string} orderTypeGroup
   */
  // @public
  applySharedShipmentDate(startISO, endISO, orderTypeGroup) {
    if (!orderTypeGroup) return;
    this.#sharedDateSyncing = true;
    try {
      this.applyDeliveryDate(startISO, endISO, 'all', {
        orderTypeGroup,
        requireExistingDate: true,
      });
    } finally {
      this.#sharedDateSyncing = false;
    }
  }

  /**
   * Ensures `this.inventoryData` is populated when we have a product id.
   * @returns {Promise<void>}
   */
  async #loadInventoryForProduct() {
    if (!this.productJSON?.id) return;
    if (isInventoryDataReady(this.inventoryData)) return;

    try {
      const response = await getInventory([this.productJSON.id]);
      if (response?.success && response?.data) {
        this.inventoryData         = response.data;
        this.#inventoryFetchFailed = false;
      } else {
        this.#inventoryFetchFailed = true;
      }
    } catch (err) {
      console.error('b2b-plus: inventory fetch failed', err);
      this.#inventoryFetchFailed = true;
    }
  }

  /**
   * Start the PDP inventory request from `data-product-id`, ahead of anything that
   * waits on the working order. The request needs only the product id and the company
   * id, so it can run alongside the working-order load sync instead of queueing behind
   * the read barrier that `rollLockedShipmentWindowsToToday()` waits on.
   *
   * Awaits `ensureOrderContext()` first — for a sales rep on behalf, the request's
   * `shopifyCompanyId` comes from that context. Deduped (started when utils loaded), so
   * no extra round trip; resolves at once for a normal buyer.
   *
   * Never rejects: `getInventory` already reports every failure as `{ success: false }`.
   *
   * @returns {{ id: number, response: Promise<object> } | null} `null` when the element
   *   carries no usable product id — the caller then fetches by `productJSON.id`.
   */
  #startEarlyInventoryFetch() {
    const id = Number(this.dataset.productId);
    if (!Number.isSafeInteger(id) || id <= 0) return null;
    const response = ensureOrderContext()
      .catch(() => null)
      .then(() => getInventory([id]));
    return { id, response };
  }

  /**
   * Parses the embedded product JSON and fetches inventory from the API.
   * **Inventory is always requested (and applied) before cart data** so the table
   * does not render from a stale/empty `inventoryData` when IndexedDB is ready
   * first. On the PDP the request is started first thing, in parallel with the
   * working-order load sync (see `#startEarlyInventoryFetch`).
   *
   * Populates `this.productJSON`, `this.inventoryData`, and `this.cartData`.
   * No-ops silently if JSON is absent or malformed.
   * @returns {Promise<void>}
   */
  async initializeProductData() {
    // PDP only (no prefetch): kick off inventory before the day-roll below, which waits
    // on the working-order read barrier. Awaited at the `Promise.all` further down.
    const earlyInventory = !this.#prefetchedData && this.productJSONElement
      ? this.#startEarlyInventoryFetch()
      : null;

    // Slide locked horizon dates to today → today+N before reading this
    // product's record so restored pickers match the current pre-populate window.
    try { await rollLockedShipmentWindowsToToday(); }
    catch { /* ignore — table still renders from the stored window */ }

    // Warm the sync mirror used by date-picker prefill (shared dates live in orderData).
    try { await getSharedShipmentDates(); }
    catch { /* ignore — prefill falls back to blank / default window */ }

    // When a parent (cart, quick view) passes prefetched data, use it, but
    // re-fetch inventory if the payload is missing (race) or a previous fetch failed.
    if (this.#prefetchedData) {
      this.productJSON   = attachVariantNetsuiteMetafields(this.#prefetchedData.productJSON) ?? null;
      this.inventoryData = this.#prefetchedData.inventoryData ?? null;
      this.cartRecord    = this.#prefetchedData.cartRecord ?? {};

      // Stamp the MSRP (`core_price`) when the feature is on and the prefetched JSON
      // didn't already carry it. Fail-soft no-op otherwise.
      if (this.productJSON) await enrichProductJSONWithMsrp(this.productJSON);
      // Catalog-price JSON has no variant metafields — pull FW/SS so cart Edit
      // Orders matches the PDP Future / Prebook season price.
      if (this.productJSON) await enrichProductJSONWithSeasonPrices(this.productJSON);

      // Init option selection before the IDB read so #storageHandle uses the correct key.
      this.#initSelectedOption();

      // Load from IDB when the host didn't supply a cartRecord (e.g. cart page
      // passing flat cart lines only) so quantities are always restored on first render.
      if (!Object.keys(this.cartRecord).length && this.productJSON?.handle) {
        try {
          this.cartRecord = await getProductRecord(this.#storageHandle);
        } catch (err) {
          console.error('b2b-plus: failed to load cart record from IDB', err);
          this.cartRecord = {};
        }
      } else {
        this.cartRecord = migrateProductRecord(this.cartRecord);
      }

      this.cartData = flattenProductRecordToLineItems(this.cartRecord);
      this.#trackSavedOrder(this.#selectedOptionValue, this.cartRecord);

      // Re-fetch inventory only when the host passed a null / failed payload.
      await this.#loadInventoryForProduct();
      if (this.#adoptFirstCarriedOption() && this.productJSON?.handle) {
        try {
          this.cartRecord = await getProductRecord(this.#storageHandle);
        } catch (err) {
          console.error('b2b-plus: failed to load cart record from IDB', err);
          this.cartRecord = {};
        }
        this.cartData = flattenProductRecordToLineItems(this.cartRecord);
        this.#trackSavedOrder(this.#selectedOptionValue, this.cartRecord);
      }
      return;
    }

    if (!this.productJSONElement) return;

    this.productJSON = attachVariantNetsuiteMetafields(
      await this.#parseProductJSON(this.productJSONElement),
    );
    if (!this.productJSON?.id) return;
    await enrichProductJSONWithSeasonPrices(this.productJSON);

    // Init option selection before the IDB read so #storageHandle uses the correct key.
    // Ahead of the MSRP/inventory fetches below — it's sync-only (reads productJSON,
    // dataset, and the URL) so it doesn't need to wait on either of them, and doing it
    // here lets the cart-record read start concurrently instead of after both finish.
    this.#initSelectedOption();

    // Placeholder for first paint — the real record is applied after the first render
    // by #applyPendingCartRecord() so the grid doesn't wait on the IDB read (which can
    // itself be gated behind the working-order barrier on a customer/location switch).
    this.cartRecord = {};
    this.cartData   = [];
    this.#pendingCartRecordPromise = this.productJSON.handle
      ? getProductRecord(this.#storageHandle)
      : null;

    // Stamp the MSRP (each variant's `core_price`) when the feature is on — the PDP's
    // embedded Liquid JSON has no `core_price`, so this is one catalog-price lookup,
    // session-cached and fail-soft — and fetch inventory concurrently; neither depends
    // on the other's result. Inventory is normally already in flight (started at the
    // top); it is re-requested only when that id doesn't match the parsed product.
    const inventoryRequest = earlyInventory && String(earlyInventory.id) === String(this.productJSON.id)
      ? earlyInventory.response
      : getInventory([this.productJSON.id]);
    const [, inventoryResponse] = await Promise.all([
      enrichProductJSONWithMsrp(this.productJSON),
      inventoryRequest,
    ]);
    if (inventoryResponse?.success && inventoryResponse?.data) {
      this.inventoryData         = inventoryResponse.data;
      this.#inventoryFetchFailed = false;
    } else {
      this.inventoryData         = null;
      this.#inventoryFetchFailed = true;
    }

    // Liquid / data-selected-option may pick a Shopify colour the app does not
    // carry. Move to the first carried option before first render so the table
    // matrix and in-table swatches are already on a real tab.
    if (this.#adoptFirstCarriedOption() && this.productJSON?.handle) {
      this.#pendingCartRecordPromise = getProductRecord(this.#storageHandle);
    }
  }

  /**
   * Await the cart-record read stashed by `initializeProductData()` and, if it
   * turned up saved data, patch it in: re-seed `#liveStates` from the real record
   * (discarding the empty-DOM guess `#renderAndInit`'s fallback seeded on first
   * paint) and re-render. No-ops when there's nothing to hydrate, so a product
   * with no saved order pays for no extra render.
   *
   * Reuses `#renderAndInit()` rather than patching `<input>` values directly —
   * cart data also determines row COUNT (extra shipment rows) and delivery-window/
   * note fields, not just quantities, so a value-only patch would miss those.
   *
   * @param {string[]} stylesheetHrefs
   * @returns {Promise<void>}
   */
  async #applyPendingCartRecord(stylesheetHrefs) {
    if (!this.#pendingCartRecordPromise) return;
    const pending = this.#pendingCartRecordPromise;
    this.#pendingCartRecordPromise = null;

    let record;
    try {
      record = await pending;
    } catch (err) {
      console.error('b2b-plus: failed to load cart record from IDB', err);
      record = {};
    }

    this.cartRecord = record;
    this.cartData   = flattenProductRecordToLineItems(record);
    this.#trackSavedOrder(this.#selectedOptionValue, record);

    if (!Object.keys(record).length) return; // first paint's empty state was already correct

    this.#liveStates.delete(this.#selectedOptionValue ?? '');
    this.#buildCombinationCaps(); // re-derive from the now-real demand (pooled caps)
    await this.#renderAndInit(stylesheetHrefs);
  }

  /**
   * Parse the embedded product JSON, tolerating the streaming-HTML race and
   * recovering from a truncated/corrupt embedded blob.
   *
   * The product blob (full `product | json` + metafields) can exceed 20KB. Two
   * distinct failure modes are handled here:
   *
   * 1. Streaming race — while the page is delivered in chunks the HTML parser can
   *    pause mid-`<script>` content; our `connectedCallback` (and the macrotask it
   *    yields) can run during that pause, so `textContent` is truncated and
   *    `JSON.parse` throws. While `document.readyState === 'loading'` the rest of
   *    the script may still be arriving, so we yield and retry.
   *
   * 2. Truncated/corrupt embedded blob — once the document has finished parsing
   *    (interactive/complete) the embedded text will never change, so re-reading
   *    the same node can't recover an incomplete blob (e.g. a partial cached
   *    response — the symptom users hit is "JSON error until a hard refresh").
   *    Rather than dying, we re-fetch the product JSON from the product-data
   *    section by handle — exactly the data a fresh load would have embedded.
   *
   * @param {HTMLScriptElement} el
   * @returns {Promise<object|null>}
   */
  async #parseProductJSON(el) {
    for (;;) {
      try {
        return JSON.parse(el.textContent);
      } catch (error) {
        if (document.readyState === 'loading') {
          // Content may still be streaming in — yield and retry on the next frame.
          await new Promise((resolve) => setTimeout(resolve, 16));
          continue;
        }
        // Document is fully parsed but the embedded blob still won't parse — it
        // was delivered truncated/corrupt. Self-heal by re-fetching fresh product
        // JSON from the catalog-price API instead of forcing a manual hard refresh.
        // That API resolves by id, hence the separate `data-product-id` attribute.
        const handle    = (this.dataset.productHandle ?? '').trim();
        const productId = (this.dataset.productId ?? '').trim();
        if (productId) {
          try {
            const fetched = await getProductJSON({ handle, productId });
            if (fetched?.id) {
              console.warn(
                'b2b-plus-product-table: recovered truncated embedded product JSON via catalog-price re-fetch',
                { handle },
              );
              return fetched;
            }
          } catch (fetchError) {
            console.error('b2b-plus-product-table: product JSON re-fetch fallback failed', fetchError);
          }
        }
        console.error('b2b-plus-product-table: invalid product JSON', error);
        return null;
      }
    }
  }

  /**
   * Set #selectedOptionValue to the first option group value when the product has a matching
   * split option with multiple values. No-op when feature is disabled or only one group exists.
   * Must be called after productJSON is assigned and before any #storageHandle access.
   */
  #initSelectedOption() {
    if (this.#selectedOptionValue) return; // already set (e.g. after a tab switch)
    const { optionGroups: groups, isSplit } = resolveOptionGroups(this.productJSON, {
      enableSplit: b2bConfig().enableProductTableSplit !== false,
      splitOption: b2bConfig().productTableSplitOption ?? '',
    }, PT_SDK_DEPS);
    if (!isSplit) return;

    // Explicit pre-selection via attribute — the checkout edit-delivery modal
    // sets `data-selected-option` so the edited colour opens and the table
    // loads/saves under its own `handle::optionValue` key. Applies even with
    // prefetched data (the modal path).
    const fromAttr = (this.dataset.selectedOption ?? '').trim();
    if (fromAttr && groups.has(fromAttr)) {
      this.#selectedOptionValue = fromAttr;
      return;
    }

    // Deep-link from the cart: `?b2b_option=<value>` (set on the cart line link)
    // pre-selects that option tab when it's a real group. Falls back to the first
    // group otherwise. Only applies on a real PDP (skip in quickview/modal hosts
    // that don't own the page URL).
    if (!this.isCart && !this.#prefetchedData) {
      try {
        const fromUrl = new URLSearchParams(window.location.search).get('b2b_option');
        if (fromUrl && groups.has(fromUrl)) {
          this.#selectedOptionValue = fromUrl;
          return;
        }
      } catch { /* malformed URL — fall through to default */ }
    }

    this.#selectedOptionValue = groups.keys().next().value;
  }

  /**
   * After inventory is known, if `#selectedOptionValue` is not carried by the
   * app (no inventory records), move to the first option that is. Prefers an
   * orderable colour; falls back to the first carried sold-out colour.
   * @returns {boolean} true when the selection changed
   */
  #adoptFirstCarriedOption() {
    if (this.isCart) return false;
    const { optionGroups, isSplit } = resolveOptionGroups(this.productJSON, {
      enableSplit: b2bConfig().enableProductTableSplit !== false,
      splitOption: b2bConfig().productTableSplitOption ?? '',
    }, PT_SDK_DEPS);
    if (!isSplit || !optionGroups) return false;

    const variantsWithInventory = computeVariantsWithInventory(
      this.productJSON?.variants ?? [],
      this.inventoryData?.inventories,
      inventoryLinesForVariant,
    );
    if (variantsWithInventory === null) return false;

    const variantsWithStock = computeVariantsWithStock(
      this.productJSON?.variants ?? [],
      this.inventoryData?.inventories,
      inventoryLinesForVariant,
      isPrebookOrderType,
    );

    const isCarried = (value) =>
      (optionGroups.get(value) ?? []).some((v) => variantsWithInventory.has(String(v.id)));
    const isOrderable = (value) => {
      if (!variantsWithStock) return true;
      return (optionGroups.get(value) ?? []).some((v) => variantsWithStock.has(String(v.id)));
    };

    if (this.#selectedOptionValue && isCarried(this.#selectedOptionValue)) return false;

    let next = null;
    for (const value of optionGroups.keys()) {
      if (!isCarried(value)) continue;
      if (isOrderable(value)) {
        next = value;
        break;
      }
      if (!next) next = value;
    }
    if (!next || next === this.#selectedOptionValue) return false;
    this.#selectedOptionValue = next;
    return true;
  }

  /**
   * Parallel-fetch IDB records for every non-active option tab and seed `#liveStates`
   * so the order summary and ATC see saved data across tabs the user hasn't visited
   * yet. No-op on cart (cart has no tab switching) or when this product has no
   * multi-value split option. Skips tabs already seeded (don't clobber unsaved work).
   * Failures are logged per-tab; one bad tab doesn't block the others.
   *
   * `signal` is checked after each IDB read so a disconnected element drops
   * stale results instead of mutating `#liveStates` after teardown. The reads
   * themselves cannot be aborted (`getProductRecord` is unsignalled and IDB
   * transactions can't be cancelled from JS) — we only discard the result.
   *
   * @param {AbortSignal} [signal]
   * @returns {Promise<void>}
   */
  async #prewarmTabRecords(signal) {
    if (this.isCart) return;
    if (b2bConfig().enableProductTableSplit === false) return;
    const productJSON = this.productJSON;
    if (!productJSON?.handle) return;
    const { optionGroups, isSplit } = resolveOptionGroups(productJSON, {
      enableSplit: true,
      splitOption: b2bConfig().productTableSplitOption ?? '',
    }, PT_SDK_DEPS);
    if (!isSplit) return;

    const currentValue = this.#selectedOptionValue;
    const others = [...optionGroups.keys()].filter((v) => v !== currentValue);

    await Promise.all(others.map(async (value) => {
      if (signal?.aborted) return;
      if (this.#liveStateFor(value)) return; // user may have unsaved work — don't clobber
      try {
        const handle = this.#storageHandleFor(value);
        if (!handle) return;
        const record = await getProductRecord(handle);
        if (signal?.aborted) return; // element detached; drop the stale result.
        // Remember whether this colour has a saved order so the cross-tab
        // "Update Order" label is right even before the buyer visits the tab.
        this.#trackSavedOrder(value, record);
        if (!record || !Object.keys(record).length) return;
        if (this.#liveStateFor(value)) return; // user populated this tab while we were reading.
        const variantIds = variantIdsForOption(value, optionGroups);
        this.#initLiveStateFromRecord(migrateProductRecord(record) ?? {}, value, variantIds);
      } catch (err) {
        if (signal?.aborted) return;
        console.error('b2b-plus: prewarm failed for option', value, err);
      }
    }));
  }

  /**
   * Full root template: stylesheet links + empty state or main table layout (lit-html).
   */
  #productTableRootTemplate({ stylesheetHrefs, rowTemplates, needsBatch, optionGroups, activeProductJSON, enableProductTableSplit }) {
    // Flag every option tab that already holds quantities so CSS can border it.
    const valuesWithQty = new Set();
    for (const [value, tab] of this.#liveStates) {
      if (value && liveTabHasAnyQty(tab)) valuesWithQty.add(value);
    }

    // Two availability sets drive the option tabs (see optionTabsTemplate):
    //   • valuesWithInventory — the app carries inventory records for the value.
    //     A value NOT in here was never meant to be orderable → tab is hidden.
    //   • valuesWithStock — at least one of its lines is actually orderable
    //     (prebook, or quantity > 0). In the first set but not this one → sold
    //     out → tab renders disabled with a struck-through swatch.
    // Both compute* helpers return null when the inventory payload is
    // missing/failed; that case must keep every tab available rather than
    // hiding the whole strip, so the null check gates each rollup.
    const variantsWithInventory = computeVariantsWithInventory(
      this.productJSON?.variants ?? [],
      this.inventoryData?.inventories,
      inventoryLinesForVariant,
    );
    const variantsWithStock = computeVariantsWithStock(
      this.productJSON?.variants ?? [],
      this.inventoryData?.inventories,
      inventoryLinesForVariant,
      isPrebookOrderType,
    );
    const valuesWithInventory = new Set();
    const valuesWithStock     = new Set();
    for (const [value, variants] of optionGroups ?? new Map()) {
      if (variantsWithInventory === null
        || (variants ?? []).some((v) => variantsWithInventory.has(String(v.id)))) {
        valuesWithInventory.add(value);
      }
      if (variantsWithStock === null
        || (variants ?? []).some((v) => variantsWithStock.has(String(v.id)))) {
        valuesWithStock.add(value);
      }
    }

    const optionImages = enableProductTableSplit ? buildOptionImageMap(optionGroups, this.productJSON) : null;
    const splitLabel = splitOptionDisplayName(activeProductJSON ?? this.productJSON, b2bConfig().productTableSplitOption ?? '');
    const optionTabsTpl = enableProductTableSplit ? optionTabsTemplate(optionGroups, this.#selectedOptionValue, valuesWithQty, valuesWithInventory, optionImages, { isCart: this.isCart, optionName: splitLabel }, valuesWithStock) : nothing;

    // When the active option (e.g. colour) has no inventory we hide ONLY the
    // inventory matrix and show the empty message in its place — the product
    // details, option-tab swatches, and order summary all stay rendered so the
    // buyer can switch to a colour that has stock without losing their order.
    const emptyMatrixTpl = !rowTemplates.length
      ? emptyInventoryTemplate(this.#inventoryFetchFailed)
      : null;

    const { inventoryData } = this;
    const productJSON = activeProductJSON;

    const bulkCartApply =
      this.dataset.cartBulkApplyControls === 'true' ? this.#readCartBulkApplyMetaForTemplate() : null;
    if (bulkCartApply) {
      bulkCartApply.sig =
        `${this.dataset.cartBulkDepartments ?? ''}|${this.dataset.cartBulkPairs ?? ''}|${this.dataset.cartSimilarSizeLabel ?? ''}`;
    }

    const showFilterBar =
      this.showInventoryFilter ||
      this.showActionButton;

    const enableOrderSummary = b2bConfig().enableOrderSummary !== false;

    const viewToggleTpl = (this.isCart && enableOrderSummary)
      ? cartViewToggleTemplate(this.#cartViewMode)
      : nothing;

    const productDetailsTpl =
      this.showProductDetails && productJSON
        ? productDetailsTemplate(productJSON, {
            isCart: this.isCart,
            showRemoveButton: this.showRemoveButton,
            bulkCartApply,
            viewToggleTpl,
            selectedOption: this.#selectedOptionValue,
          })
        : nothing;

    const filterBarTpl = showFilterBar && !emptyMatrixTpl
      ? filterBarTemplate({
          showInventoryFilter: this.showInventoryFilter,
          showActionButton:    this.showActionButton,
          fd:                  getInventoryFilterData(inventoryData),
          isCart:              this.isCart,
          // When summary is on for PDP/quickview the canonical `[data-atc-btn]`
          // lives in the summary bar. Suppress the filter-bar copy so we don't
          // ship two ATC buttons (and so `#initAtcButton`'s delegated handler
          // doesn't have a stale duplicate to keep in sync).
          enableOrderSummary,
        })
      : nothing;

    // In split mode each rendered table is a single option group (e.g. one
    // colour), so the variant titles all repeat that option value (e.g.
    // "Red / Small"). Strip it from the column headers — the matrix already
    // identifies the colour via the active tab — leaving just the size.
    const isSplit = enableProductTableSplit && (optionGroups?.size ?? 0) >= 1;
    const headerLabeler = isSplit
      ? makeSummaryColumnLabeler(productJSON, String(b2bConfig().productTableSplitOption ?? '').trim())
      : null;
    const variantHeaderCellsTpl = html`${this.#variantHeaderTemplates(productJSON, inventoryData, headerLabeler)}`;
    const tbodyContentTpl       = needsBatch ? nothing : html`${rowTemplates}`;

    // "Add Shipment" UI — gated by `window.b2bPlus.config.enableAddShipment` (default: on).
    // Suppress when the matrix is empty — "Add Shipment" only adds delivery-window
    // rows to a matrix that isn't shown for an out-of-stock option.
    const enableAddShipment = b2bConfig().enableAddShipment !== false && !emptyMatrixTpl;
    let addShipmentTpl = nothing;
    if (enableAddShipment) {
      const addShipmentCombinations = buildAddShipmentCombinations(this.#combinationCaps);
      addShipmentTpl = addShipmentButtonTemplate(addShipmentCombinations, { isCart: this.isCart });
    }

    // Store for use by #renderOrderSummary (called after render and on every input).
    this.#renderedVariantIds  = (productJSON?.variants ?? []).map((v) => String(v.id));

    const orderSummaryTpl = enableOrderSummary
      ? orderSummaryWrapperTemplate({ isCart: this.isCart })
      : nothing;

    // Manual-save mode (cart only): empty slot the "Save changes" bar renders into.
    const manualSaveRootTpl = this.#isManualSave() ? html`<div data-manual-save-root></div>` : nothing;

    const layoutTpl = productTableLayoutTemplate({
      showProductDetails: this.showProductDetails,
      productDetailsTpl,
      filterBarTpl,
      variantHeaderCellsTpl,
      tbodyContentTpl,
      emptyMatrixTpl,
      productJSON,
      addShipmentTpl,
      optionTabsTpl,
      orderSummaryTpl,
      manualSaveRootTpl,
      isCart: this.isCart,
    });
    return html`${stylesheetLinksTemplate(stylesheetHrefs)}${layoutTpl}`;
  }

  #variantHeaderTemplates(productJSON, inventoryData, labeler = null) {
    const config = b2bConfig();
    const _inventories = inventoryData?.inventories;
    const _variants    = productJSON?.variants ?? [];
    const variantsWithInventory = computeVariantsWithInventory(_variants, _inventories, inventoryLinesForVariant);

    return _variants.map((variant) => {
      const noInventory = variantsWithInventory !== null && !variantsWithInventory.has(String(variant.id));
      return variantHeaderThTemplate(variant, {
        noInventory,
        showVariantPrice: config.showVariantPrice,
        isCart: this.isCart,
        label: labeler ? labeler(variant) : undefined,
      });
    });
  }

  #initProductLineTotalsSync() {
    this.#updateProductLineTotalsFromInputs();
    this.#inventoryTbody()?.querySelectorAll('[data-qty-input]').forEach((el) => {
      this.#on(el, 'input', () => this.#updateProductLineTotalsFromInputs());
    });
  }

  /**
   * Recompute the product-details "Total quantity" / "Total price" from the rows that
   * are currently visible.
   *
   * Public because the hiding is not always done by this element: `<b2b-plus-cart>`
   * hides rows itself when a delivery-window (shipment) / season / type filter is
   * applied, so it must ask each table to re-total afterwards.
   * @returns {void}
   */
  // @public
  refreshProductLineTotals() {
    this.#updateProductLineTotalsFromInputs();
  }

  /**
   * Sums the quantity inputs of the **visible** rows and updates the product details
   * line total / subtotal. Hidden rows are excluded so that selecting a shipment
   * (delivery-window) tab — or a season/type filter — scopes the totals to that
   * shipment rather than reporting the whole product.
   */
  #updateProductLineTotalsFromInputs() {
    if (!this.showProductDetails) return;
    const qtyEl   = this.#shadow.querySelector('[data-product-line-total-qty]');
    const priceEl = this.#shadow.querySelector('[data-product-line-total-price]');
    // Cart only: wholesale price follows the selected cells' unit prices.
    const unitEl  = this.#shadow.querySelector('[data-product-line-wholesale-price]');
    if (!qtyEl && !priceEl && !unitEl) return;

    const variantById = new Map(
      (this.productJSON?.variants ?? []).map((v) => [String(v.id), v]),
    );
    let q     = 0;
    let cents = 0;
    const unitCents = [];
    this.#inventoryTbody()?.querySelectorAll('[data-qty-input]').forEach((input) => {
      if (input.disabled) return;
      // `row.hidden` is set by the filter controller (PDP) and by <b2b-plus-cart>'s
      // #applyFilters (cart). Rows filtered out must not count toward the totals.
      const rowEl = input.closest('[data-b2b-plus-inventory-row]');
      if (rowEl?.hidden) return;
      const n = Number(input.value);
      if (!Number.isFinite(n) || n <= 0) return;
      const picker = rowEl?.querySelector('[data-delivery-date-picker]');
      const start = String(
        picker?.dataset.selectedStart
        || picker?.dataset.rowStart
        || rowEl?.dataset.rowStart
        || '',
      ).trim();
      const unit = unitCentsForInventorySelection(
        variantById.get(String(input.dataset.variantId)),
        input.dataset.type,
        start,
      );
      q     += n;
      cents += n * unit;
      unitCents.push(unit);
    });
    if (qtyEl)   qtyEl.textContent   = String(q);
    if (priceEl) priceEl.textContent = formatMoney(cents);
    if (unitEl)  unitEl.textContent  = summaryPriceLabelFromCents(unitCents) || (unitEl.dataset.fallback ?? '');
  }

  /**
   * Converts raw inventory data into lit-html `<tr>` templates.
   * Produces one base row per unique combination from the API, then appends any
   * additional shipment rows for that combination.
   * Pre-fill values: PDP reads from liveTab.combos, cart reads from this.cartRecord.
   * @param {object} productJSON
   * @param {object} inventoryData
   * @param {object|null} [liveTab=null] - PDP liveState tab; ignored on cart.
   * @returns {import('lit-html').TemplateResult[]}
   */
  #inventoryDataToRowTemplates(productJSON, inventoryData, liveTab = null) {
    const { rows, variantIds } = getMergedInventoryRows(
      productJSON, inventoryData, startOfLocalDay(new Date()), PT_SDK_DEPS,
    );
    if (!rows.length) return [];

    const config = b2bConfig();

    const thresholdRules = inventoryData?.inventory_threshold_rules ?? [];
    const allTemplates   = [];

    rows.forEach((row, rowIndex) => {
      // When the ATS horizon is disabled, omit atsPicker so bounds match the full API window.
      const atsPicker = getAtsImmediatePickerHorizonDays() === null
        ? null
        : {
          isLockedType: isLockedShipmentWindowEnabled,
          isAtsImmediateOrderType,
          atsImmediatePickerEnd,
          today: startOfLocalDay(new Date()),
        };
      const { inventoryStartStr, inventoryEndStr, pickerStartStr, pickerEndStr } =
        inventoryDateStrsFromRow(row, toISODate, atsPicker);
      const comboKeyStr  = combinationKey({
        type:      row.type,
        season:    row.season,
        startDate: inventoryStartStr,
        endDate:   inventoryEndStr,
      });
      const isPrebookRow = isPrebookOrderType(row.type);
      const lockedWindowDates = isLockedShipmentWindowEnabled(row.type)
        ? this.#lockedPrefillDeliveryDates(pickerStartStr, pickerEndStr)
        : null;
      const lockedWindow = lockedWindowDates
        ? joinDeliveryWindow(lockedWindowDates.selectedStart, lockedWindowDates.selectedEnd)
        : '';
      const baseParams   = {
        row, rowIndex, variantIds,
        rowStartStr: inventoryStartStr,
        rowEndStr: inventoryEndStr,
        pickerStartStr,
        pickerEndStr,
        isPrebookRow, thresholdRules, enableItemNotes: !!config.enableItemNotes,
        combinationKeyStr: comboKeyStr,
        isCart: this.isCart,
        isExpiredRow: !!row.expired,
        quantityRules: this.#getQuantityRules(),
        dateLocked: !!lockedWindow,
        productPrice: productJSON?.price ?? null,
        seasonPrices: seasonPricesFromVariants(productJSON?.variants),
      };

      if (this.isCart) {
        // Tolerant lookup: a record saved with resolved inventory dates in the
        // combo key won't byte-match the blank-API-date key the table now
        // computes from inventory. resolveCartComboForDomKey falls back to a
        // type+season match so saved quantities still pre-fill the grid.
        const comboRecord = resolveCartComboForDomKey(this.cartRecord, comboKeyStr)?.combo;
        const recordRows  = comboRecord?.rows ?? [];
        const baseRecord  = recordRows.find((r) => r?.rowId === 'base');
        allTemplates.push(inventoryRowTemplate({
          ...baseParams,
          rowId:                 'base',
          shipmentIndex:         0,
          initialQtys:           baseRecord?.byVariantId    ?? {},
          // Shared pre-populated date still shows when prefillDeliveryDateWindow is false.
          initialDeliveryWindow: lockedWindow || this.#resolveRowDeliveryWindow(row.type, baseRecord?.deliveryWindow),
          initialNote:           baseRecord?.note           ?? '',
        }));
        recordRows
          .filter((r) => r?.rowId && r.rowId !== 'base')
          .forEach((r, si) => {
            allTemplates.push(inventoryRowTemplate({
              ...baseParams,
              rowId:                 r.rowId,
              shipmentIndex:         si + 1,
              shipmentName:          r.shipmentName    ?? '',
              initialQtys:           r.byVariantId     ?? {},
              initialDeliveryWindow: lockedWindow || this.#resolveRowDeliveryWindow(row.type, r.deliveryWindow),
              initialNote:           r.note            ?? '',
            }));
          });
      } else {
        const liveCombo   = liveTab?.combos?.[comboKeyStr];
        const liveBaseRow = liveCombo?.rows?.['base'];
        allTemplates.push(inventoryRowTemplate({
          ...baseParams,
          rowId:                 'base',
          shipmentIndex:         0,
          initialQtys:           liveBaseRow?.qtys           ?? {},
          // Shared pre-populated date still shows when prefillDeliveryDateWindow is false.
          initialDeliveryWindow: lockedWindow || this.#resolveRowDeliveryWindow(row.type, liveBaseRow?.deliveryWindow),
          initialNote:           liveBaseRow?.note           ?? '',
        }));
        (liveCombo?.rowIds ?? [])
          .filter((id) => id !== 'base')
          .forEach((rowId, si) => {
            const liveRow = liveCombo.rows[rowId];
            allTemplates.push(inventoryRowTemplate({
              ...baseParams,
              rowId,
              shipmentIndex:         si + 1,
              shipmentName:          liveRow?.shipmentName   ?? '',
              initialQtys:           liveRow?.qtys           ?? {},
              initialDeliveryWindow: lockedWindow || this.#resolveRowDeliveryWindow(row.type, liveRow?.deliveryWindow),
              initialNote:           liveRow?.note           ?? '',
            }));
          });
      }
    });

    return allTemplates;
  }

  /**
   * IDB storage key for this table instance.
   * Single-option products: plain `productJSON.handle`.
   * Split-option products: `”handle::optionValue”` scoped to the active tab.
   * @returns {string}
   */
  get #storageHandle() {
    return this.#storageHandleFor(this.#selectedOptionValue);
  }

  /**
   * IDB key for an arbitrary option value (not just the active tab).
   * Used by #prewarmTabRecords to read every option's saved data without mutating selection.
   *
   * On the cart the host (`expandValidProducts`) already scopes `productJSON.handle`
   * to the full storage key — `handle` for a single-option product, `handle::Red`
   * for a split-option line. Re-appending the active option would double the
   * suffix (`handle::Red::Red`) and miss the saved record, so the item would
   * render with no quantities. Use the handle as-is on the cart; there is no
   * cross-tab reading here (`#prewarmTabRecords` is PDP/quickview only), so the
   * `optionValue` argument is irrelevant.
   * @param {string|null} optionValue
   * @returns {string}
   */
  #storageHandleFor(optionValue) {
    if (this.isCart) return this.productJSON?.handle ?? '';
    return optionStorageKey(this.productJSON?.handle, optionValue);
  }

  /**
   * Build a serialisable snapshot from the source product table to drive cart-wide bulk apply.
   * Called by `<b2b-plus-cart>` when orchestrating cross-table propagation.
   * @param {{ m: string, t?: string, s?: string, d?: string }} detail
   * @returns {{ kind: 'size', sizeToQty: Record<string, number> }|null}
   */
  // @public
  getBulkSourceSnapshot(detail) { return this.#bulkApply?.getBulkSourceSnapshot(detail) ?? null; }

  /**
   * Apply a cart-wide snapshot to **this** product table (all matching rows, including same product).
   * @param {{ kind: 'size', sizeToQty: Record<string, number> }} snapshot
   * @param {{ m: string, t?: string, s?: string, d?: string }} detail
   * @returns {{ updated: boolean, clamped: boolean, siblingMutated: boolean }}
   */
  // @public
  applyCartBulkSnapshot(snapshot, detail) { return this.#bulkApply?.applyCartBulkSnapshot(snapshot, detail) ?? { updated: false, clamped: false, siblingMutated: false }; }

  /**
   * Validates the table's current input state for checkout.
   * Displays inline error messages for any violations and returns the result so
   * callers (e.g. b2b-plus-cart) can aggregate errors without querying shadow internals.
   * `issues` describes each failure as a cart issue (see `CART_ISSUE_TYPES` in
   * b2b-plus/cart-sdk) for the cart's issue strip and per-product issue list.
   * @returns {{ valid: boolean, firstErrorEl: Element|null, issues: object[] }}
   */
  // @public
  validate() {
    const root = this.#shadow;
    let hasErrors    = false;
    let firstErrorEl = null;
    // Structured copy of every failure, for the cart's issue strip / per-product list.
    const issues = [];
    const handle = this.dataset.productHandle || this.productJSON?.handle || '';
    const rowFacts = (row) => ({
      handle,
      type:           row.dataset.rowType ?? '',
      season:         row.dataset.rowSeason ?? '',
      startDate:      row.dataset.rowStart ?? '',
      endDate:        row.dataset.rowEnd ?? '',
      rowId:          row.dataset.rowId ?? '',
      combinationKey: row.dataset.combinationKey ?? '',
    });

    // Reconcile cross-shipment sums against the API cap before validating, so
    // two rows that are each within cap but sum over it are caught at checkout
    // (not only on live keystrokes / load). When a clamp was actually needed,
    // block this submit and surface the corrected input so the buyer reviews the
    // reduced quantity instead of the order silently shrinking. A cart already
    // reconciled on load / by keystroke mutates nothing here and validates clean.
    if (this.#reconcileCartSiblingCaps()) {
      hasErrors = true;
      firstErrorEl = root.querySelector('[data-qty-input].b2b-plus-quantity-input--error') ?? this;
      // One issue per input the reconcile just reduced, so the strip names what changed.
      for (const input of root.querySelectorAll('[data-qty-input].b2b-plus-quantity-input--error')) {
        const row = input.closest('[data-b2b-plus-inventory-row]');
        if (!row) continue;
        const max = input.dataset.maxQty !== undefined ? Number(input.dataset.maxQty) : null;
        issues.push({
          ...rowFacts(row), code: 'qty-above-max', variantId: input.dataset.variantId ?? '',
          max, requested: Number(input.value),
          message: input.parentElement?.querySelector('[data-qty-error]')?.textContent?.trim()
            || b2bPlusString('qtyExceededError').replace('{max}', String(max ?? '')),
        });
      }
    }

    const minCheckoutErr = root.querySelector('[data-checkout-min-qty-error]');
    if (minCheckoutErr) { minCheckoutErr.hidden = true; minCheckoutErr.textContent = ''; }

    const qtyInputs = [...root.querySelectorAll('[data-qty-input]')];
    if (qtyInputs.length && !qtyInputs.some((i) => Number(i.value) > 0)) {
      if (minCheckoutErr) {
        minCheckoutErr.textContent = b2bPlusString('cartCheckoutMinQtyProduct');
        minCheckoutErr.hidden = false;
      }
      hasErrors = true;
      firstErrorEl = minCheckoutErr ?? this;
      issues.push({ code: 'no-quantity', handle, message: b2bPlusString('cartCheckoutMinQtyProduct') });
    }

    root.querySelectorAll('[data-b2b-plus-inventory-row][data-row-season]').forEach((row) => {
      const filledInputs = [...row.querySelectorAll('[data-qty-input]')].filter((i) => Number(i.value) > 0);
      if (!filledInputs.length) return;

      const datePicker  = row.querySelector('[data-delivery-date-picker]');
      const dateErrorEl = row.querySelector('[data-date-error]');
      const hasDate     = datePicker?.dataset.selectedStart && datePicker?.dataset.selectedEnd;

      if (!hasDate) {
        datePicker?.classList.add('b2b-plus-date-picker--error');
        if (dateErrorEl) { dateErrorEl.textContent = b2bPlusString('dateRequiredError'); dateErrorEl.hidden = false; }
        hasErrors = true;
        if (!firstErrorEl) firstErrorEl = dateErrorEl ?? datePicker;
        issues.push({ ...rowFacts(row), code: 'date-required', message: b2bPlusString('dateRequiredError') });
      } else if (isDeliveryWindowPast(datePicker.dataset.selectedStart)) {
        // A restored date that has gone by is kept in the field (see
        // #flagPastCartDeliveryDates) but can never be ordered — block until re-picked.
        this.#showPastDeliveryDateError(datePicker);
        hasErrors = true;
        if (!firstErrorEl) firstErrorEl = dateErrorEl ?? datePicker;
        issues.push({ ...rowFacts(row), code: 'date-past', message: b2bPlusString('deliveryDatePastError') });
      }

      filledInputs.forEach((input) => {
        const maxQty   = input.dataset.maxQty !== undefined ? Number(input.dataset.maxQty) : null;
        const minQty   = input.dataset.minQty !== undefined ? Number(input.dataset.minQty) : null;
        const qty      = Number(input.value);
        const qtyError = input.parentElement?.querySelector('[data-qty-error]');
        if (maxQty !== null && qty > maxQty) {
          const message = b2bPlusString('qtyExceededError').replace('{max}', String(maxQty));
          if (qtyError) { qtyError.textContent = message; qtyError.hidden = false; }
          hasErrors = true;
          if (!firstErrorEl) firstErrorEl = qtyError ?? input;
          issues.push({
            ...rowFacts(row), code: 'qty-above-max', variantId: input.dataset.variantId ?? '',
            max: maxQty, requested: qty, message,
          });
        } else if (minQty !== null && qty > 0 && qty < minQty) {
          const message = b2bPlusString('qtyBelowMinError').replace('{min}', String(minQty));
          if (qtyError) { qtyError.textContent = message; qtyError.hidden = false; }
          hasErrors = true;
          if (!firstErrorEl) firstErrorEl = qtyError ?? input;
          issues.push({
            ...rowFacts(row), code: 'qty-below-min', variantId: input.dataset.variantId ?? '',
            min: minQty, requested: qty, message,
          });
        }
      });
    });

    // The summary view (cart default) hides the min-qty banner AND the entire
    // inventory matrix where the per-row date / qty errors live, so a validation
    // failure would render invisible. Flip the line into edit view so every error
    // surfaces and the caller's scrollIntoView(firstErrorEl) lands on a shown node.
    if (hasErrors && this.isCart && this.#cartViewMode === 'summary'
        && b2bConfig().enableOrderSummary !== false) {
      this.#setCartViewMode('edit');
    }

    return { valid: !hasErrors, firstErrorEl, issues };
  }

  /**
   * Cart-only: show this product's checkout issues. Each issue pinned to a quantity cell
   * or delivery date gets its cart-wide number as a badge (plus a short status under a
   * quantity cell), and every issue is listed under the matrix. `[]` clears them all.
   * Issues are re-pinned after every re-render (option tab switch), so this can be
   * called at any time — including before the first render finishes.
   * @param {object[]} issues - Numbered issues for this product (see `numberCartIssues`).
   * @returns {void}
   */
  // @public
  setCartIssues(issues) {
    this.#cartIssues = Array.isArray(issues) ? issues : [];
    this.#renderCartIssues();
  }

  /**
   * Cart-only: scroll to (and, for an editable quantity, focus) the element issue
   * `number` is pinned to — its list row when it has no cell. Returns false when this
   * table does not carry that issue.
   * @param {number} number
   * @returns {boolean}
   */
  // @public
  focusCartIssue(number) {
    const target = this.#cartIssueTargets.get(Number(number));
    if (!target) return false;
    target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    if (target.matches?.('[data-qty-input]') && !target.disabled) target.focus({ preventScroll: true });
    const flashEl = target.closest?.('[data-cart-issue-cell]') ?? target;
    flashEl.removeAttribute('data-cart-issue-flash');
    // Force a reflow so re-flashing the same element restarts the animation.
    void flashEl.offsetWidth;
    flashEl.setAttribute('data-cart-issue-flash', '');
    setTimeout(() => flashEl.removeAttribute('data-cart-issue-flash'), 1600);
    return true;
  }

  /**
   * Issue list rows jump to their cell; editing a flagged quantity (or fixing a flagged
   * date) reports the issue as resolved so the cart can drop it. Registered per render
   * via `#on`, so tab-switch teardown removes it.
   */
  #initCartIssueListeners() {
    const root = this.#shadow;
    const jump = (e) => {
      const item = e.target?.closest?.('[data-cart-issue-item]');
      if (!item || !root.contains(item)) return;
      if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      this.focusCartIssue(Number(item.dataset.cartIssueItem));
    };
    this.#on(root, 'click', jump);
    this.#on(root, 'keydown', jump);
    this.#on(root, 'input', (e) => {
      if (!this.#cartIssues.length || !e.target?.matches?.('[data-qty-input]')) return;
      const anchor = e.target.closest('[data-cart-issue-cell]');
      const resolved = this.#cartIssues
        .filter((issue) => issue.code === 'no-quantity'
          || (anchor && this.#locateCartIssue(issue)?.input === e.target))
        .filter((issue) => issue.code !== 'no-quantity' || Number(e.target.value) > 0)
        .map((issue) => issue.number);
      this.#reportResolvedCartIssues(resolved);
    });
    // Dates commit through flatpickr (no `input` event) and always save — re-check the
    // date issues after each save.
    this.#on(this, 'b2b:cart-saved', () => {
      const resolved = this.#cartIssues
        .filter((issue) => issue.code === 'date-required' || issue.code === 'date-past')
        .filter((issue) => {
          const picker = this.#locateCartIssue(issue)?.picker;
          const start  = picker?.dataset.selectedStart;
          return Boolean(start && picker.dataset.selectedEnd && !isDeliveryWindowPast(start));
        })
        .map((issue) => issue.number);
      this.#reportResolvedCartIssues(resolved);
    });
  }

  /** @param {number[]} numbers */
  #reportResolvedCartIssues(numbers) {
    if (!numbers.length) return;
    this.dispatchEvent(new CustomEvent('b2b:cart-issues-resolved', {
      bubbles: true,
      detail: { handle: this.dataset.productHandle ?? '', numbers },
    }));
  }

  /**
   * Find where an issue lives in the rendered matrix. Rows are matched by row id first
   * (DOM-validation issues carry it), then by inventory window, then by type + season;
   * a quantity issue prefers the row that actually orders the variant.
   * @param {object} issue
   * @returns {{ row: Element, anchor: Element|null, input: Element|null, picker: Element|null, header: Element|null }|null}
   */
  #locateCartIssue(issue) {
    const scope = CART_ISSUE_TYPES[issue?.code]?.scope;
    if (!scope || scope === 'product') return null;
    const rows = [...(this.#inventoryTbody()?.querySelectorAll('[data-b2b-plus-inventory-row]') ?? [])];
    const inputIn = (row) => (issue.variantId
      ? row.querySelector(`[data-qty-input][data-variant-id="${CSS.escape(String(issue.variantId))}"]`)
      : null);

    let row = issue.rowId
      ? rows.find((r) => r.dataset.rowId === issue.rowId && r.dataset.combinationKey === issue.combinationKey) ?? null
      : null;
    if (!row) {
      const sameKind = (r) => (r.dataset.rowType ?? '') === (issue.type ?? '')
        && (r.dataset.rowSeason ?? '') === (issue.season ?? '');
      const exact = rows.filter((r) => sameKind(r)
        && (r.dataset.rowStart ?? '') === (issue.startDate ?? '')
        && (r.dataset.rowEnd ?? '') === (issue.endDate ?? ''));
      const candidates = exact.length ? exact : rows.filter(sameKind);
      if (scope === 'cell') {
        const ordering = (r) => Number(inputIn(r)?.value) > 0;
        row = candidates.find(ordering) ?? candidates[0] ?? rows.find(ordering) ?? null;
      } else if (issue.requestedWindow) {
        // A row issue from storage carries the date that passed — prefer the row showing it.
        const shows = (r) => {
          const p = r.querySelector('[data-delivery-date-picker]');
          return joinDeliveryWindow(p?.dataset.selectedStart, p?.dataset.selectedEnd) === issue.requestedWindow;
        };
        row = candidates.find(shows) ?? rows.find(shows) ?? candidates[0] ?? null;
      } else {
        row = candidates[0] ?? null;
      }
    }
    if (!row) return null;

    const picker = row.querySelector('[data-delivery-date-picker]');
    if (scope === 'row') {
      return { row, anchor: picker?.parentElement ?? null, input: picker, picker, header: null };
    }
    const input = inputIn(row);
    const cell  = input?.closest('[role="cell"]') ?? null;
    let header  = null;
    if (cell) {
      const index = [...row.querySelectorAll('[role="cell"]')].indexOf(cell);
      header = this.#shadow.querySelectorAll('[data-b2b-plus-inventory-table] [role="columnheader"]')[index + 1] ?? null;
    }
    return { row, anchor: cell, input, picker, header };
  }

  /**
   * "3T · Feb 1, 2027": the column's variant (with the option name when the product has
   * a single option, e.g. "Size 3T") and the row's delivery date — or its order type +
   * season when no date is picked.
   * @param {object} issue
   * @param {object|null} target - `#locateCartIssue()` result.
   * @returns {string}
   */
  #cartIssueLabel(issue, target) {
    const scope = CART_ISSUE_TYPES[issue.code]?.scope;
    if (scope === 'product') return '';
    let variantLabel = '';
    if (scope === 'cell') {
      const variant = (this.productJSON?.variants ?? []).find((v) => String(v.id) === String(issue.variantId));
      const title   = variant?.title && variant.title !== 'Default Title' ? variant.title : (issue.variantTitle || issue.sku || '');
      const options = this.productJSON?.options ?? [];
      variantLabel  = title && options.length === 1 ? `${optionLabel(options[0])} ${title}`.trim() : title;
    }
    const dateLabel = String(target?.picker?.value ?? '').trim()
      || formatDisplayWindowString(issue.requestedWindow ?? '')
      || formatDisplayDeliveryWindow(issue.startDate ?? '', issue.endDate ?? '')
      || [issue.type, issue.season].filter(Boolean).join(' ');
    if (!variantLabel || !dateLabel) return variantLabel || dateLabel;
    return (b2bPlusString('cartIssueCellLabel') || '{variant} · {date}')
      .replace('{variant}', variantLabel)
      .replace('{date}', dateLabel);
  }

  /** Pin the number badge (and quantity status) of `issue` onto its cell. */
  #pinCartIssue(issue, { anchor, header }) {
    header?.setAttribute('data-cart-issue-column', '');
    // Two issues on one cell: the lower number owns the badge, both stay in the list.
    if (anchor.hasAttribute('data-cart-issue-cell')) return;
    anchor.setAttribute('data-cart-issue-cell', CART_ISSUE_TYPES[issue.code]?.scope ?? '');
    const badge = document.createElement('span');
    badge.className = 'b2b-plus-cell-issue-badge';
    badge.setAttribute('data-cart-issue-badge', '');
    badge.setAttribute('aria-hidden', 'true');
    badge.textContent = String(issue.number);
    anchor.appendChild(badge);

    const status = cartIssueCellStatus(issue);
    if (!status) return;
    let text = b2bPlusString(status.key) || status.fallback;
    for (const [token, value] of Object.entries(status.values)) text = text.replaceAll(`{${token}}`, value);
    const statusEl = document.createElement('span');
    statusEl.className = 'b2b-plus-cell-issue-status';
    statusEl.setAttribute('data-cart-issue-status', '');
    statusEl.textContent = text;
    anchor.appendChild(statusEl);
  }

  /** Re-draw the issue badges, statuses, title pill and list from `#cartIssues`. */
  #renderCartIssues() {
    if (!this.isCart) return;
    const root = this.#shadow;
    if (!root) return;
    root.querySelectorAll('[data-cart-issue-badge], [data-cart-issue-status]').forEach((el) => el.remove());
    root.querySelectorAll('[data-cart-issue-cell]').forEach((el) => el.removeAttribute('data-cart-issue-cell'));
    root.querySelectorAll('[data-cart-issue-column]').forEach((el) => el.removeAttribute('data-cart-issue-column'));
    this.#cartIssueTargets.clear();

    const issues = this.#cartIssues;
    // The summary view (cart default) hides the matrix the badges are pinned to.
    if (issues.length && this.#cartViewMode === 'summary' && b2bConfig().enableOrderSummary !== false
        && root.querySelector('.b2b-plus-table-container')) {
      this.#setCartViewMode('edit');
    }
    const listed = issues.map((issue) => {
      const target = this.#locateCartIssue(issue);
      if (target?.anchor) {
        this.#pinCartIssue(issue, target);
        this.#cartIssueTargets.set(issue.number, target.input ?? target.anchor);
      }
      return { ...issue, label: this.#cartIssueLabel(issue, target) };
    });

    const listEl = root.querySelector('[data-cart-issues-list]');
    if (listEl) {
      render(listed.length ? cartIssueListTemplate(listed) : nothing, listEl);
      listEl.hidden = !listed.length;
      // Issues with no cell (product-level, or a variant missing from the matrix) jump
      // to their list row instead.
      for (const issue of listed) {
        if (this.#cartIssueTargets.has(issue.number)) continue;
        const item = listEl.querySelector(`[data-cart-issue-item="${issue.number}"]`);
        if (item) this.#cartIssueTargets.set(issue.number, item);
      }
    }

    const pill = root.querySelector('[data-cart-issues-pill]');
    if (pill) {
      pill.textContent = issues.length
        ? (b2bPlusString(issues.length === 1 ? 'cartIssuesPillOne' : 'cartIssuesPillOther') || '{count} issues')
          .replaceAll('{count}', String(issues.length))
        : '';
      pill.hidden = !issues.length;
    }

    const hasIssues = issues.length > 0;
    this.toggleAttribute('data-b2b-plus-has-issues', hasIssues);
    this.closest('[data-b2b-plus-cart-line-slot]')?.toggleAttribute('data-b2b-plus-has-issues', hasIssues);
  }

  /**
   * Flush any in-flight auto-save and await the committed write, so a caller that
   * reads the IDB record immediately after an edit (e.g. checkout validation) never
   * sees a stale record. Two save paths must be covered:
   *   - Qty/shipment-name edits debounce for 1s (`#debounceTimer`) — fire it now.
   *   - Delivery-date commits and multi-apply save IMMEDIATELY but un-awaited, with
   *     no timer set — so awaiting `#debounceTimer` alone would miss them.
   * Awaiting `#pendingSavePromise` (set on every save path) covers both. No-op when
   * nothing has been saved.
   * @returns {Promise<void>}
   */
  async flushPendingSave() {
    if (this.#debounceTimer !== null) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
      this.#onAutoSave?.();
    } else if (this.#isManualSave() && this.#isDirty) {
      // Manual mode: commit staged edits so checkout validation reads them.
      this.#onAutoSave?.();
      this.#clearDirty();
    }
    await this.#pendingSavePromise;
  }

  /**
   * Push fresh inventory availability into the already-rendered rows.
   *
   * The cart renders each table once with the inventory snapshot from page load.
   * When checkout re-fetches inventory and stock has changed, the displayed
   * "available" number and per-input cap (`data-max-qty`) are stale, so the top
   * banner is the only signal. This re-derives each rendered row's available
   * quantity from the fresh data, updates the cap + availability badge, and
   * highlights any input whose current quantity now exceeds available stock.
   *
   * Does NOT auto-clamp — the buyer is asked to review and adjust (matching the
   * cart's "Please review and adjust quantities" banner). Returns whether any
   * rendered input now exceeds available stock.
   *
   * @param {object} freshInventoryData - raw inventory API `data` object
   * @returns {boolean}
   */
  refreshAvailabilityFromInventory(freshInventoryData) {
    const root = this.#shadow;
    if (!root || !freshInventoryData) return false;

    this.inventoryData = freshInventoryData;
    // Rebuild the cached per-combination caps from the fresh inventory so the
    // keystroke sibling-cap pass (`#updateSiblingCaps`) no longer restores the
    // stale `data-max-qty` (e.g. 110) after we clamp the input down to the new
    // availability (e.g. 100). Reads `this.inventoryData`, set above.
    this.#buildCombinationCaps();
    const freshInventoriesMap = freshInventoryData.inventories ?? {};
    const thresholdRules      = freshInventoryData.inventory_threshold_rules ?? [];
    const variants            = Array.isArray(this.productJSON?.variants) ? this.productJSON.variants : [];
    let anyExceeded = false;
    let anyClamped  = false;

    root.querySelectorAll('[data-b2b-plus-inventory-row]').forEach((rowEl) => {
      rowEl.querySelectorAll('[data-qty-input][data-variant-id]').forEach((input) => {
        // Prebook rows have no stock cap; unavailable/disabled inputs have no badge.
        if (input.disabled || 'unavailable' in input.dataset) return;
        if (isPrebookOrderType(input.dataset.type ?? '')) return;

        const variant = variants.find((v) => String(v.id) === String(input.dataset.variantId));
        if (!variant) return;

        const lines = inventoryLinesForVariant(variant, freshInventoriesMap) ?? [];
        const matched = findMatchedInventoryLine({
          cartOrderType: input.dataset.type   ?? '',
          cartSeason:    input.dataset.season ?? '',
          cartStartDate: input.dataset.start  ?? '',
          cartEndDate:   input.dataset.end    ?? '',
          lines,
        });
        // No match → leave the row as-is; the checkout banner still blocks it.
        if (!matched) return;
        const available = Number(matched.quantity);
        if (!Number.isFinite(available)) return;

        // Fold the catalog quantity-rule max back into the refreshed cap so a
        // re-fetch with higher stock can't lift the input above its rule ceiling.
        const ruleMax = this.#getQuantityRules()[input.dataset.variantId]?.max ?? null;
        let   effMax  = effectiveMaxQty(available, ruleMax) ?? available;

        // When pooling is on, the orderable cap is the pooled headroom (already
        // rebuilt from fresh inventory + live demand by #buildCombinationCaps
        // above, with the rule max folded in), NOT this window's raw stock.
        if (this.#poolingEnabled()) {
          const ck     = input.closest('[data-b2b-plus-inventory-row]')?.dataset.combinationKey;
          const pooled = ck ? this.#combinationCaps.get(ck)?.[input.dataset.variantId] : undefined;
          if (pooled != null && Number.isFinite(Number(pooled))) effMax = Number(pooled);
        }

        // Update the cap + the displayed availability badge/number (the badge
        // still shows true stock; the cap is the effective rule-bounded max).
        input.dataset.maxQty = String(effMax);
        const wrap = input.parentElement?.querySelector('.available-quantity');
        const numEl = wrap?.querySelector('[data-available-qty]');
        if (numEl) setSlotText(numEl, available);
        const badge = wrap?.querySelector('.b2b-plus-availability-badge');
        if (badge) {
          const color = resolveThresholdColor(available, thresholdRules);
          if (color) badge.style.backgroundColor = color;
          else badge.style.removeProperty('background-color');
        }

        // Clamp the current quantity down to the refreshed cap so the buyer can
        // only keep the latest available amount (mirrors `#validateQtyInput` /
        // `#clampCartQtysToMax`), and surface the standard inline error.
        const qty      = Number(input.value);
        const qtyError = input.parentElement?.querySelector('[data-qty-error]');
        if (qty > effMax) {
          input.value = String(effMax);
          input.classList.add('b2b-plus-quantity-input--error');
          if (qtyError) {
            qtyError.textContent = b2bPlusString('qtyExceededError').replace('{max}', String(effMax));
            qtyError.hidden = false;
          }
          anyExceeded = true;
          anyClamped  = true;
        } else {
          input.classList.remove('b2b-plus-quantity-input--error');
          if (qtyError) qtyError.hidden = true;
        }
      });
    });

    // Persist the clamped quantities so IndexedDB (and the next checkout pass)
    // matches the corrected display. No-op when nothing was clamped, this isn't
    // the cart, or auto-save is off.
    if (anyClamped && this.isCart) this.#onAutoSave?.();

    // Re-express the badges as pooled remaining (overrides the per-window raw
    // stock written above). Recompute first so the numbers reflect any values
    // just clamped in the loop. No-op when pooling is disabled.
    if (this.#poolingEnabled()) {
      this.#recomputePooledCaps();
      this.#updatePooledAvailabilityDisplay();
    }

    return anyExceeded;
  }

  /**
   * Initialise thumbnail-click behaviour for every `[data-slider]` in the shadow root.
   */
  #initImageSliders() {
    this.#shadow.querySelectorAll('[data-slider]').forEach((slider) => {
      const mainImg = slider.querySelector('[data-slider-main]');
      if (!mainImg) return;
      slider.querySelectorAll('[data-slider-thumb]').forEach((thumb) => {
        this.#on(thumb, 'click', () => {
          mainImg.src = thumb.dataset.src;
          slider.querySelectorAll('[data-slider-thumb]').forEach((t) => t.classList.toggle('is-active', t === thumb));
        });
      });
    });
  }

  /**
   * Initialise Flatpickr on every `[data-delivery-date-picker]` in the inventory tbody.
   */
  #initDatePickers() {
    this.#flatpickrInstances = new Map();
    this.#inventoryTbody()?.querySelectorAll('[data-delivery-date-picker]').forEach((input) => {
      this.#initDatePickerForInput(input);
    });
  }

  /**
   * Default delivery range for a row's inventory bounds — same rules as the date picker prefill.
   * Used so IndexedDB `deliveryWindow` on base + shipment rows matches the DOM when prefill is on.
   * @param {string} rowStartStr - `data-row-start` (YYYY-MM-DD)
   * @param {string} rowEndStr   - `data-row-end` (YYYY-MM-DD)
   * @returns {{ selectedStart: string, selectedEnd: string } | null}
   */
  #defaultPrefillDeliveryDates(rowStartStr, rowEndStr) {
    const { minDays, maxDays } = getShipWindowLengths(this.inventoryData);
    return defaultPrefillDeliveryDates(rowStartStr, rowEndStr, {
      minDays,
      maxDays,
      prefillEnabled: b2bConfig()?.prefillDeliveryDateWindow !== false,
      today:          startOfLocalDay(new Date()),
    }, PT_SDK_DEPS);
  }

  /**
   * Horizon-length delivery window for a locked inventory type (today/start → start + N).
   * Always prefills, independent of `prefillDeliveryDateWindow`.
   * @param {string} rowStartStr
   * @param {string} rowEndStr
   * @returns {{ selectedStart: string, selectedEnd: string } | null}
   */
  #lockedPrefillDeliveryDates(rowStartStr, rowEndStr) {
    const horizonDays = getAtsImmediatePickerHorizonDays();
    if (horizonDays === null) return null;
    const { minDays, maxDays } = getShipWindowLengths(this.inventoryData);
    const today = startOfLocalDay(new Date());
    const start = String(rowStartStr ?? '').trim() || toISODate(today);
    return defaultPrefillDeliveryDates(start, rowEndStr, {
      minDays,
      maxDays,
      prefillEnabled: true,
      today,
      horizonDays,
    }, PT_SDK_DEPS);
  }

  /**
   * Default delivery window string for a row, ignoring `prefillDeliveryDateWindow`.
   * Reuses {@link defaultPrefillDeliveryDates} (today → today + minDays − 1, clamped).
   * @param {string} rowStartStr
   * @param {string} rowEndStr
   * @returns {string} Canonical `deliveryWindow` or '' when bounds are unusable.
   */
  #fallbackDeliveryWindow(rowStartStr, rowEndStr) {
    const { minDays, maxDays } = getShipWindowLengths(this.inventoryData);
    const today = startOfLocalDay(new Date());
    // Blank inventory start → treat as today so the helper can still build a window.
    const start = String(rowStartStr ?? '').trim() || toISODate(today);
    const dates = defaultPrefillDeliveryDates(start, rowEndStr, {
      minDays,
      maxDays,
      prefillEnabled: true,
      today,
    }, PT_SDK_DEPS);
    return dates ? joinDeliveryWindow(dates.selectedStart, dates.selectedEnd) : '';
  }

  /**
   * On option-tab leave: copy any picker selection into liveState; for rows that
   * still have qty but no date, assign {@link #fallbackDeliveryWindow}. Does not
   * run on cart (DOM is source of truth) and does not change active-tab ATC
   * validation for a missing date on the current colour.
   * @param {{ combos?: object }} leavingTab
   */
  #ensureLeavingTabDeliveryWindows(leavingTab) {
    if (!leavingTab || this.isCart) return;
    const tbody = this.#inventoryTbody();
    if (!tbody) return;

    tbody.querySelectorAll('[data-b2b-plus-inventory-row][data-row-id][data-combination-key]').forEach((tr) => {
      const liveRow = leavingTab.combos?.[tr.dataset.combinationKey]?.rows?.[tr.dataset.rowId];
      if (!liveRow) return;
      const hasQty = Object.values(liveRow.qtys ?? {}).some((q) => Number(q) > 0);
      if (!hasQty) return;

      const picker = tr.querySelector('[data-delivery-date-picker]');
      const start  = (picker?.dataset.selectedStart ?? '').trim();
      const end    = (picker?.dataset.selectedEnd ?? '').trim();
      if (start) {
        liveRow.deliveryWindow = joinDeliveryWindow(start, end || start);
        liveRow.hasDateError   = false;
        return;
      }
      if (String(liveRow.deliveryWindow ?? '').trim()) return;

      const rowStart = (picker?.dataset.rowStart ?? tr.dataset.rowStart ?? '').trim();
      const rowEnd   = (picker?.dataset.rowEnd ?? tr.dataset.rowEnd ?? '').trim();
      const lockedDates = isLockedShipmentWindowEnabled(tr.dataset.rowType ?? '')
        ? this.#lockedPrefillDeliveryDates(rowStart, rowEnd)
        : null;
      const fallback = lockedDates
        ? joinDeliveryWindow(lockedDates.selectedStart, lockedDates.selectedEnd)
        : this.#fallbackDeliveryWindow(rowStart, rowEnd);
      if (fallback) {
        liveRow.deliveryWindow = fallback;
        liveRow.hasDateError   = false;
      }
    });
  }

  /**
   * Set the date-picker input's visible label only. ISO dates stay on
   * `data-selected-start` / `data-selected-end` for save/filter/cart logic.
   * @param {HTMLInputElement|null|undefined} input
   * @param {string} [startISO]
   * @param {string} [endISO]
   */
  #setDatePickerDisplay(input, startISO, endISO) {
    if (!input) return;
    input.value = formatDeliveryWindowDisplay(startISO, endISO);
    this.#updateFutureSeasonPrice(input.closest('[data-b2b-plus-inventory-row]'), startISO);
  }

  /**
   * Paint the row's type price. Future / Prebook use the shipment-window start
   * (never today) to pick FW vs SS, and fall back to wholesale when that start
   * is still in today's season window. Immediate / ATS always show the wholesale
   * amount stamped on `data-wholesale-cents`. The element is plain text, not a
   * lit binding, so this write survives the cart-record re-render.
   * @param {HTMLElement|null|undefined} tr
   * @param {string} [startISO]
   */
  #updateFutureSeasonPrice(tr, startISO = '') {
    if (!tr) return;
    const priceEl = tr.querySelector('.b2b-plus-type-price');
    if (!priceEl) return;
    const wholesaleRaw = priceEl.dataset.wholesaleCents;
    const wholesale = wholesaleRaw != null && wholesaleRaw !== '' ? Number(wholesaleRaw) : NaN;
    const paintWholesale = () => {
      priceEl.textContent = Number.isFinite(wholesale) ? formatMoney(wholesale) : '';
    };
    // Immediate / ATS wholesale is static. The slot is plain text (no lit binding)
    // so date commits and the cart-record re-render can both write it safely.
    if (!usesShipmentStartSeasonPrice(tr.dataset.rowType)) {
      paintWholesale();
      return;
    }
    const picker = tr.querySelector('[data-delivery-date-picker]');
    const start = String(
      startISO
      || picker?.dataset.selectedStart
      || picker?.dataset.rowStart
      || tr.dataset.rowStart
      || '',
    ).trim();
    const fwRaw = priceEl.dataset.seasonFwCents;
    const ssRaw = priceEl.dataset.seasonSsCents;
    const cents = seasonalCentsForStartDate({
      fw: fwRaw != null && fwRaw !== '' ? Number(fwRaw) : null,
      ss: ssRaw != null && ssRaw !== '' ? Number(ssRaw) : null,
    }, start);
    if (cents != null && Number.isFinite(cents)) {
      priceEl.textContent = formatMoney(cents);
      return;
    }
    paintWholesale();
  }

  /**
   * Resolve the delivery window to show on a row when rendering.
   * Saved/live window wins; otherwise the shared pre-populated date for the
   * inventory type (independent of `prefillDeliveryDateWindow`).
   * @param {string} rowType
   * @param {string} [existingWindow]
   * @returns {string}
   */
  #resolveRowDeliveryWindow(rowType, existingWindow = '') {
    const existing = String(existingWindow ?? '').trim();
    if (existing) return existing;
    return getSharedShipmentDateForTypeSync(rowType) || '';
  }

  /**
   * Prefill a blank picker from the shared shipment date for its inventory type.
   * Works even when `prefillDeliveryDateWindow` is false.
   * Clamps the end into the row's allowed window; skips when the start is out of range.
   * @param {HTMLInputElement} input
   * @param {string} rowType
   * @param {Date|null} rowStart
   * @param {Date|null} rowEnd
   * @returns {boolean} True when a shared date was applied
   */
  #applySharedDateToPicker(input, rowType, rowStart, rowEnd) {
    if (!input || isLockedShipmentWindowEnabled(rowType)) return false;
    if (!isPrePopulatedShipmentDateEnabled(rowType)) return false;
    const parts = parseDeliveryWindowParts(getSharedShipmentDateForTypeSync(rowType));
    if (!parts) return false;
    const selStart = parseISODateLocal(parts.start);
    if (!selStart) return false;
    if ((rowStart && selStart < rowStart) || (rowEnd && selStart > rowEnd)) return false;
    const selEnd = parseISODateLocal(parts.end) || selStart;
    let effectiveEnd = rowEnd && selEnd > rowEnd ? rowEnd : selEnd;
    if (isAutoHorizonEndShipmentEnabled(rowType)) {
      effectiveEnd = atsImmediatePickerEnd(rowEnd, selStart) ?? effectiveEnd;
    }
    const endISO = toISODate(effectiveEnd);
    input.dataset.selectedStart = parts.start;
    input.dataset.selectedEnd   = endISO;
    this.#setDatePickerDisplay(input, parts.start, endISO);

    // Keep liveState / ATC in sync so a shared prefill (with prefillDeliveryDateWindow
    // false) is treated as a real selection until the buyer changes it.
    const tr = input.closest('[data-b2b-plus-inventory-row]');
    const liveRow = this.#currentLiveState?.combos[tr?.dataset.combinationKey]?.rows[tr?.dataset.rowId];
    if (liveRow) {
      liveRow.deliveryWindow = joinDeliveryWindow(parts.start, endISO);
      liveRow.hasDateError   = false;
    }
    return true;
  }

  /**
   * True when any same-type shipment window (inactive option tabs in
   * `#liveStates`, or other pickers in the current DOM) differs from
   * `nextWindow`. The row being edited is ignored because `commitRange`
   * already wrote `nextWindow` into its liveState before this runs.
   * @param {string} orderTypeGroup
   * @param {string} nextWindow
   * @param {HTMLInputElement|null} excludeInput
   * @returns {boolean}
   */
  #hasSiblingShipmentWindowDiff(orderTypeGroup, nextWindow, excludeInput = null) {
    const g = normalizeOrderTypeGroup(orderTypeGroup);
    const next = String(nextWindow ?? '').trim();
    if (g === 'unknown' || !next) return false;

    for (const [, tab] of this.#liveStates) {
      for (const combo of Object.values(tab?.combos ?? {})) {
        if (normalizeOrderTypeGroup(combo?.type) !== g) continue;
        for (const row of Object.values(combo?.rows ?? {})) {
          const win = String(row?.deliveryWindow ?? '').trim();
          if (win && win !== next) return true;
        }
      }
    }

    const inv = this.#inventoryTbody();
    for (const picker of inv?.querySelectorAll('[data-delivery-date-picker]') ?? []) {
      if (excludeInput && picker === excludeInput) continue;
      const tr = picker.closest('[data-b2b-plus-inventory-row]');
      if (normalizeOrderTypeGroup(tr?.dataset.rowType ?? '') !== g) continue;
      const start = (picker.dataset.selectedStart ?? '').trim();
      if (!start) continue;
      const end = (picker.dataset.selectedEnd ?? start).trim();
      const win = joinDeliveryWindow(start, end);
      if (win && win !== next) return true;
    }
    return false;
  }

  /**
   * After a user commits a shipment date: persist the shared default for that
   * inventory type; when changing an existing shared date, warn and amend all
   * previously dated rows of the same type (IDB + open tables + inactive tabs).
   * @param {HTMLElement|null} tr
   * @param {HTMLInputElement} input
   * @param {string} startISO
   * @param {string} endISO
   * @returns {Promise<void>}
   */
  async #syncSharedShipmentDateAfterCommit(tr, input, startISO, endISO) {
    const rowType = tr?.dataset.rowType ?? '';
    if (isLockedShipmentWindowEnabled(rowType)) return;
    if (!isPrePopulatedShipmentDateEnabled(rowType)) return;

    const group = normalizeOrderTypeGroup(rowType);
    if (group === 'unknown') return;

    const nextWindow = joinDeliveryWindow(startISO, endISO);
    if (!nextWindow) return;

    // Prefill/shared UI dates often never write the shared store until the first
    // explicit Flatpickr commit. Detect amend from sibling liveState/DOM windows
    // as well so changing a date on another option tab still updates every
    // same-type variant (and the order summary).
    const prevShared = String(getSharedShipmentDateForTypeSync(rowType) ?? '').trim();
    const isAmend = Boolean(
      (prevShared && prevShared !== nextWindow)
      || this.#hasSiblingShipmentWindowDiff(group, nextWindow, input),
    );

    try {
      await setSharedShipmentDate(group, nextWindow);
    } catch (err) {
      console.error('b2b-plus: failed to persist shared shipment date', err);
    }

    // Update this table's DOM + inactive `#liveStates` tabs first so any
    // concurrent auto-save snapshot already includes the shared/amended dates.
    this.#sharedDateSyncing = true;
    try {
      this.applyDeliveryDate(startISO, endISO, 'all', {
        orderTypeGroup: group,
        excludeInput: input,
        // Amend: push to every same-type row with qty or an existing date.
        // First seed: only fill still-blank pickers/rows.
        ...(isAmend ? {} : { onlyBlank: true }),
      });
    } finally {
      this.#sharedDateSyncing = false;
    }

    // Summary reads `#liveStates` on PDP — always refresh after the amend/seed
    // pass so inactive option tabs are reflected even when the DOM loop no-ops.
    this.#renderOrderSummary();

    // First selection for this type: local seed only (other products pick it up on init).
    if (!isAmend) return;

    toastNotification(
      b2bPlusString('sharedShipmentDateAmended')
        || 'Previously selected products will also have their ship date amended.',
      'warning',
    );

    // Let this table's pending save finish before the cart-wide IDB amend so a
    // stale DOM snapshot cannot overwrite amended sibling windows.
    try { await this.flushPendingSave(); }
    catch { /* ignore */ }

    try {
      await updateDeliveryWindowsForOrderTypeGroup(group, startISO, endISO, {
        onlyIfHasDate: true,
      });
    } catch (err) {
      console.error('b2b-plus: failed to amend shipment dates by inventory type', err);
    }

    // Notify cart (and any other mounted tables) so open UIs stay in sync.
    this.dispatchEvent(new CustomEvent('b2b:shared-shipment-date-changed', {
      bubbles: true,
      composed: true,
      detail: { group, start: startISO, end: endISO, window: nextWindow },
    }));
  }

  /**
   * Auto-fill the horizon window and make the picker non-interactive.
   * @param {HTMLInputElement} input
   */
  #applyLockedShipmentWindow(input) {
    const dates = this.#lockedPrefillDeliveryDates(
      input.dataset.rowStart ?? '',
      input.dataset.rowEnd ?? '',
    );
    if (dates) {
      input.dataset.selectedStart = dates.selectedStart;
      input.dataset.selectedEnd   = dates.selectedEnd;
      this.#setDatePickerDisplay(input, dates.selectedStart, dates.selectedEnd);
      const tr = input.closest('[data-b2b-plus-inventory-row]');
      const liveRow = this.#currentLiveState?.combos[tr?.dataset.combinationKey]?.rows[tr?.dataset.rowId];
      if (liveRow) {
        liveRow.deliveryWindow = joinDeliveryWindow(dates.selectedStart, dates.selectedEnd);
        liveRow.hasDateError   = false;
      }
    }
    this.#lockDatePickerInput(input);
  }

  /**
   * Keep the existing date-picker look; block focus / click from opening Flatpickr.
   * @param {HTMLInputElement} input
   */
  #lockDatePickerInput(input) {
    input.classList.add('b2b-plus-date-picker--locked');
    input.setAttribute('aria-disabled', 'true');
    input.tabIndex = -1;
  }

  /**
   * Undo {@link #lockDatePickerInput} for ONE row.
   *
   * Used only by `#flagPastCartDeliveryDates`: a locked row's window is machine-chosen
   * (today → today + horizon) and normally needs no picker, but a saved order whose
   * stored date has gone by is exactly the case where the buyer must be able to choose
   * again. Without this the row would show "date is in the past" over a picker that
   * cannot be opened — an error with no way out.
   *
   * `--locked` carries `pointer-events: none`, so dropping the class is what actually
   * restores interactivity; the aria/tabindex removal restores keyboard reach.
   * @param {HTMLInputElement} input
   */
  #unlockDatePickerInput(input) {
    input.classList.remove('b2b-plus-date-picker--locked');
    input.removeAttribute('aria-disabled');
    input.removeAttribute('tabindex');
  }

  /**
   * Initialise Flatpickr on a single `[data-delivery-date-picker]` input.
   * Extracted from #initDatePickers so it can be called for dynamically added rows.
   * @param {HTMLInputElement} input
   */
  #initDatePickerForInput(input) {
    if (!input) return;
    const tr = input.closest('[data-b2b-plus-inventory-row]');
    const rowType = tr?.dataset.rowType ?? '';
    // `#flagPastCartDeliveryDates` re-enters here for a locked row whose stored window
    // had gone by (or was blanked by an older version of that pass). That row must stay
    // unlocked and keep what it shows — the flagged past date, or blank: re-applying the
    // locked horizon (or any prefill / shared date) would silently answer the question
    // the buyer has just been asked to answer, which is the bug this marker exists for.
    const pastDateRepick = input.dataset.pastDateRepick === '1';
    if (!pastDateRepick && isLockedShipmentWindowEnabled(rowType)) {
      this.#applyLockedShipmentWindow(input);
      this.#updateFutureSeasonPrice(tr, input.dataset.selectedStart || input.dataset.rowStart || '');
      return;
    }
    const rowStart = parseISODateLocal(input.dataset.rowStart);
    if (!rowStart) {
      this.#updateFutureSeasonPrice(tr, input.dataset.selectedStart || input.dataset.rowStart || '');
      return;
    }
    // rowEnd is null when the row has no end date (infinite window).
    const rowEnd = input.dataset.rowEnd ? parseISODateLocal(input.dataset.rowEnd) : null;

    const cfg = b2bConfig();
    const { minDays, maxDays: defaultDays } = getShipWindowLengths(this.inventoryData);
    const singleDayMode = isSingleDayShipWindow(this.inventoryData);
    const autoHorizonEnd = isAutoHorizonEndShipmentEnabled(rowType);
    const startOnlyMode = autoHorizonEnd || singleDayMode;

    // ── Default date pre-selection (no flatpickr instance needed yet) ──
    // Write the default window into the dataset and display it in the
    // input so users see the expected delivery range immediately.
    // dataset.selectedStart/End are the source of truth for filters and
    // cart submission — the flatpickr calendar is only the picker UI.
    // #validateRestoredData runs after this and overwrites rows with saved dates.
    // Priority: shared shipment date (per inventory type; works when
    // prefillDeliveryDateWindow is false) → config.prefillDeliveryDateWindow default.
    // Skip if the template already set a saved / shared date.
    if (!input.dataset.selectedStart && !pastDateRepick) {
      const tr = input.closest('[data-b2b-plus-inventory-row]');
      const rowType = tr?.dataset.rowType ?? '';
      const sharedApplied = this.#applySharedDateToPicker(input, rowType, rowStart, rowEnd);
      if (!sharedApplied) {
        const prefill = this.#defaultPrefillDeliveryDates(input.dataset.rowStart ?? '', input.dataset.rowEnd ?? '');
        if (prefill) {
          input.dataset.selectedStart = prefill.selectedStart;
          input.dataset.selectedEnd   = prefill.selectedEnd;
          this.#setDatePickerDisplay(input, prefill.selectedStart, prefill.selectedEnd);
        }
      }
    } else {
      // Template may already show a shared date while liveState is still blank
      // (prefillDeliveryDateWindow false). Mirror it into liveState for ATC/summary.
      const tr = input.closest('[data-b2b-plus-inventory-row]');
      const liveRow = this.#currentLiveState?.combos[tr?.dataset.combinationKey]?.rows[tr?.dataset.rowId];
      if (liveRow && !String(liveRow.deliveryWindow ?? '').trim()) {
        const start = (input.dataset.selectedStart ?? '').trim();
        const end   = (input.dataset.selectedEnd ?? start).trim();
        if (start) {
          liveRow.deliveryWindow = joinDeliveryWindow(start, end);
          liveRow.hasDateError   = false;
        }
      }
    }

    // ── Lazy flatpickr initialization ───────────────────────────────────
    // Each flatpickr instance costs ~50–100 KB and 1–5 ms to initialise.
    // For 300-row tables this was 15–30 MB + up to 1.5 s before first paint.
    // Create the instance only when the user first focuses an input so
    // the cost is spread across actual interactions, not page load.
    const onFirstFocus = () => {
      let settling = false;

      const commitRange = (selStart, selEnd) => {
        fp.input.dataset.selectedStart = toISODate(selStart);
        fp.input.dataset.selectedEnd   = toISODate(selEnd);
        this.#setDatePickerDisplay(
          fp.input,
          fp.input.dataset.selectedStart,
          fp.input.dataset.selectedEnd,
        );
        this.#userHasInteracted = true;
        this.#updateButtonState?.();
        this.#triggerSave({ debounce: false });
        const tr      = input.closest('[data-b2b-plus-inventory-row]');
        const liveRow = this.#currentLiveState?.combos[tr?.dataset.combinationKey]?.rows[tr?.dataset.rowId];
        if (liveRow) {
          const isoStart = fp.input.dataset.selectedStart;
          const isoEnd   = fp.input.dataset.selectedEnd;
          liveRow.deliveryWindow = joinDeliveryWindow(isoStart, isoEnd);
          liveRow.hasDateError   = false;
        }
        // flatpickr's commit does not reliably emit a bubbling `change` event to
        // the shadow-root sync listener, so refresh the summary directly here.
        // No-ops when the order summary is disabled (root element absent).
        this.#renderOrderSummary();
        if (!this.#sharedDateSyncing) {
          void this.#syncSharedShipmentDateAfterCommit(
            tr,
            fp.input,
            fp.input.dataset.selectedStart,
            fp.input.dataset.selectedEnd,
          );
        }
      };

      const revertPicker = () => {
        const prevStart = input.dataset.selectedStart;
        const prevEnd   = input.dataset.selectedEnd;
        settling = true;
        if (prevStart && prevEnd) {
          if (startOnlyMode) {
            fp.setDate(prevStart, false);
          } else {
            fp.setDate([prevStart, prevEnd], false);
          }
          this.#setDatePickerDisplay(input, prevStart, prevEnd);
        } else {
          fp.clear();
          delete input.dataset.selectedStart;
          delete input.dataset.selectedEnd;
          input.value = '';
          const tr      = input.closest('[data-b2b-plus-inventory-row]');
          const liveRow = this.#currentLiveState?.combos[tr?.dataset.combinationKey]?.rows[tr?.dataset.rowId];
          if (liveRow) {
            liveRow.deliveryWindow = '';
            liveRow.hasDateError   = false;
          }
          this.#updateFutureSeasonPrice(tr, input.dataset.rowStart || '');
        }
        settling = false;
        // Keep the summary in sync when a date is reverted/cleared.
        this.#renderOrderSummary();
      };

      const isEndInShipWindow = (selStart, selEnd) => {
        const endDay = startOfLocalDay(selEnd);
        const { minEnd, maxEnd } = shipWindowEndBounds(selStart, minDays, defaultDays, rowEnd);
        return endDay >= minEnd && endDay <= maxEnd;
      };

      const shipWindowAlertMessage = () => {
        const base = b2bPlusString('dateOutOfRangeError') || 'Date not available for this row.';
        if (minDays === defaultDays) {
          return `${base} (${minDays} day${minDays === 1 ? '' : 's'} required.)`;
        }
        return `${base} Select between ${minDays} and ${defaultDays} days.`;
      };

      const fpConfig = {
        mode: startOnlyMode ? 'single' : 'range',
        minDate: rowStart < startOfLocalDay(new Date()) ? startOfLocalDay(new Date()) : rowStart,
        maxDate: rowEnd ?? undefined,
        // Display-only date rendering ("May 20" / "May 20, 2027"); the canonical
        // ISO window stays in dataset.selectedStart/End.
        ...displayDateFlatpickrOptions(),
        disableMobile: true,
        onChange: (selectedDates) => {
          input.classList.remove('b2b-plus-date-picker--error');
          const dateErrorEl = input.parentElement?.querySelector('[data-date-error]');
          if (dateErrorEl) dateErrorEl.hidden = true;
          if (settling) return;

          if (startOnlyMode) {
            if (selectedDates.length !== 1) return;
            const day = selectedDates[0];
            const end = autoHorizonEnd
              ? (atsImmediatePickerEnd(rowEnd, day) ?? day)
              : day;
            commitRange(day, end);
            return;
          }

          if (selectedDates.length === 1) {
            input.dataset.selectedStart = toISODate(selectedDates[0]);
            delete input.dataset.selectedEnd;
            this.#setDatePickerDisplay(input, input.dataset.selectedStart, input.dataset.selectedStart);
            return;
          }
          if (selectedDates.length === 2) {
            const selStart = selectedDates[0];
            const selEnd   = selectedDates[1];
            if (!isEndInShipWindow(selStart, selEnd)) {
              window.alert(shipWindowAlertMessage());
              revertPicker();
              return;
            }
            commitRange(selStart, selEnd);
          }
        },
        // When minimum is 1 but max > 1: closing after one click commits a single-day delivery.
        // Auto-horizon Future/Prebook already commit on the first click (start + N).
        onClose: (selectedDates) => {
          if (autoHorizonEnd) {
            const s = input.dataset.selectedStart;
            const e = input.dataset.selectedEnd;
            if (s) this.#setDatePickerDisplay(input, s, e || s);
          }
          if (startOnlyMode || minDays !== 1 || settling) return;
          if (selectedDates.length !== 1) return;
          const day = selectedDates[0];
          commitRange(day, day);
        },
      };

      const fp = flatpickr(input, fpConfig);

      const savedStart = input.dataset.selectedStart;
      const savedEnd   = input.dataset.selectedEnd;
      // A saved start that has gone by sits below `minDate`, and flatpickr silently drops
      // it — handing it over would rewrite the field. Leave the field showing the saved
      // (flagged) date; picking a new one replaces it as usual.
      if (savedStart && savedEnd && !isDeliveryWindowPast(savedStart)) {
        if (singleDayMode) {
          fp.setDate(savedStart, false);
        } else {
          fp.setDate([savedStart, savedEnd], false);
        }
        this.#setDatePickerDisplay(input, savedStart, savedEnd);
      }

      this.#flatpickrInstances.set(input, fp);
      fp.open();
    };

    // Track in this.#listeners so disconnectedCallback cleans up the lazy
    // listener for inputs that were never focused. Inputs that WERE focused
    // self-remove via { once: true } — removeEventListener is a no-op then.
    input.addEventListener('focus', onFirstFocus, { once: true });
    this.#listeners.push({ target: input, type: 'focus', handler: onFirstFocus });
    this.#updateFutureSeasonPrice(tr, input.dataset.selectedStart || input.dataset.rowStart || '');
  }

  /**
   * Wire up the "Add note" toggle button and note input for each row.
   */
  #initNoteToggles() {
    this.#inventoryTbody()?.querySelectorAll('[data-b2b-plus-inventory-row][data-row-id]').forEach((tr) => this.#wireNoteToggleForRow(tr));
  }

  /**
   * Wire note-toggle + textarea listeners for a single row `<tr>`.
   * Called during bulk init and when a new shipment row is added dynamically.
   * @param {HTMLElement} tr
   */
  #wireNoteToggleForRow(tr) {
    const btn = tr.querySelector('[data-note-toggle]');
    const textarea = tr.querySelector('[data-row-note]');

    if (btn && textarea) {
      btn.textContent = textarea.value.trim() ? b2bPlusString('editNote') : b2bPlusString('addNote');
      this.#on(btn, 'click', () => {
        textarea.hidden = !textarea.hidden;
        if (!textarea.hidden) textarea.focus();
      });
    }

    if (textarea) {
      this.#on(textarea, 'input', () => {
        this.#userHasInteracted = true;
        this.#updateButtonState?.();
        this.#triggerSave({ debounce: false });
        if (btn) btn.textContent = textarea.value.trim() ? b2bPlusString('editNote') : b2bPlusString('addNote');
        const liveRow = this.#currentLiveState?.combos[tr.dataset.combinationKey]?.rows[tr.dataset.rowId];
        if (liveRow) liveRow.note = textarea.value.trim();
      });
    }
  }

  /**
   * Attach input validation to every quantity input that has a `data-max-qty`.
   * Reads maxQty dynamically so sibling-cap updates (data-max-qty mutations) apply.
   */
  #initQtyInputs() {
    const minCheckoutErr = this.#shadow.querySelector('[data-checkout-min-qty-error]');
    this.#shadow.querySelectorAll('[data-qty-input]').forEach((input) => {
      this.#on(input, 'change', () => {
        if (input.value === '') input.value = 0;
        this.#writeInputQtyToLiveState(input);
      });
      if (minCheckoutErr) {
        this.#on(input, 'input', () => {
          minCheckoutErr.hidden = true;
          minCheckoutErr.textContent = '';
        });
      }

      // Numeric-only enforcement (Safari-safe). Shared with dynamically added
      // shipment rows via #wireRowBehavior so every qty input is hardened.
      this.#applyNumericHardening(input);

      // Mirror live-state qtys on every keystroke (after the sanitiser above has
      // stripped non-digits and leading zeros) so the order summary reflects in-progress typing.
      this.#on(input, 'input', () => {
        // Clamp to `data-max-qty` inside `#writeInputQtyToLiveState`. The
        // validation listener runs after this one and only mutates `input.value`,
        // so the summary (which reads live state) must apply the same cap here.
        this.#writeInputQtyToLiveState(input);
      });

      // On blur, clear the (now-clamped) max error, then enforce the catalog
      // min rule — which must persist while the entered qty is below the floor.
      this.#on(input, 'blur', () => {
        const errorEl = input.parentElement?.querySelector('[data-qty-error]');
        input.classList.remove('b2b-plus-quantity-input--error');
        if (errorEl) errorEl.hidden = true;
        this.#validateQtyRuleMin(input, errorEl);
      });
    });

    this.#inventoryTbody()?.querySelectorAll('[data-qty-input][data-max-qty]').forEach((input) => {
      const tr      = input.closest('[data-b2b-plus-inventory-row]');
      const errorEl = input.parentElement?.querySelector('[data-qty-error]');

      this.#on(input, 'input', () => {
        this.#validateQtyInput(input, errorEl);
        const comboKey = tr?.dataset.combinationKey;
        if (comboKey) this.#syncCapsAfterChange(comboKey);
      });
    });

    // Pooled inventory — apply the pooled per-window caps at first paint. The
    // template writes each window's own stock as `data-max-qty`; pooling can
    // raise the orderable ceiling (a later window drawing on earlier stock), so
    // reconcile every rendered combo against the freshly-recomputed pooled caps.
    if (this.#poolingEnabled()) {
      this.#recomputePooledCaps();
      for (const ck of new Set(
        [...(this.#inventoryTbody()?.querySelectorAll('[data-b2b-plus-inventory-row][data-combination-key]') ?? [])]
          .map((tr) => tr.dataset.combinationKey).filter(Boolean),
      )) {
        this.#updateSiblingCaps(ck, { clearErrorsWhenValid: true });
      }
      this.#updatePooledAvailabilityDisplay();
    }
  }

  /**
   * Clamp a qty input to its `data-max-qty` cap and show/hide the inline error.
   * No-op when the input has no `data-max-qty` attribute (uncapped prebook rows).
   * @param {HTMLInputElement} input
   * @param {Element|null} errorEl
   */
  /**
   * Numeric-only enforcement for a quantity input (Safari-safe). Chrome/Firefox
   * enforce type="number" natively, but Safari (desktop + iOS) lets non-numeric
   * characters through, so three layers are needed. Shared by `#initQtyInputs`
   * (init rows) and `#wireRowBehavior` (dynamically added shipment rows) so the
   * hardening can never drift between the two paths.
   *   1. keydown — block single non-digit keystrokes before they appear (multi-char
   *      keys like Backspace/Arrow/Tab and Ctrl/Cmd combos stay functional). Also
   *      blocks a leading 0 in front of another digit (`01`, `001`) while still
   *      allowing a lone `0`.
   *   2. beforeinput — intercept paste / autocorrect / drag-drop / voice input.
   *   3. input sanitiser — strip any non-digit that still slips through (IME) and
   *      collapse leading zeros (`01` → `1`, `000` → `0`).
   * @param {HTMLInputElement} input
   */
  #applyNumericHardening(input) {
    const stripLeadingZeros = (raw) => String(raw ?? '').replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '');

    this.#on(input, 'keydown', (e) => {
      if (e.key.length > 1 || e.ctrlKey || e.metaKey) return;
      if (!/[0-9]/.test(e.key)) {
        e.preventDefault();
        return;
      }
      const current = String(input.value ?? '');
      // Field is empty or only zeros: extra zeros would become 00 / 000.
      if (e.key === '0' && /^0*$/.test(current)) {
        if (current !== '') e.preventDefault();
        return;
      }
      // Typing 1–9 over a lone 0 should replace it (01 → 1), not prefix.
      if (/[1-9]/.test(e.key) && /^0+$/.test(current)) {
        e.preventDefault();
        input.value = e.key;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
    this.#on(input, 'beforeinput', (e) => {
      if (e.data != null && !/^\d*$/.test(e.data)) e.preventDefault();
    });
    // A cleared field snaps back to 0 right away. Runs before the other `input`
    // listeners, so they all read 0; the keydown pass above replaces this lone 0
    // with the next digit typed.
    this.#on(input, 'input', () => {
      const raw = input.value;
      const normalized = stripLeadingZeros(raw) || '0';
      if (normalized !== raw) input.value = normalized;
    });
  }

  #validateQtyInput(input, errorEl) {
    if (!('maxQty' in input.dataset)) return;
    const maxQty = Number(input.dataset.maxQty);
    if (Number(input.value) > maxQty) {
      input.value = maxQty;
      input.classList.add('b2b-plus-quantity-input--error');
      if (errorEl) { errorEl.textContent = b2bPlusString('qtyExceededError').replace('{max}', maxQty); errorEl.hidden = false; }
    } else {
      input.classList.remove('b2b-plus-quantity-input--error');
      if (errorEl) errorEl.hidden = true;
    }
  }

  /**
   * Cart-only: flag every stored delivery window whose ship date has already passed,
   * and tell the buyer why.
   *
   * A working order can sit for days before it is reopened, so its dates rot: the
   * row still carries "2026-02-10 to 2026-02-16" long after that week is gone. The
   * row's own calendar is configured with `minDate: today`, so that date can never be
   * ordered as-is.
   *
   * The date is deliberately LEFT IN PLACE — record, storage and working order are
   * untouched, and the field shows exactly what the buyer had picked — but the field is
   * put in error with `deliveryDatePastError`. `validate()` (rendered tables) and the
   * cart's storage-wide checkout pass (`#validateAllCartRecords`) refuse the order until
   * a new date is picked. This used to blank the date instead, which hid what the buyer
   * had chosen and read as data loss.
   *
   * Locked rows (today → today + horizon, machine-chosen) render the horizon over the
   * stored window, so a passed stored window is written back into the picker here and
   * the picker is unlocked — otherwise the buyer is told to re-pick a date they cannot
   * pick. `dataset.pastDateRepick` tells `#initDatePickerForInput` not to re-lock it.
   *
   * Immediate / ATS locked rows (`isPastDateAutoAdjustType`) are the exception: the
   * buyer never picks that date, so a passed window is MOVED to the current locked
   * window (normally already done by `rollLockedShipmentWindowsToToday`; this is the
   * fallback for a record that turned up past after that ran, e.g. a mid-session
   * hydrate) and the row gets an info note — not an error — naming the old date.
   *
   * A LOCKED row with no stored window at all is one an earlier version of this pass
   * blanked. It is never legitimately blank (`addToCart` always stores one), so it is
   * shown as date-required — without this the row would silently heal to today's
   * window on reload.
   *
   * Cart-only because `#validateRestoredData` returns early on the cart.
   *
   * @returns {boolean} true when at least one window was flagged
   */
  #flagPastCartDeliveryDates() {
    if (!this.isCart) return false;
    const record = this.cartRecord;
    if (!record || !Object.keys(record).length) return false;

    const today = startOfLocalDay(new Date());

    // Rendered rows indexed by their RESOLVED record combo, so a legacy storage key
    // still finds its row.
    const trsByCombo = new Map();
    for (const tr of this.#inventoryTbody()?.querySelectorAll(
      '[data-b2b-plus-inventory-row][data-row-id][data-combination-key]',
    ) ?? []) {
      const combo = resolveCartComboForDomKey(record, tr.dataset.combinationKey)?.combo;
      if (!combo) continue;
      if (!trsByCombo.has(combo)) trsByCombo.set(combo, new Map());
      trsByCombo.get(combo).set(tr.dataset.rowId, tr);
    }

    const todayISO = toISODate(today);
    let recordChanged = false;
    let flagged       = false;
    let adjustedShown = false;
    for (const combo of Object.values(record)) {
      const locked = isLockedShipmentWindowEnabled(combo?.type ?? '');
      const autoAdjustPast = isPastDateAutoAdjustType(combo?.type ?? '');

      for (const row of Array.isArray(combo?.rows) ? combo.rows : []) {
        const stored = String(row?.deliveryWindow ?? '').trim();
        const isPast = isDeliveryWindowPast(stored, today);
        const needsPick = locked && !stored;

        const tr     = trsByCombo.get(combo)?.get(row.rowId) ?? null;
        const picker = tr?.querySelector('[data-delivery-date-picker]') ?? null;

        if (isPast && autoAdjustPast) {
          const next = lockedHorizonWindowForCombo(combo);
          if (next) {
            row.deliveryWindow = next.window;
            row[PAST_DATE_ADJUSTED_KEY] = { from: stored, on: todayISO };
            recordChanged = true;
            if (picker) {
              picker.dataset.selectedStart = next.start;
              picker.dataset.selectedEnd   = next.end;
              this.#setDatePickerDisplay(picker, next.start, next.end);
            }
          }
        }
        const adjusted = row?.[PAST_DATE_ADJUSTED_KEY];
        if (picker && adjusted?.on === todayISO && autoAdjustPast) {
          this.#showPastDeliveryDateAdjustedInfo(picker, adjusted.from);
          adjustedShown = true;
        }
        if (autoAdjustPast && isPast && row.deliveryWindow !== stored) continue;
        if (!isPast && !needsPick) continue;

        // Unlocked rows only: the row renders the stored window into the picker, so the two
        // normally agree. When they don't, the picker already holds a valid replacement —
        // `#initDatePickers` ran first and prefilled the default window into a row the
        // template left blank. Adopt it rather than flagging a date no longer on screen.
        if (!locked) {
          const pickerWindow = joinDeliveryWindow(picker?.dataset.selectedStart, picker?.dataset.selectedEnd);
          if (pickerWindow && pickerWindow !== stored && !isDeliveryWindowPast(pickerWindow, today)) {
            row.deliveryWindow = pickerWindow;
            recordChanged = true;
            continue;
          }
        }
        flagged = true;
        if (!picker) continue;

        const parts = isPast ? parseDeliveryWindowParts(stored) : null;
        if (parts) {
          // Keep (re-assert) the saved window: a locked row's template put the horizon here.
          picker.dataset.selectedStart = parts.start;
          picker.dataset.selectedEnd   = parts.end;
          this.#setDatePickerDisplay(picker, parts.start, parts.end);
        } else {
          this.#flatpickrInstances?.get(picker)?.clear(false);
          picker.value = '';
          delete picker.dataset.selectedStart;
          delete picker.dataset.selectedEnd;
        }
        if (locked) {
          // A locked picker is `pointer-events: none` AND never had a flatpickr focus
          // handler bound (`#initDatePickerForInput` returns early for locked rows), so
          // both have to be undone or the buyer cannot pick a replacement date.
          picker.dataset.pastDateRepick = '1';
          this.#unlockDatePickerInput(picker);
          this.#flatpickrInstances?.delete(picker);
          this.#initDatePickerForInput(picker);
        }
        if (parts) {
          this.#showPastDeliveryDateError(picker);
        } else {
          picker.classList.add('b2b-plus-date-picker--error');
          const errEl = tr?.querySelector('[data-date-error]');
          if (errEl) { errEl.textContent = b2bPlusString('dateRequiredError'); errEl.hidden = false; }
        }
      }
    }

    // The summary view (cart default) hides the inventory matrix where these row
    // errors (and the moved-date notes) live, so flip to edit view — otherwise the
    // explanation is invisible.
    if ((flagged || adjustedShown) && this.#cartViewMode === 'summary' && b2bConfig().enableOrderSummary !== false) {
      this.#setCartViewMode('edit');
    }

    if (recordChanged) {
      this.cartData = flattenProductRecordToLineItems(this.cartRecord);
      const handle  = this.#storageHandle;
      if (handle) {
        this.#pendingSavePromise = saveProductRecord(handle, this.cartRecord, { productId: this.productJSON?.id })
          .then(() => this.dispatchEvent(new CustomEvent('b2b:cart-saved', { bubbles: true })))
          .catch((err) => console.error('b2b-plus: failed to save adopted delivery dates', err));
      }
    }
    return flagged;
  }

  /**
   * Info (not an error) under an Immediate / ATS date field whose saved window had
   * passed and was moved to the current locked window. `from` is the old window.
   * @param {HTMLInputElement} picker
   * @param {string} from
   */
  #showPastDeliveryDateAdjustedInfo(picker, from) {
    const infoEl = picker.closest('[data-b2b-plus-inventory-row]')?.querySelector('[data-date-info]')
      ?? picker.parentElement?.querySelector('[data-date-info]');
    if (!infoEl) return;
    const tmpl = b2bPlusString('deliveryDatePastAdjustedInfo')
      || 'The previous delivery date ({date}) has passed — updated to the next available window.';
    infoEl.textContent = tmpl.replace('{date}', formatDisplayWindowString(from) || from);
    infoEl.hidden = false;
  }

  /**
   * Put a delivery-date field whose selected date has passed in error, with the
   * past-date message under it. The date itself is left as it is.
   * @param {HTMLInputElement} picker
   */
  #showPastDeliveryDateError(picker) {
    picker.classList.add('b2b-plus-date-picker--error');
    const errEl = picker.closest('[data-b2b-plus-inventory-row]')?.querySelector('[data-date-error]')
      ?? picker.parentElement?.querySelector('[data-date-error]');
    if (errEl) {
      errEl.textContent = b2bPlusString('deliveryDatePastError');
      errEl.hidden = false;
    }
  }

  /**
   * Cart-only initial-render clamp: bring every rendered qty input whose saved
   * value exceeds its `data-max-qty` cap down to the cap, and surface the standard
   * "exceeds available" inline error. Mirrors `#validateQtyInput` (which only runs
   * on user `input`), so a saved order loaded with stale-high quantities shows the
   * max-available value with an error instead of leaving the line at the raw value
   * or collapsing the total to 0.
   *
   * Disabled / uncapped (prebook) inputs are left untouched. When at least one
   * input is clamped, the corrected values are persisted to IndexedDB via the
   * auto-save path so the clamp survives reloads and feeds the correct quantity
   * into checkout.
   *
   * @returns {boolean} true when at least one input was clamped
   */
  #clampCartQtysToMax() {
    if (!this.isCart) return false;
    let clamped = false;
    this.#inventoryTbody()
      ?.querySelectorAll('[data-qty-input][data-max-qty]')
      .forEach((input) => {
        if (input.disabled) return;
        const maxQty = Number(input.dataset.maxQty);
        if (!Number.isFinite(maxQty)) return;
        if (Number(input.value) <= maxQty) return;
        input.value = maxQty;
        input.classList.add('b2b-plus-quantity-input--error');
        const errorEl = input.parentElement?.querySelector('[data-qty-error]');
        if (errorEl) {
          errorEl.textContent = b2bPlusString('qtyExceededError').replace('{max}', String(maxQty));
          errorEl.hidden = false;
        }
        clamped = true;
      });
    // Persist the corrected quantities so IndexedDB (and thus checkout) matches
    // the clamped display. No-op when nothing was clamped or auto-save is off.
    if (clamped) this.#onAutoSave?.();
    return clamped;
  }

  /**
   * Cart-only: reconcile multi-shipment rows that share a combination against
   * the SUMMED API cap. `#clampCartQtysToMax` only checks each input against its
   * own `data-max-qty`, so it can't catch two shipment rows that are each within
   * cap but whose sum exceeds it (e.g. a saved order that split 60+60 of a
   * variant capped at 100 across two deliveries). This runs the same
   * running-total pass the live-keystroke handler uses — over every combo present
   * in the DOM — so the caps/quantities are correct on load and at checkout, not
   * only after a keystroke. Mirrors the PDP `#validateRestoredData` sibling-cap
   * rule, which the cart path skips (it returns early for `isCart`).
   *
   * Persists any correction (via auto-save) so the line totals and the checkout
   * payload use the in-stock quantities.
   * @returns {boolean} true when any quantity was corrected
   */
  #reconcileCartSiblingCaps() {
    if (!this.isCart) return false;
    const tbody = this.#inventoryTbody();
    if (!tbody) return false;
    // Refresh pooled caps from current demand first (no-op when pooling is off),
    // exactly as the PDP rule does, so each window reconciles against its
    // current pooled headroom.
    if (this.#poolingEnabled()) this.#recomputePooledCaps();
    let mutated = false;
    for (const ck of new Set(
      [...tbody.querySelectorAll('[data-b2b-plus-inventory-row][data-combination-key]')]
        .map((tr) => tr.dataset.combinationKey).filter(Boolean),
    )) {
      if (this.#updateSiblingCaps(ck)) mutated = true;
    }
    if (this.#poolingEnabled()) this.#updatePooledAvailabilityDisplay();
    if (mutated) this.#onAutoSave?.();
    return mutated;
  }

  /**
   * Enforce the catalog quantity-rule minimum (`data-min-qty`) on commit (blur /
   * checkout validation) — NOT on every keystroke, so a buyer typing up toward
   * the floor isn't flagged mid-entry. A qty of 0 (not ordering this variant) is
   * always allowed; any positive qty below the floor shows the min error.
   *
   * Does not auto-bump the value — clamping a min upward would silently
   * over-order, so we surface an error and let the buyer adjust.
   *
   * @param {HTMLInputElement} input
   * @param {Element|null} errorEl
   * @returns {boolean} true when the value satisfies the min rule (or has none)
   */
  #validateQtyRuleMin(input, errorEl) {
    if (!('minQty' in input.dataset)) return true;
    const minQty = Number(input.dataset.minQty);
    const qty    = Number(input.value) || 0;
    if (qty > 0 && qty < minQty) {
      input.classList.add('b2b-plus-quantity-input--error');
      if (errorEl) { errorEl.textContent = b2bPlusString('qtyBelowMinError').replace('{min}', String(minQty)); errorEl.hidden = false; }
      return false;
    }
    return true;
  }

  /**
   * Wire up every "Add to order" button under the shadow root.
   *
   * Multiple `[data-atc-btn]` elements can exist in the same shadow tree
   * (filter-bar copy + summary-bar copy when `enableOrderSummary` is on).
   * The summary-bar's button is also late-rendered — it only enters the DOM
   * on the first `#renderOrderSummary` that finds non-empty rows. Both facts
   * require shadow-root event delegation rather than per-button binding:
   *   - Click: delegated, finds the clicked `[data-atc-btn]` via event target.
   *   - Qty input: delegated, marks user interaction + refreshes button state.
   *   - `updateButtonState` queries every ATC button on each call so a newly
   *     appeared button is kept in sync without re-running init.
   *
   * Optional element attributes (Add / Update order only):
   *   - `data-toast-on-order` — if `"false"`, do not show the success toast after
   *     a successful save. **Default: enabled** (any other value or omitted = toast).
   *   - `data-close-modal-on-order` — if `"true"`, after a successful save, dispatch
   *     `b2b:order-committed` (bubbles) so a parent (e.g. quick view) can close a modal.
   */
  #initAtcButton() {
    const showOrderSuccessToast = this.dataset.toastOnOrder !== 'false';
    // Do not reset `#userHasInteracted` here. `#renderAndInit` re-runs this on
    // every option-tab switch; wiping the flag would disable Add to Order even
    // when another colour already has quantities in `#liveStates`. First paint
    // starts false from the field initializer; Clear / successful save still
    // set it false explicitly.

    // `showActionButton=false` opts out of the ATC entirely (e.g. on the cart
    // line tables where the cart footer owns checkout). Hide every current and
    // future button — `#renderOrderSummary` re-applies the hide after the
    // summary-bar repaints — and skip click/input wiring.
    if (!this.showActionButton) {
      this.#shadow.querySelectorAll('[data-atc-btn]').forEach((b) => { b.hidden = true; });
      return;
    }

    // Only the visible colour's table. A swatch with every qty at 0 must disable
    // ATC so we never submit a zero-qty add. Other colours' drafts stay in
    // `#liveStates` until the buyer switches back to a tab that has qty > 0.
    const hasQty = () => {
      const inv = this.#inventoryTbody();
      return [...(inv?.querySelectorAll('[data-qty-input]') ?? [])].some((i) => Number(i.value) > 0);
    };

    const updateButtonState = () => {
      const disabled = !this.#userHasInteracted || !hasQty();
      this.#shadow.querySelectorAll('[data-atc-btn]').forEach((b) => { b.disabled = disabled; });
    };
    this.#updateButtonState = updateButtonState;

    // Initial "Update Order" label for any `[data-atc-btn]` already in DOM.
    // The summary-bar's button (rendered later) picks the same label up from
    // `#orderSummaryOpts().atcButtonText`. Both track SAVED state across every
    // option tab (not live edits), so an existing order on another colour still
    // shows "Update Order" even when the visible tab is empty.
    // Filter-bar label is plain text (no lit binding). The summary-bar label is a
    // lit binding inside `[data-summary-bar-root]` and is painted by
    // `#renderOrderSummary` — writing textContent there strips the marker and
    // the next summary render throws.
    const atcLabel = this.#hasAnySavedOrder()
      ? b2bPlusString('updateOrder')
      : (b2bPlusString('addToOrder') || 'Add to Order');
    this.#setImperativeAtcLabel(atcLabel);

    // Shadow-delegated input handler — fires for init rows AND shipment rows
    // added later, so we don't have to re-bind per-input. `#wireRowBehavior`
    // calls `#updateButtonState` directly for its own reasons; this handler
    // covers init rows that don't go through `#wireRowBehavior`.
    this.#on(this.#shadow, 'input', (e) => {
      if (!e.target.closest('[data-qty-input]')) return;
      this.#userHasInteracted = true;
      updateButtonState();
    });

    // Select the whole value on focus/click so typing replaces it instead of
    // appending. `focusin` (not `focus`) bubbles, so a single shadow-delegated
    // listener covers init rows and shipment rows added later. The `click`
    // pass re-selects when focus was already in the field (a second click can
    // otherwise place a caret and collapse the selection).
    const selectQty = (e) => {
      const input = e.target.closest('[data-qty-input]');
      if (input) input.select();
    };
    this.#on(this.#shadow, 'focusin', selectQty);
    this.#on(this.#shadow, 'click', selectQty);

    // Shadow-delegated click handler. `closest('[data-atc-btn]')` lets us
    // bind once and still catch a button the summary bar adds later.
    this.#on(this.#shadow, 'click', async (e) => {
      const btn = e.target.closest('[data-atc-btn]');
      if (!btn || btn.disabled) return;

      const inv          = this.#inventoryTbody();
      const filledInputs = [...(inv?.querySelectorAll('[data-qty-input]') ?? [])].filter((i) => Number(i.value) > 0);
      // Never submit when the visible colour has no qty > 0.
      if (!filledInputs.length) return;

      let hasErrors = false;
      for (const input of filledInputs) {
        const qty    = Number(input.value);
        const maxQty = input.dataset.maxQty !== undefined ? Number(input.dataset.maxQty) : null;
        if (maxQty !== null && qty > maxQty) { hasErrors = true; continue; }

        // Catalog min-quantity rule: a positive qty below the floor blocks the add.
        const qtyError = input.parentElement?.querySelector('[data-qty-error]');
        if (!this.#validateQtyRuleMin(input, qtyError)) { hasErrors = true; continue; }

        const row           = input.closest('[data-b2b-plus-inventory-row]');
        const datePicker    = row?.querySelector('[data-delivery-date-picker]');
        const selectedStart = datePicker?.dataset.selectedStart;
        const selectedEnd   = datePicker?.dataset.selectedEnd;

        if (!selectedStart || !selectedEnd) {
          datePicker?.classList.add('b2b-plus-date-picker--error');
          const el = row?.querySelector('[data-date-error]');
          if (el) { el.textContent = b2bPlusString('dateRequiredError'); el.hidden = false; }
          hasErrors = true;
          continue;
        }
      }
      if (hasErrors) return;

      const isUpdate   = this.#hasAnySavedOrder();
      const spinner    = btn.querySelector('[data-loading-spinner]');
      const buttonText = btn.querySelector('[data-button-text]');

      btn.disabled = true;
      spinner?.removeAttribute('hidden');
      if (buttonText) buttonText.hidden = true;

      const handle = this.#storageHandle;
      try {
        // Commit EVERY option tab the user populated this session, each to its
        // own IDB key, so the saved order matches the cross-tab order summary.
        // On PDP/quickview tab switches no longer auto-save — the only place the
        // other colours' quantities live is `#liveStates` — so we serialise each
        // populated tab here. The active tab is included in this set.
        // (On cart, `showActionButton` is false so this handler never runs there;
        //  the `#liveStates.size` guard keeps the single-record fallback safe.)
        let record;
        // Every record committed by this click — used to detect a mix of inventory types.
        const committedRecords = [];
        if (!this.isCart && this.#liveStates.size) {
          let activeRecord = null;
          await Promise.all([...this.#liveStates.entries()].map(async ([optionValue, liveTab]) => {
            const rec = buildRecordForTab(liveTab);
            if (!hasExistingSavedCart(rec)) return; // skip tabs with no quantities
            const tabHandle = this.#storageHandleFor(optionValue);
            if (!tabHandle) return;
            await saveProductRecord(tabHandle, rec, { productId: this.productJSON?.id });
            this.#trackSavedOrder(optionValue, rec);
            committedRecords.push(rec);
            if (optionValue === this.#selectedOptionValue) activeRecord = rec;
          }));
          // Reflect the active tab's committed record locally (label + isUpdate).
          record = activeRecord ?? this.#buildCurrentRecord();
          if (!activeRecord) {
            await saveProductRecord(handle, record, { productId: this.productJSON?.id });
            committedRecords.push(record);
          }
        } else {
          record = this.#buildCurrentRecord();
          await saveProductRecord(handle, record, { productId: this.productJSON?.id });
          committedRecords.push(record);
        }
        this.#trackSavedOrder(this.#selectedOptionValue, record);
        this.cartRecord = record;
        this.cartData = flattenProductRecordToLineItems(this.cartRecord);

        if (showOrderSuccessToast) {
          // Mixed inventory types in one add: show the conflict notice first, then
          // stack the success toast above it (`prepend`).
          const hasMixedTypes = countCommittedInventoryTypes(committedRecords) > 1;
          if (hasMixedTypes) {
            toastNotification(b2bPlusString('inventoryTypeConflict'), 'error', 6000);
          }
          toastNotification(
            b2bPlusString(isUpdate ? 'orderSaveSuccess' : 'orderAddSuccess'),
            'success',
            { duration: 3000, prepend: hasMixedTypes },
          );
        }
        if (this.dataset.closeModalOnOrder === 'true') {
          this.dispatchEvent(new CustomEvent('b2b:order-committed', { bubbles: true }));
        }

        this.dispatchEvent(new CustomEvent('b2b:cart-saved', { bubbles: true }));
        this.#userHasInteracted = false;

        // Flip every ATC button's label to "Update Order" after a successful
        // save — both the clicked one and any sibling button (filter-bar +
        // summary-bar both exist in some configurations).
        this.#setImperativeAtcLabel(b2bPlusString('updateOrder'));
        if (b2bConfig().enableOrderSummary !== false) this.#renderOrderSummary();
      } catch (err) {
        console.error('b2b-plus-product-table: addToCart failed', err);
        // Surface the failure — the buyer must know their order was NOT saved
        // rather than seeing the spinner stop and assuming success.
        toastNotification(
          b2bPlusString('tabSwitchSaveFailed') || 'Could not save your order. Please try again.',
          'error',
        );
      } finally {
        spinner?.setAttribute('hidden', '');
        if (buttonText) buttonText.hidden = false;
        updateButtonState();
      }
    });

    // Re-apply after option-tab re-init. The summary-bar button is created later
    // by `#renderOrderSummary` and re-synced there; this covers the filter-bar
    // copy and any `[data-atc-btn]` already in the tree.
    updateButtonState();
  }


  /**
   * Reads the current live DOM inputs to produce summary rows for the order summary panel.
   * Only rows with at least one qty > 0 are included. The delivery window label is derived
   * from `data-selected-start` / `data-selected-end` (display format); ISO storage is unchanged.
   * When `#activeDateFilter` is set (cart shipment tab), only rows whose ISO window
   * matches that filter are included — same key as cart `#applyFilters`.
   *
   * @returns {Array<{ deliveryWindow: string, type: string, seasonStart: string, qtys: number[] }>}
   */
  #buildSummaryRowsFromDom() {
    const ids   = this.#renderedVariantIds;
    const tbody = this.#inventoryTbody();
    if (!ids.length || !tbody) return [];

    const dateFilter = this.#activeDateFilter;
    const rows = [];
    for (const rowEl of tbody.querySelectorAll('[data-b2b-plus-inventory-row]')) {
      const qtys = ids.map((id) => {
        const input = rowEl.querySelector(`[data-qty-input][data-variant-id="${id}"]`);
        return (input && !input.disabled) ? (Number(input.value) || 0) : 0;
      });
      if (!qtys.some((q) => q > 0)) continue;
      const picker = rowEl.querySelector('[data-delivery-date-picker]');
      if (picker?.classList.contains('b2b-plus-date-picker--error')) continue;
      const start = picker?.dataset.selectedStart;
      const end   = picker?.dataset.selectedEnd;
      if (dateFilter) {
        const isoWindow = start && end
          ? (start === end ? start : `${start} to ${end}`)
          : '';
        if (isoWindow !== dateFilter) continue;
      }
      const deliveryWindow = (start && end)
        ? formatDeliveryWindowDisplay(start, end)
        : (picker?.value ?? '');
      // Inventory/order type for the row — read off any qty input's data-type
      // (every input in a row shares the same combo type). Drives the optional
      // "type · window" prefix in the order-summary grid.
      const type = rowEl.querySelector('[data-qty-input]')?.dataset.type ?? '';
      // ISO start for FW/SS pricing. The visible label above is display-formatted
      // and cannot be parsed back into a season.
      const seasonStart = String(start || rowEl.dataset.rowStart || '').trim();
      rows.push({ deliveryWindow, type, seasonStart, qtys });
    }
    return rows;
  }

  /**
   * Record/forget whether `optionValue`'s saved IDB record holds an order, so
   * the cross-tab "Update Order" label stays accurate. Pass the record that was
   * just read from / written to IDB (not live state).
   * @param {string|null} optionValue
   * @param {object|null} record
   */
  #trackSavedOrder(optionValue, record) {
    const key = optionValue ?? '';
    if (hasExistingSavedCart(record)) this.#savedOrderValues.add(key);
    else this.#savedOrderValues.delete(key);
  }

  /**
   * True when ANY option tab of this product has a saved order — drives the
   * "Add to Order" vs "Update Order" label. Reads the cross-tab saved set, with
   * the active tab's `cartRecord` as a floor so the label is never wrong-low.
   */
  #hasAnySavedOrder() {
    return this.#savedOrderValues.size > 0 || hasExistingSavedCart(this.cartRecord);
  }

  /**
   * Set every ATC button's label from the current cross-tab saved state. The
   * summary-bar button repaints itself from `atcButtonText` on each summary
   * render, but the filter-bar button is set imperatively — so this is called
   * after the prewarm learns other colours' saved orders, to flip it without a
   * full re-render.
   */
  #refreshAtcLabel() {
    const label = this.#hasAnySavedOrder()
      ? b2bPlusString('updateOrder')
      : (b2bPlusString('addToOrder') || 'Add to Order');
    this.#setImperativeAtcLabel(label);
  }

  /**
   * Paint the filter-bar Add / Update label. Skips the order-summary bar, whose
   * label is a lit-html binding re-rendered by `#renderOrderSummary`.
   * @param {string} label
   */
  #setImperativeAtcLabel(label) {
    this.#shadow?.querySelectorAll('[data-atc-btn] [data-button-text]').forEach((el) => {
      if (el.closest('[data-summary-bar-root]')) return;
      el.textContent = label;
    });
  }

  /**
   * Shared options object passed to both `orderSummaryGridTemplate` and
   * `orderSummaryBarTemplate` from `#renderOrderSummary`. Centralises the
   * split-option config read so neither caller duplicates it. `atcButtonText`
   * tracks SAVED state across every option tab (not live edits), so the label
   * reads "Update Order" whenever any colour already has a saved order and does
   * not flip mid-typing before the first save.
   */
  #orderSummaryOpts() {
    return {
      variantIds:          this.#renderedVariantIds,
      isCart:              this.isCart,
      splitOptionName:     String(b2bConfig().productTableSplitOption ?? '').trim() || null,
      selectedOptionValue: this.#selectedOptionValue || null,
      atcButtonText:       this.#hasAnySavedOrder() ? b2bPlusString('updateOrder') : null,
    };
  }

  /**
   * Build order-summary rows from #liveStates for the PDP path.
   * Each result row carries its own variantIds snapshot so the template can render
   * correct column headers per option group. Rows with hasDateError or zero total qty
   * are excluded. When split-by-option is disabled all rows are under '' and optionValue
   * is ignored by the template.
   */
  #buildSummaryRowsFromLiveState() {
    // Cart-lock can disable Immediate/Prebook before those rows are in
    // `#liveStates` (the saved order only held Future). After Clear Selection
    // the inputs work again, but the summary still reads live state — copy the
    // visible matrix in first so a newly enabled type shows up.
    this.#syncVisibleMatrixToLiveState();
    return buildSummaryRowsFromLiveState(this.#liveStates);
  }

  /**
   * Live-state row for a rendered inventory `<tr>`, creating the combo and row
   * when the saved cart never contained that type.
   * @param {HTMLElement|null|undefined} tr
   * @returns {object|null}
   */
  #liveRowForMatrixRow(tr) {
    if (!tr || this.isCart) return null;
    const comboKey = tr.dataset.combinationKey;
    const rowId = tr.dataset.rowId;
    if (!comboKey || !rowId) return null;

    let tab = this.#currentLiveState;
    if (!tab) {
      tab = { variantIds: [...(this.#renderedVariantIds ?? [])], combos: {} };
      this.#liveStates.set(this.#selectedOptionValue ?? '', tab);
    }
    if (!tab.combos[comboKey]) {
      const picker = tr.querySelector('[data-delivery-date-picker]');
      const start = (picker?.dataset.selectedStart ?? '').trim();
      const end = (picker?.dataset.selectedEnd ?? start).trim();
      tab.combos[comboKey] = emptyLiveCombo({
        type:      tr.dataset.rowType ?? '',
        season:    tr.dataset.rowSeason ?? '',
        warehouse: tr.querySelector('[data-qty-input]')?.dataset.warehouse ?? '',
        startDate: tr.dataset.rowStart ?? '',
        endDate:   tr.dataset.rowEnd ?? '',
        baseDeliveryWindow: start ? joinDeliveryWindow(start, end) : '',
      });
    }
    const combo = tab.combos[comboKey];
    if (!combo.rows[rowId]) {
      const picker = tr.querySelector('[data-delivery-date-picker]');
      const start = (picker?.dataset.selectedStart ?? '').trim();
      const end = (picker?.dataset.selectedEnd ?? start).trim();
      if (!Array.isArray(combo.rowIds)) combo.rowIds = [];
      combo.rowIds.push(rowId);
      combo.rows[rowId] = emptyLiveRow(start ? joinDeliveryWindow(start, end) : '');
    }
    return combo.rows[rowId];
  }

  /**
   * Write one qty input into live state. A positive qty clears `hasDateError`
   * so a stale window flag from cart-lock cannot keep the row out of the summary.
   * @param {HTMLInputElement} input
   */
  #writeInputQtyToLiveState(input) {
    const tr = input.closest('[data-b2b-plus-inventory-row]');
    const liveRow = this.#liveRowForMatrixRow(tr);
    if (!liveRow) return;
    const vid = String(input.dataset.variantId ?? '');
    if (!vid) return;
    let qty = Number(input.value) || 0;
    if ('maxQty' in input.dataset) {
      const max = Number(input.dataset.maxQty);
      if (Number.isFinite(max)) qty = Math.min(qty, max);
    }
    if (qty > 0) {
      liveRow.qtys[vid] = qty;
      liveRow.hasDateError = false;
      if (!String(liveRow.deliveryWindow ?? '').trim()) {
        const picker = tr.querySelector('[data-delivery-date-picker]');
        const start = (picker?.dataset.selectedStart ?? '').trim();
        const end = (picker?.dataset.selectedEnd ?? start).trim();
        if (start) liveRow.deliveryWindow = joinDeliveryWindow(start, end);
      }
    } else {
      delete liveRow.qtys[vid];
    }
  }

  /**
   * Project the visible matrix into `#liveStates` before the PDP summary is built.
   * Inactive option tabs are not in the DOM and are left untouched.
   */
  #syncVisibleMatrixToLiveState() {
    if (this.isCart) return;
    this.#inventoryTbody()
      ?.querySelectorAll('[data-b2b-plus-inventory-row][data-row-id][data-combination-key]')
      .forEach((tr) => {
        tr.querySelectorAll('[data-qty-input][data-variant-id]').forEach((input) => {
          if (input.disabled || input.hasAttribute('data-unavailable')) return;
          // Only push positive DOM quantities. A zero must not wipe a saved
          // live-state qty that the matrix has not rendered yet.
          if (!((Number(input.value) || 0) > 0)) return;
          this.#writeInputQtyToLiveState(input);
        });
      });
  }

  /**
   * Repaint the order-summary panel — both the grid and (on PDP / quickview)
   * the inline action bar. Synchronous: both `render()` calls finish before
   * returning, so callers can rely on the wrapper reflecting the latest rows
   * by the time this returns.
   *
   * - On cart: rows come from the live DOM (auto-save source of truth).
   * - On PDP / quickview: rows come from `#liveStates` (post-migration source).
   *
   * The Clear / Add-to-Order buttons stay under the parent's existing click
   * handlers (`#initSummaryClearBtn`, `#initAtcButton`) — they live in the
   * bar template which the parent renders directly, so their selectors are
   * always reachable by the shadow-delegated handlers.
   */
  /**
   * Coalesce rapid order-summary repaints (e.g. one per keystroke while typing a
   * quantity) into a single render on the next animation frame. Date commits and
   * other low-frequency callers continue to use `#renderOrderSummary` directly.
   */
  #scheduleOrderSummaryRender() {
    if (this.#summaryRenderRaf !== null) return;
    this.#summaryRenderRaf = requestAnimationFrame(() => {
      this.#summaryRenderRaf = null;
      this.#renderOrderSummary();
    });
  }

  #renderOrderSummary() {
    // Date commits refresh the summary without a qty `input` event. Recompute
    // the product-details total here so a season change (FW ↔ SS) updates it
    // even when the summary panel itself is off.
    this.#updateProductLineTotalsFromInputs();

    const wrapper = this.#shadow.querySelector('[data-order-summary-root]');
    if (!wrapper) return;

    // Universal chokepoint for "quantities changed" — keep the option tabs'
    // has-qty borders current here so every mutation path (input, multi-apply,
    // clear, date edits) is covered without a per-path refresh call.
    this.#refreshOptionTabQtyFlags();

    const rows = this.isCart
      ? this.#buildSummaryRowsFromDom()
      : this.#buildSummaryRowsFromLiveState();

    const hasContent = rows.length > 0 && this.productJSON;
    wrapper.hidden = !hasContent;
    if (!hasContent) return;

    const opts = this.#orderSummaryOpts();

    // Summary filter tabs — PDP / quickview only. Tab values are the distinct
    // split-option values that currently carry quantities. If the active filter
    // points at a value that no longer has rows (e.g. it was just cleared),
    // fall back to "All" so the grid never renders empty.
    const optionValues = [...new Set(rows.map((r) => r.optionValue).filter(Boolean))];
    if (this.#summaryFilterValue && !optionValues.includes(this.#summaryFilterValue)) {
      this.#summaryFilterValue = null;
    }
    if (!this.isCart) {
      const tabsRoot = wrapper.querySelector('[data-summary-tabs-root]');
      if (tabsRoot) render(summaryTabsTemplate(optionValues, this.#summaryFilterValue), tabsRoot);
    }

    // Grid reflects the active filter tab; the action bar (totals + ATC) always
    // reflects the whole order, since "Add to Order" commits every populated tab.
    const gridRows = this.#summaryFilterValue
      ? rows.filter((r) => (r.optionValue ?? '') === this.#summaryFilterValue)
      : rows;

    const gridRoot = wrapper.querySelector('[data-summary-grid-root]');
    if (gridRoot) render(orderSummaryGridTemplate(gridRows, this.productJSON, opts), gridRoot);

    // Action bar — PDP / quickview only. The bar mount is omitted from the
    // wrapper on cart by `orderSummaryWrapperTemplate`.
    if (!this.isCart) {
      const barRoot = wrapper.querySelector('[data-summary-bar-root]');
      if (barRoot) {
        render(orderSummaryBarTemplate(rows, this.productJSON, opts), barRoot);
        // The bar's `[data-atc-btn]` is template-rendered `disabled` and is
        // created here for the first time on the keystroke that makes the
        // summary non-empty. `#initAtcButton`'s input handler runs `updateButtonState`
        // *before* this render (the button didn't exist yet), so re-apply the
        // enabled/disabled state now — otherwise the button stays disabled until
        // the next keystroke.
        this.#updateButtonState?.();
        // Mirror the `#initAtcButton` showActionButton=false hide pass — the
        // bar renders its own `[data-atc-btn]` independently of the filter-bar.
        if (!this.showActionButton) {
          barRoot.querySelectorAll('[data-atc-btn]').forEach((b) => { b.hidden = true; });
        }
      }
    }
  }

  /**
   * Wires qty inputs and date pickers to `#renderOrderSummary` via shadow-root event
   * delegation so rows added after initial render (shipment rows) are covered without
   * re-binding. Initial render happens separately after `#validateRestoredData` in `#renderAndInit`.
   */
  #initOrderSummarySync() {
    this.#on(this.#shadow, 'input', (e) => {
      if (e.target.closest('[data-qty-input]')) this.#scheduleOrderSummaryRender();
    });
    this.#on(this.#shadow, 'change', (e) => {
      if (e.target.closest('[data-delivery-date-picker]')) this.#renderOrderSummary();
    });
  }

  /**
   * Clears the order-summary selection. Only wired in the product-page context
   * (not cart). The scope depends on the active summary filter tab:
   *   - "All" tab (`#summaryFilterValue` null) → clears every option tab.
   *   - A specific value → clears only that split-option value's selection,
   *     then resets the filter back to "All" so the next view shows what remains.
   */
  #initSummaryTabHandler() {
    this.#on(this.#shadow, 'click', (e) => {
      const tab = e.target.closest('[data-summary-tab]');
      if (!tab) return;
      // Empty value = the "All" tab. Normalise to null so the filter logic and
      // the clear-button label both treat it as "no filter".
      const value = tab.dataset.summaryTab || null;
      if (value === this.#summaryFilterValue) return;
      this.#summaryFilterValue = value;
      this.#renderOrderSummary();
    });
  }

  #initSummaryClearBtn() {
    this.#on(this.#shadow, 'click', async (e) => {
      if (!e.target.closest('[data-summary-clear-btn]')) return;
      if (this.#summaryFilterValue) {
        await this.#clearSummaryOptionValue(this.#summaryFilterValue);
      } else {
        await this.#clearAllSummarySelections();
      }
    });
  }

  /**
   * Cart-lock just disabled every inventory row whose type is not `lockedType`.
   * Zero those quantities in the inputs and in `#liveStates` so the order
   * summary drops them, then persist when a saved order already contained them.
   *
   * Called by `b2b-plus/cart-lock` after it marks the conflicting rows.
   * Safe to call again — a second pass finds nothing left to clear.
   *
   * @param {string} lockedType - display type the cart is locked to
   * @returns {Promise<void>}
   */
  // @public
  clearCartLockConflictSelections(lockedType) {
    if (!this.isConnected || !this.#shadow) return Promise.resolve();
    if (!this.#zeroCartLockConflictQtys(lockedType)) return Promise.resolve();
    const snapshot = this.#snapshotCartLockClear();
    if (!snapshot) return Promise.resolve();
    const run = () => this.#persistCartLockClearSnapshot(snapshot);
    this.#cartLockPersistChain = this.#cartLockPersistChain.then(run, run);
    return this.#cartLockPersistChain;
  }

  /**
   * Same type comparison cart-lock uses to decide a row conflicts
   * (lowercase, strip spaces / underscores / hyphens).
   * @param {string} type
   * @returns {string}
   */
  #cartLockTypeKey(type) {
    return String(type ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  }

  /**
   * Set every conflicting row's quantities to 0 — visible inputs and every
   * option tab in `#liveStates`. The order summary on PDP reads live state,
   * so clearing the inputs alone would leave the rows in the summary.
   * @param {string} lockedType
   * @returns {boolean} true when at least one quantity was cleared
   */
  #zeroCartLockConflictQtys(lockedType) {
    const locked = this.#cartLockTypeKey(lockedType);
    if (!locked) return false;

    const conflicts = (type) => {
      const key = this.#cartLockTypeKey(type);
      return key !== '' && key !== locked;
    };

    let changed = false;
    const comboKeys = new Set();

    for (const tab of this.#liveStates.values()) {
      for (const [ck, combo] of Object.entries(tab?.combos ?? {})) {
        const type = combo?.type || String(ck).split('|')[0];
        if (!conflicts(type)) continue;
        for (const row of Object.values(combo?.rows ?? {})) {
          for (const vid of Object.keys(row?.qtys ?? {})) {
            if ((Number(row.qtys[vid]) || 0) > 0) changed = true;
            delete row.qtys[vid];
          }
        }
      }
    }

    this.#inventoryTbody()?.querySelectorAll('[data-b2b-plus-inventory-row]').forEach((row) => {
      if (!conflicts(row.dataset.rowType ?? '')) return;
      if (row.dataset.combinationKey) comboKeys.add(row.dataset.combinationKey);
      row.querySelectorAll('[data-qty-input]').forEach((input) => {
        if (!((Number(input.value) || 0) > 0)) return;
        input.value = '0';
        input.classList.remove('b2b-plus-quantity-input--error');
        const errEl = input.parentElement?.querySelector('[data-qty-error]');
        if (errEl) errEl.hidden = true;
        changed = true;
      });
    });

    if (!changed) return false;

    for (const ck of comboKeys) this.#syncCapsAfterChange(ck, { clearErrorsWhenValid: true });
    this.#updateButtonState?.();
    if (b2bConfig().enableOrderSummary !== false) this.#renderOrderSummary();
    else this.#updateProductLineTotalsFromInputs();
    this.#refreshOptionTabQtyFlags();
    return true;
  }

  /**
   * Capture the post-clear cart record to write back. Only tabs that already
   * have a saved order are included — an unsaved draft that cart-lock wiped
   * stays unsaved. On the cart the DOM is the saved order.
   * @returns {{ kind: 'cart', handle: string, record: object }|{ kind: 'pdp', tabs: Array<{ optionValue: string, handle: string, record: object }> }|null}
   */
  #snapshotCartLockClear() {
    if (this.isCart) {
      const handle = this.#storageHandle;
      if (!handle) return null;
      return { kind: 'cart', handle, record: this.#buildCurrentRecord() };
    }

    const tabs = [];
    for (const [optionValue, liveTab] of this.#liveStates) {
      const key = optionValue ?? '';
      if (!this.#savedOrderValues.has(key)) continue;
      const handle = this.#storageHandleFor(optionValue);
      if (!handle) continue;
      tabs.push({ optionValue, handle, record: buildRecordForTab(liveTab) });
    }
    if (!tabs.length) return null;
    return { kind: 'pdp', tabs };
  }

  /**
   * Write a cart-lock clear snapshot back to IndexedDB and notify cart chrome.
   * @param {{ kind: 'cart', handle: string, record: object }|{ kind: 'pdp', tabs: Array<{ optionValue: string, handle: string, record: object }> }} snapshot
   * @returns {Promise<void>}
   */
  async #persistCartLockClearSnapshot(snapshot) {
    if (!this.isConnected) return;
    try {
      if (snapshot.kind === 'cart') {
        this.cartRecord = snapshot.record;
        await saveProductRecord(snapshot.handle, snapshot.record, { productId: this.productJSON?.id });
        this.cartData = flattenProductRecordToLineItems(this.cartRecord);
        this.dispatchEvent(new CustomEvent('b2b:cart-saved', { bubbles: true }));
        return;
      }

      for (const { optionValue, record } of snapshot.tabs) {
        if ((optionValue ?? '') !== (this.#selectedOptionValue ?? '')) continue;
        this.cartRecord = hasExistingSavedCart(record) ? record : {};
      }
      await Promise.all(snapshot.tabs.map(async ({ optionValue, handle, record }) => {
        if (hasExistingSavedCart(record)) {
          await saveProductRecord(handle, record, { productId: this.productJSON?.id });
          this.#trackSavedOrder(optionValue, record);
        } else {
          await clearCart(handle);
          this.#trackSavedOrder(optionValue, null);
        }
      }));
      this.cartData = flattenProductRecordToLineItems(this.cartRecord);
      this.#refreshAtcLabel();
      this.dispatchEvent(new CustomEvent('b2b:cart-saved', { bubbles: true }));
    } catch (err) {
      console.error('b2b-plus: failed to clear cart-lock conflict quantities', err);
    }
  }

  /**
   * "Clear All": wipe every option tab's saved record + live state and reset the
   * visible tab's DOM controls so the table returns to a blank state.
   */
  async #clearAllSummarySelections() {
    // An in-flight cart-lock write captured this product before the clear. Let
    // it finish so it cannot put the row back after we delete it.
    await this.#cartLockPersistChain;

    // 1. Clear the IndexedDB record for EVERY option tab of this product, not
    //    just the active one — otherwise a split-option product keeps the saved
    //    quantities/dates of the tabs the buyer isn't currently viewing.
    const { optionGroups, isSplit } = resolveOptionGroups(this.productJSON, {
      enableSplit: b2bConfig().enableProductTableSplit !== false,
      splitOption: b2bConfig().productTableSplitOption ?? '',
    }, PT_SDK_DEPS);
    const optionValues = isSplit ? [...optionGroups.keys()] : [null];

    await Promise.all(optionValues.map((value) => {
      const handle = this.#storageHandleFor(value);
      return handle ? clearCart(handle) : Promise.resolve();
    }));
    this.cartRecord = {};
    this.#savedOrderValues.clear();

    // 2. Wipe in-memory live state for every tab (quantities, delivery dates,
    //    notes) while preserving each tab's row structure so later edits still
    //    have a live state to write into.
    // Locked ATS / Immediate windows are auto-selected and can't be re-picked,
    // so they survive the clear.
    clearLiveStatesInPlace(this.#liveStates, { keepDeliveryWindow: isLockedShipmentWindowEnabled });

    // 3. Reset the visible tab's DOM controls (the only tab with rendered inputs).
    this.#clearVisibleTabControls();

    this.#summaryFilterValue = null;
    this.#userHasInteracted = false;
    this.#updateButtonState?.();
    this.#renderOrderSummary(); // also refreshes the option tabs' has-qty flags
    this.#syncCartLockAfterSelectionClear();
  }

  /**
   * "Clear Selection" for a single split-option value: wipe only that value's
   * saved record + live state, reset the DOM only when that value is the tab
   * currently rendered in the matrix, then drop back to the "All" filter.
   * @param {string} value
   */
  async #clearSummaryOptionValue(value) {
    await this.#cartLockPersistChain;
    const handle = this.#storageHandleFor(value);
    if (handle) await clearCart(handle);
    this.#savedOrderValues.delete(value ?? '');

    const liveTab = this.#liveStateFor(value);
    if (liveTab) {
      clearLiveStatesInPlace([[value, liveTab]], { keepDeliveryWindow: isLockedShipmentWindowEnabled });
    }

    // Only the active matrix tab has rendered inputs. If the cleared value is
    // that tab, reset its controls and drop the now-stale active record.
    if (value === this.#selectedOptionValue) {
      this.cartRecord = {};
      this.#clearVisibleTabControls();
    }

    this.#summaryFilterValue = null;
    this.#userHasInteracted = false;
    this.#updateButtonState?.();
    this.#renderOrderSummary(); // also refreshes the option tabs' has-qty flags
    this.#syncCartLockAfterSelectionClear();
  }

  /**
   * Cart-lock re-reads IndexedDB only on `b2b:cart-saved`. Clear Selection
   * deletes this product without that event, so the conflict banner and
   * disabled rows would stay even after the cart is empty. Dispatching here
   * lets the lock pass remove the banner and enable every row when nothing
   * with quantity remains; a cart that still holds another product keeps its lock.
   */
  #syncCartLockAfterSelectionClear() {
    this.dispatchEvent(new CustomEvent('b2b:cart-saved', { bubbles: true }));
  }

  /** Reset the visible tab's qty inputs, delivery pickers, and note controls. */
  #clearVisibleTabControls() {
    this.#shadow.querySelectorAll('[data-qty-input]').forEach((input) => {
      input.value = '0';
      input.classList.remove('b2b-plus-quantity-input--error');
      const errEl = input.parentElement?.querySelector('[data-qty-error]');
      if (errEl) errEl.hidden = true;
    });
    this.#shadow.querySelectorAll('[data-delivery-date-picker]').forEach((input) => {
      // Locked ATS / Immediate pickers hold an auto-selected window the buyer
      // can't re-pick — keep it (the live row keeps it too, see clearLiveStatesInPlace).
      const rowType = input.closest('[data-b2b-plus-inventory-row]')?.dataset.rowType ?? '';
      if (isLockedShipmentWindowEnabled(rowType)) return;
      this.#flatpickrInstances?.get(input)?.clear();
      input.value = '';
      delete input.dataset.selectedStart;
      delete input.dataset.selectedEnd;
      input.classList.remove('b2b-plus-date-picker--error');
      const dateError = input.closest('[data-b2b-plus-inventory-row]')?.querySelector('[data-date-error]');
      if (dateError) { dateError.textContent = ''; dateError.hidden = true; }
    });
    // The "no quantity entered" checkout banner is qty-derived too — a Clear
    // must not leave a stale error above an already-empty table.
    const minCheckoutErr = this.#shadow.querySelector('[data-checkout-min-qty-error]');
    if (minCheckoutErr) { minCheckoutErr.textContent = ''; minCheckoutErr.hidden = true; }
    this.#shadow.querySelectorAll('[data-row-note]').forEach((textarea) => {
      textarea.value  = '';
      textarea.hidden = true;
    });
    this.#shadow.querySelectorAll('[data-note-toggle]').forEach((btn) => {
      btn.textContent = b2bPlusString('addNote');
    });

    // Zeroing inputs does not fire `input` handlers, so sibling caps and the
    // pooled `[data-available-qty]` badges would stay at the reduced values from
    // the previous entries. Reconcile every visible combo so max-qty and the
    // availability number revert to the original stock (mirrors Clear → 0 demand).
    const tbody = this.#inventoryTbody();
    if (!tbody) return;
    if (this.#poolingEnabled()) this.#recomputePooledCaps();
    for (const ck of new Set(
      [...tbody.querySelectorAll('[data-b2b-plus-inventory-row][data-combination-key]')]
        .map((tr) => tr.dataset.combinationKey).filter(Boolean),
    )) {
      this.#updateSiblingCaps(ck, { clearErrorsWhenValid: true });
    }
    if (this.#poolingEnabled()) this.#updatePooledAvailabilityDisplay();
    // Same reason: no `input` event fired, so the product-details
    // `[data-product-line-total-qty]` / `[data-product-line-total-price]` slots
    // would keep showing the pre-Clear totals.
    this.#updateProductLineTotalsFromInputs();
  }

  /**
   * Applies `data-cart-view` to `.b2b-plus-table-container` and updates the toggle
   * button label to describe the action (what clicking will switch *to*). CSS rules key
   * off this attribute to show/hide the edit vs. summary regions without re-rendering.
   * Called immediately after `render()` to prevent both panels flashing visible, then
   * again on every toggle click.
   */
  #setCartViewMode(mode) {
    this.#cartViewMode = mode;
    const container = this.#shadow.querySelector('.b2b-plus-table-container');
    if (container) container.dataset.cartView = mode;
    const btn = this.#shadow.querySelector('[data-view-toggle-btn]');
    if (btn) {
      btn.textContent = mode === 'summary'
        ? (b2bPlusString('orderSummaryViewEdit') || 'Edit Deliveries')
        : (b2bPlusString('orderSummaryViewSummary') || 'View Order Summary');
    }
  }

  /** Wires the toggle button click; initial view state is set earlier in `#renderAndInit`. */
  #initCartViewToggle() {
    this.#on(this.#shadow, 'click', (e) => {
      if (!e.target.closest('[data-view-toggle-btn]')) return;
      this.#setCartViewMode(this.#cartViewMode === 'summary' ? 'edit' : 'summary');
    });
  }

  /**
   * Wire up the "Remove Item" button.
   *
   * Clears all IndexedDB entries for this product handle and dispatches
   * `b2b:product-removed` so `<b2b-plus-cart>` can remove the table from
   * the DOM and refresh totals. Controlled by `data-show-remove-button`.
   * Default: hidden. Enabled on the cart page by `<b2b-plus-cart>` when
   * `config.cart.enableRemoveButton` is true.
   */
  #initRemoveButton() {
    if (!this.showRemoveButton) return;
    const root = this.#shadow;
    const btn  = root.querySelector('[data-remove-btn]');
    if (!btn) return;

    const spinner = btn.querySelector('[data-remove-spinner]');
    const btnText = btn.querySelector('[data-remove-btn-text]');

    this.#on(btn, 'click', async () => {
      if (btn.disabled) return;
      const handle = this.#storageHandle;
      if (!handle) return;
      if (!window.confirm(b2bPlusString('removeFromOrderConfirm'))) return;

      btn.disabled = true;
      spinner?.removeAttribute('hidden');
      if (btnText) btnText.hidden = true;

      try {
        await clearCart(handle);
        // Notify <b2b-plus-cart> to remove this table from the DOM and update totals.
        // `composed: true` is required so the event crosses the cart's closed shadow boundary.
        this.dispatchEvent(new CustomEvent('b2b:product-removed', {
          bubbles:  true,
          composed: true,
          detail:   { handle },
        }));
      } catch (err) {
        console.error('b2b-plus-product-table: remove failed', err);
        btn.disabled = false;
        spinner?.setAttribute('hidden', '');
        if (btnText) btnText.hidden = false;
      }
    });
  }

  /**
   * Seed `#liveStates[optionValue]` from a v2 IDB record. Converts the on-disk
   * shape into the in-memory `combos` structure that drives renders and saves.
   * @param {object} record - v2 nested record from IDB
   * @param {string|null} optionValue
   * @param {string[]} [variantIds]
   */
  #initLiveStateFromRecord(record, optionValue, variantIds = this.#renderedVariantIds) {
    this.#liveStates.set(optionValue ?? '', buildLiveTabFromRecord(record, variantIds));
  }

  /**
   * Post-render validation pass. Coordinator only — each rule is a pure
   * compute function in `b2b-plus/product-table-sdk`. The class snapshots
   * DOM + liveState into plain data, gets Correction descriptors back, and
   * applies them via the small `#apply*` helpers below. Any persisting
   * correction triggers a single IDB write-back derived from liveState.
   *
   * Rules, in order:
   *   1. canonicalizeLiveState — bridge legacy IDB keys to current DOM keys.
   *   2. computeRowVariantCorrections — `data-unavailable` purge + cap clamp.
   *   3. computeDeliveryWindowCorrection — clear stale windows; restore valid flatpickrs.
   *   4. #updateSiblingCaps — reconcile multi-shipment splits against API caps.
   */
  #validateRestoredData() {
    // Cart page reads from DOM and auto-saves on action; split options each have
    // their own line items. liveState maintenance is PDP/quickview only.
    if (this.isCart) return;
    const recordBefore = JSON.stringify(this.cartRecord ?? {});
    this.cartRecord    = migrateProductRecord(this.cartRecord);
    const record       = this.cartRecord;
    if (!record || !Object.keys(record).length) return;

    const handle = this.#storageHandle;
    const bounds = getShipWindowLengths(this.inventoryData);
    const tbody = this.#inventoryTbody();
    const inventoryRows = tbody
      ? [...tbody.querySelectorAll('[data-b2b-plus-inventory-row][data-row-id][data-combination-key]')]
      : [];

    let needsSave = recordBefore !== JSON.stringify(record);

    // Rule 1 — rekey liveTab.combos upfront so every per-row lookup below
    // uses the canonical (DOM) keys.
    const domComboKeys = new Set(inventoryRows.map((tr) => tr.dataset.combinationKey).filter(Boolean));
    if (canonicalizeLiveState(record, this.#currentLiveState, domComboKeys, resolveCartComboForDomKey)) {
      needsSave = true;
    }

    const isSingleDay = isSingleDayShipWindow(this.inventoryData);

    for (const tr of inventoryRows) {
      const refs = this.#resolveRowRefs(tr, record);
      if (!refs) continue;
      const { row, liveRow } = refs;

      // Rule 2 — variant constraints.
      const variantSnap = this.#snapshotRowVariantsForValidation(tr, row, liveRow);
      const variantCorrections = computeRowVariantCorrections(variantSnap);
      if (variantCorrections.length) {
        this.#applyRowVariantCorrections(tr, variantCorrections, { row, liveRow });
        needsSave = true;
      }

      // Rule 3 — delivery window freshness. Locked types keep the auto-selected
      // horizon window written by `#initDatePickerForInput`; do not restore a
      // previously saved custom range.
      const datePicker = tr.querySelector('[data-delivery-date-picker]');
      if (isLockedShipmentWindowEnabled(tr.dataset.rowType ?? '')) {
        const start = (datePicker?.dataset.selectedStart ?? '').trim();
        const end   = (datePicker?.dataset.selectedEnd ?? start).trim();
        if (start) {
          const win = joinDeliveryWindow(start, end);
          if (liveRow && liveRow.deliveryWindow !== win) {
            liveRow.deliveryWindow = win;
            liveRow.hasDateError   = false;
            needsSave = true;
          }
          if (row && row.deliveryWindow !== win) {
            row.deliveryWindow = win;
            needsSave = true;
          }
        }
      } else {
        const savedWindow = liveRow ? (liveRow.deliveryWindow ?? '') : (row.deliveryWindow ?? '');
        const analysis = analyzeSavedDeliveryWindow(savedWindow, { minDays: bounds.minDays, maxDays: bounds.maxDays });
        const windowCorrections = computeDeliveryWindowCorrection(analysis, {
          hasPicker:   !!datePicker,
          isSingleDay,
        });
        if (windowCorrections && windowCorrections.length) {
          const persisted = this.#applyDeliveryWindowCorrections(tr, datePicker, windowCorrections, { row, liveRow });
          if (persisted) needsSave = true;
        }
      }
    }

    // Rule 4 — sibling caps for every combo present in the DOM. When pooling is
    // on, refresh the pooled caps from the just-settled demand first so the loop
    // reconciles every window against its current pooled headroom.
    if (this.#poolingEnabled()) this.#recomputePooledCaps();
    for (const ck of new Set(
      [...(tbody?.querySelectorAll('[data-b2b-plus-inventory-row][data-combination-key]') ?? [])]
        .map((tr) => tr.dataset.combinationKey).filter(Boolean),
    )) {
      if (this.#updateSiblingCaps(ck)) needsSave = true;
    }

    if (needsSave && handle) this.#persistValidatedRecord(handle);

    this.#updateButtonState?.();
    this.#updateProductLineTotalsFromInputs();
  }

  /**
   * Locate the cart-record row and the matching liveState row for one rendered
   * `<tr>`. Assumes `canonicalizeLiveState` already ran for the current pass
   * so storageKey === domCk; returns `null` when no record entry matches.
   *
   * @param {HTMLElement} tr
   * @param {object} record - the migrated v2 cartRecord
   * @returns {{ row: object, liveRow: object|undefined } | null}
   */
  #resolveRowRefs(tr, record) {
    const domCk = tr.dataset.combinationKey;
    const rowId = tr.dataset.rowId;
    if (!domCk || !rowId) return null;
    const resolved = resolveCartComboForDomKey(record, domCk);
    if (!resolved) return null;
    const row = (resolved.combo.rows ?? []).find((r) => r.rowId === rowId);
    if (!row) return null;
    const liveRow = this.#currentLiveState?.combos[domCk]?.rows[rowId];
    return { row, liveRow };
  }

  /**
   * Snapshot one row's variants into RowVariantSnapshot[] for the SDK to
   * reason over. liveState wins as the qty source when present — same
   * authoritativeness rule as the original pre-extraction code.
   *
   * @param {HTMLElement} tr
   * @param {object} row
   * @param {object|undefined} liveRow
   * @returns {Array<{ variantId: string, currentQty: number, maxQty: number|null, disabled: boolean, unavailable: boolean, inputExists: boolean }>}
   */
  #snapshotRowVariantsForValidation(tr, row, liveRow) {
    const qtysSource = liveRow?.qtys ?? row.byVariantId ?? {};
    const snaps = [];
    for (const [variantId, qty] of Object.entries(qtysSource)) {
      const input = tr.querySelector(
        `[data-qty-input][data-variant-id="${CSS.escape(String(variantId))}"]`,
      );
      snaps.push({
        variantId,
        currentQty:  Number(qty ?? 0) || 0,
        maxQty:      input?.dataset.maxQty !== undefined ? (Number(input.dataset.maxQty) || 0) : null,
        disabled:    !!input?.disabled,
        unavailable: !!input?.hasAttribute('data-unavailable'),
        inputExists: !!input,
      });
    }
    return snaps;
  }

  /**
   * Apply set-qty corrections from the row-variant rule to the DOM and to the
   * authoritative qty source. Centralizes every input.value / errEl write so
   * the SDK rules can stay pure.
   *
   * Mirrors the historical dual-target write: liveRow.qtys when present,
   * row.byVariantId otherwise — `#persistValidatedRecord` rebuilds the IDB
   * record from liveState, so shadow writes to row.byVariantId would be
   * discarded when liveRow is the source.
   *
   * @param {HTMLElement} tr
   * @param {Array<{ kind: 'set-qty', variantId: string, value: number, errorCode?: string, errorParams?: object }>} corrections
   * @param {{ row: object, liveRow: object|undefined }} ctx
   */
  #applyRowVariantCorrections(tr, corrections, { row, liveRow }) {
    for (const c of corrections) {
      const input = tr.querySelector(
        `[data-qty-input][data-variant-id="${CSS.escape(String(c.variantId))}"]`,
      );
      if (!input) continue;
      input.value = String(c.value);
      if (c.errorCode) {
        if (c.errorCode === 'qtyRestoredError') input.classList.add('b2b-plus-quantity-input--error');
        const errEl = input.parentElement?.querySelector('[data-qty-error]');
        if (errEl) {
          errEl.textContent = this.#localizeError(c.errorCode, c.errorParams);
          errEl.hidden = false;
        }
      }
      if (liveRow) {
        if (c.value > 0) liveRow.qtys[c.variantId] = c.value;
        else             delete liveRow.qtys[c.variantId];
      } else {
        if (c.value > 0) row.byVariantId[c.variantId] = c.value;
        else             delete row.byVariantId[c.variantId];
      }
    }
  }

  /**
   * Resolve an SDK errorCode + errorParams to a localized string via
   * `b2bPlusString`, with `{key}` substitution. Falls back to the existing
   * inline English for the multi-shipment overflow message when the string
   * key is missing — matches the pre-extraction behavior of
   * `#updateSiblingCaps`.
   *
   * @param {string} code
   * @param {object} [params]
   * @returns {string}
   */
  #localizeError(code, params) {
    const tpl = b2bPlusString(code);
    if (!tpl) {
      if (code === 'qtyInventoryUpdatedAcrossShipments' && params?.total != null) {
        return `Only ${params.total} unit(s) available across all shipments — quantities were adjusted.`;
      }
      return code;
    }
    if (!params) return tpl;
    let out = tpl;
    for (const [k, v] of Object.entries(params)) {
      out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
    return out;
  }

  /**
   * Apply delivery-window corrections to the DOM, picker, and liveRow / row.
   * Returns true when at least one applied correction is persisting (i.e.
   * `persist !== false`) — restoring a valid range via `set-flatpickr-window`
   * is a UI refresh and does NOT count toward needsSave.
   *
   * @param {HTMLElement} tr
   * @param {HTMLElement|null} datePicker
   * @param {Array<{ kind: string, errorCode?: string, start?: string, end?: string, single?: boolean, persist?: boolean }>} corrections
   * @param {{ row: object, liveRow: object|undefined }} ctx
   * @returns {boolean}
   */
  #applyDeliveryWindowCorrections(tr, datePicker, corrections, { row, liveRow }) {
    let persisted = false;
    for (const c of corrections) {
      if (c.kind === 'clear-window') {
        if (datePicker) {
          datePicker.classList.add('b2b-plus-date-picker--error');
          delete datePicker.dataset.selectedStart;
          delete datePicker.dataset.selectedEnd;
          datePicker.value = '';
        }
        const errEl = tr.querySelector('[data-date-error]');
        if (errEl) {
          errEl.textContent = this.#localizeError(c.errorCode);
          errEl.hidden = false;
        }
        if (liveRow) { liveRow.deliveryWindow = ''; liveRow.hasDateError = true; }
        else         { row.deliveryWindow     = ''; }
        if (c.persist !== false) persisted = true;
      } else if (c.kind === 'set-flatpickr-window') {
        const canPrefillDeliveryWindow = window.b2bPlus?.config?.prefillDeliveryDateWindow !== false;
        if (canPrefillDeliveryWindow) {
          const fp = this.#flatpickrInstances?.get(datePicker);
          if (fp) {
            c.single
              ? fp.setDate(c.start, false)
              : fp.setDate([c.start, c.end], false);
          }
          if (datePicker && c.start) {
            this.#setDatePickerDisplay(datePicker, c.start, c.single ? c.start : c.end);
          }
        }
        if (c.persist !== false) persisted = true;
      }
    }
    return persisted;
  }

  /**
   * Persist the corrected liveState back to IDB and emit `b2b:cart-saved`.
   * Fires while `connectedCallback` is still finishing — the cart consumer
   * absorbs the burst with `B2B_CART_SAVED_DEBOUNCE_MS` (single source of
   * truth in `b2b-plus-cart-storage`). Do not hard-code a competing value.
   */
  #persistValidatedRecord(handle) {
    this.cartRecord = this.#buildCurrentRecord();
    saveProductRecord(handle, this.cartRecord, { productId: this.productJSON?.id })
      .then(() => {
        this.cartData = flattenProductRecordToLineItems(this.cartRecord);
        this.dispatchEvent(new CustomEvent('b2b:cart-saved', { bubbles: true }));
      })
      .catch((err) => console.error('b2b-plus: failed to write-back corrected cart data', err));
  }

  /**
   * When `data-auto-save` is present, persists the current table state to IndexedDB
   * in real time using the v2 record shape.
   * Qty and shipment-name inputs share a 1000ms debounce; date and note changes save immediately via #onAutoSave.
   */
  #initAutoSave() {
    if (!this.hasAttribute('data-auto-save')) return;

    const handle = this.#storageHandle;
    if (!handle) return;

    const persistToIndexedDB = () => {
      // Capture epoch before the async write. Structural mutations (remove-shipment)
      // bump `#saveEpoch` so a stale DOM snapshot queued behind the handle lock
      // cannot overwrite the post-removal record and leave a ghost date tab.
      const epoch = this.#saveEpoch;
      const record = this.#buildCurrentRecordFromDom();
      this.cartRecord = record;
      // Track + return the promise so callers (flushPendingSave) can await the
      // committed write before reading the record back during checkout validation.
      // Recording it here covers EVERY save path — debounced, beforeunload, and the
      // immediate un-awaited date-commit/multi-apply saves — not just the debounce.
      this.#pendingSavePromise = saveProductRecord(handle, record, { productId: this.productJSON?.id })
        .then(async () => {
          if (epoch !== this.#saveEpoch) {
            // A newer structural write won — re-persist live state so IDB matches.
            this.cartRecord = this.#buildCurrentRecord();
            await saveProductRecord(handle, this.cartRecord, { productId: this.productJSON?.id });
          }
          this.cartData = flattenProductRecordToLineItems(this.cartRecord);
          this.dispatchEvent(new CustomEvent('b2b:cart-saved', { bubbles: true }));
        })
        .catch((err) => console.error('b2b-plus: auto-save failed', err));
      return this.#pendingSavePromise;
    };

    // Qty / shipment-name edits route through #triggerSave so manual-save mode
    // stages them (dirty flag + "Save changes" bar) instead of persisting.
    const scheduleAutoSave = () => this.#triggerSave();

    this.#inventoryTbody()?.querySelectorAll('[data-qty-input]').forEach((qtyInput) => {
      this.#on(qtyInput, 'input', scheduleAutoSave);
    });

    this.#inventoryTbody()?.querySelectorAll('[data-shipment-name]').forEach((nameInput) => {
      this.#on(nameInput, 'input', scheduleAutoSave);
    });

    const flushOnUnload = () => {
      if (this.#debounceTimer !== null) {
        clearTimeout(this.#debounceTimer);
        this.#debounceTimer = null;
        persistToIndexedDB();
      } else if (this.#isManualSave() && this.#isDirty) {
        // Manual mode: don't lose staged edits if the buyer navigates away.
        persistToIndexedDB();
      }
    };
    this.#beforeUnloadHandler = flushOnUnload;
    window.addEventListener('beforeunload', flushOnUnload);

    this.#onAutoSave = persistToIndexedDB;
  }

  /** Manual mode only: delegate "Save changes" clicks to the commit path. */
  #initManualSaveBar() {
    if (!this.#isManualSave()) return;
    this.#on(this.#shadow, 'click', (e) => {
      const btn = e.target?.closest?.('[data-manual-save-btn]');
      if (btn && this.#shadow.contains(btn)) this.#commitManualSave();
    });
  }

  /**
   * Whether this table stages edits behind a manual "Save changes" button instead
   * of auto-persisting. True only on the cart when `config.cart.autoUpdateQuantities`
   * is explicitly false. Memoised; config is server-rendered before the cart loads.
   * @returns {boolean}
   */
  #isManualSave() {
    if (this.#manualSaveMode === null) {
      this.#manualSaveMode = this.isCart && b2bConfig().cart?.autoUpdateQuantities === false;
    }
    return this.#manualSaveMode;
  }

  /**
   * Single entry point every user-edit handler calls to request a save.
   * - Auto mode (default): schedules a debounced write, or an immediate one when
   *   `debounce` is false (date commits, multi-apply, bulk apply) — prior behaviour.
   * - Manual mode (cart + autoUpdateQuantities === false): writes nothing. Marks the
   *   table dirty and reveals the "Save changes" bar; the write only happens on a
   *   Save click or a flush boundary (checkout / tab switch / page unload).
   * No-op on PDP/quickview where auto-save was never wired (#onAutoSave unset).
   * @param {{ debounce?: boolean }} [opts]
   */
  #triggerSave({ debounce = true } = {}) {
    if (this.#isManualSave()) {
      this.#markDirty();
      return;
    }
    if (!this.#onAutoSave) return;
    clearTimeout(this.#debounceTimer);
    if (debounce) {
      this.#debounceTimer = setTimeout(() => {
        this.#debounceTimer = null;
        this.#onAutoSave();
      }, 1000);
    } else {
      this.#debounceTimer = null;
      this.#onAutoSave();
    }
  }

  /** Manual mode: flag unsaved edits and (re)render the "Save changes" bar. */
  #markDirty() {
    this.#isDirty = true;
    this.#renderManualSaveBar();
  }

  /** Manual mode: clear the unsaved-edits flag and hide the "Save changes" bar. */
  #clearDirty() {
    this.#isDirty = false;
    this.#renderManualSaveBar();
  }

  /**
   * Persist the staged manual-save edits to IndexedDB now (the "Save changes" click
   * handler). Reuses the auto-save persist path so the `b2b:cart-saved` event still
   * fires and the cart footer totals refresh.
   */
  #commitManualSave() {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = null;
    this.#onAutoSave?.();
    this.#clearDirty();
  }

  /** Render the manual-save bar into its slot. No-op outside manual mode / before render. */
  #renderManualSaveBar() {
    if (!this.#isManualSave()) return;
    const root = this.#shadow.querySelector('[data-manual-save-root]');
    if (!root) return;
    render(
      manualSaveBarTemplate({
        dirty: this.#isDirty,
        showDateWarning: this.#isDirty && !!this.#activeDateFilter,
      }),
      root,
    );
  }

  /**
   * Tell this table which cart date-filter window is active so the order-summary
   * grid shows only that shipment's delivery rows, and the manual-save bar can
   * warn that edits are scoped to a single delivery window. Called by
   * <b2b-plus-cart> on every filter change. `null`/'all' clears the filter.
   * @param {string|null} dateFilter
   */
  // @public
  setActiveDateFilter(dateFilter) {
    const next = dateFilter && dateFilter !== 'all' ? dateFilter : null;
    if (next === this.#activeDateFilter) return;
    this.#activeDateFilter = next;
    this.#scheduleOrderSummaryRender();
    // Only the warning visibility changes; re-render the bar if edits are staged.
    if (this.#isDirty) this.#renderManualSaveBar();
  }

  /**
   * Injects product / inventory / cart snapshot before the element is connected.
   * **Must be called before the element is inserted into the DOM.** Calling after
   * `connectedCallback` fires has no effect — the data will be ignored.
   * @param {{ productJSON: object, inventoryData?: object|null, cartRecord?: object, cartData?: object[] }|null} data
   */
  // @public
  setPrefetchedData(data) {
    this.#prefetchedData = data ?? null;
  }

  /**
   * Populate #combinationCaps from the live inventory API response. Thin
   * wrapper over `computeCombinationCaps` in product-table-sdk; called once
   * at init (and once per tab switch). Never persisted to IDB.
   */
  #buildCombinationCaps() {
    const { rows } = getMergedInventoryRows(
      this.productJSON, this.inventoryData, startOfLocalDay(new Date()), PT_SDK_DEPS,
    );
    this.#mergedRows = rows;
    if (inventoryPoolingOptions().enabled) {
      // Physical caps (no quantity-rule fold — pooling operates on raw stock;
      // the rule max is re-applied per window afterwards).
      this.#rawCombinationCaps = computeCombinationCaps(rows, PT_SDK_DEPS);
      this.#recomputePooledCaps();
    } else {
      this.#rawCombinationCaps = null;
      this.#poolByCombo = new Map();
      this.#consolidatedMap = new Map();
      this.#combinationCaps = computeCombinationCaps(rows, PT_SDK_DEPS, this.#getQuantityRules());
    }
  }

  /** True when pooled inventory is active for this element (caps were built pooled). */
  #poolingEnabled() {
    return this.#rawCombinationCaps != null;
  }

  /**
   * Recompute pooled per-combination caps from the current in-DOM demand and the
   * cached raw physical caps, then fold the catalog quantity-rule max back in.
   * Refreshes #combinationCaps, #poolByCombo and #consolidatedMap. No-op unless
   * pooling is enabled. Cheap: pools are tiny (a handful of windows per variant).
   */
  #recomputePooledCaps() {
    if (!this.#rawCombinationCaps) return;
    const { pooledCaps, consolidated, poolByCombo } = computePooledInventory({
      mergedRows:  this.#mergedRows,
      rawCaps:     this.#rawCombinationCaps,
      currentQtys: this.#currentDemandByCombo(),
      opts:        inventoryPoolingOptions(),
      deps:        PT_SDK_DEPS,
    });
    this.#combinationCaps = foldQuantityRuleMaxIntoCaps(pooledCaps, this.#getQuantityRules());
    this.#consolidatedMap = consolidated;
    this.#poolByCombo     = poolByCombo;
  }

  /**
   * Sum the currently-entered quantity per (combinationKey, variantId) from the
   * rendered rows. Used as the pool's "committed demand" input so a later
   * window's cap reflects what earlier/sibling windows have already taken.
   * @returns {Map<string, Record<string, number>>}
   */
  #currentDemandByCombo() {
    const out = new Map();
    const tbody = this.#inventoryTbody();
    if (!tbody) return out;
    tbody.querySelectorAll('[data-b2b-plus-inventory-row][data-combination-key]').forEach((tr) => {
      const ck = tr.dataset.combinationKey;
      if (!ck) return;
      tr.querySelectorAll('[data-qty-input][data-variant-id]').forEach((input) => {
        const qty = Number(input.value) || 0;
        if (qty <= 0) return;
        const vid = String(input.dataset.variantId);
        const rec = out.get(ck) ?? {};
        rec[vid] = (rec[vid] ?? 0) + qty;
        out.set(ck, rec);
      });
    });
    return out;
  }

  /**
   * Cap-sync after a quantity change. Reconciles `data-max-qty` and repaints the
   * available number so sibling shipment rows share one cap. With pooling,
   * editing one window changes the pooled headroom of every co-pooled window,
   * so the pooled caps are recomputed from live demand first.
   * @returns {boolean} true when any quantity value was clamped (caller may save).
   */
  #syncCapsAfterChange(comboKey, opts = {}) {
    if (!comboKey) return false;
    let mutated = false;
    if (this.#poolingEnabled()) {
      this.#recomputePooledCaps();
      const pool = this.#poolByCombo.get(comboKey);
      const keys = pool && pool.size ? pool : new Set([comboKey]);
      for (const ck of keys) {
        if (this.#updateSiblingCaps(ck, opts)) mutated = true;
      }
    } else {
      mutated = this.#updateSiblingCaps(comboKey, opts);
    }
    // Sibling shipment rows share one cap. Refresh the visible available
    // number after the max-qty pass so a new row doesn't keep the full stock.
    this.#updatePooledAvailabilityDisplay();
    return mutated;
  }

  /**
   * Availability badge under each qty input.
   *
   * The number is stock still left in this window: the cap (pooled headroom
   * when pooling is on) minus every unit already entered on any shipment row
   * of that window, including the row being typed in. A future row of 700
   * with 100 entered shows 600; typing 50 on the next shipment drops both
   * badges to 550. With `byType` on, Future June 100 + Future July 50 shows
   * 150 on June and 50 on July before any quantity is typed. Prebook /
   * uncapped inputs are left untouched.
   */
  #updatePooledAvailabilityDisplay() {
    const tbody = this.#inventoryTbody();
    if (!tbody) return;
    const entered        = this.#currentDemandByCombo();
    const thresholdRules = this.inventoryData?.inventory_threshold_rules ?? [];
    tbody.querySelectorAll('[data-b2b-plus-inventory-row][data-combination-key]').forEach((tr) => {
      const ck   = tr.dataset.combinationKey;
      const caps = this.#combinationCaps.get(ck);
      if (!caps) return;
      tr.querySelectorAll('[data-qty-input][data-variant-id]').forEach((input) => {
        const vid = String(input.dataset.variantId);
        const cap = caps[vid];
        if (cap == null) return; // prebook / uncapped — no meaningful pooled number
        const enteredQty = Number(entered.get(ck)?.[vid] ?? 0) || 0;
        const remaining  = Math.max(0, cap - enteredQty);
        const wrap  = input.parentElement?.querySelector('.available-quantity');
        const numEl = wrap?.querySelector('[data-available-qty]');
        if (numEl) setSlotText(numEl, remaining);
        const badge = wrap?.querySelector('.b2b-plus-availability-badge');
        if (badge) {
          const color = resolveThresholdColor(remaining, thresholdRules);
          if (color) badge.style.backgroundColor = color;
          else       badge.style.removeProperty('background-color');
        }
      });
    });
  }

  /**
   * Lazily build (and memoise) the per-variant quantity-rule map from
   * `productJSON.quantity_rules` + config. Returns an empty object until
   * productJSON is available, so it is safe to call from any render path.
   * @returns {Record<string, { min: number|null, max: number|null }>}
   */
  #getQuantityRules() {
    if (this.#quantityRules) return this.#quantityRules;
    if (!this.productJSON) return {};
    this.#quantityRules = buildQuantityRuleMap(this.productJSON, quantityRuleOptions());
    return this.#quantityRules;
  }

  /**
   * Copy each Immediate variant's rendered start/end onto the row that will
   * be saved. PDP keeps them on live state (Add/Update reads that). Cart
   * patches the stored record and writes it when the map changed, so checkout
   * does not wait for another quantity edit.
   */
  #syncImmediateVariantWindows() {
    const tbody = this.#inventoryTbody();
    if (!tbody) return;

    const windowsFor = (tr) => {
      const windows = {};
      tr.querySelectorAll('[data-qty-input][data-variant-id]').forEach((input) => {
        const vid = String(input.dataset.variantId ?? '');
        if (!vid) return;
        windows[vid] = {
          startDate: String(input.dataset.start ?? '').trim(),
          endDate:   String(input.dataset.end ?? '').trim(),
        };
      });
      return windows;
    };

    const rows = tbody.querySelectorAll(
      '[data-b2b-plus-inventory-row][data-row-id][data-combination-key]',
    );

    if (!this.isCart) {
      const liveTab = this.#currentLiveState;
      if (!liveTab) return;
      rows.forEach((tr) => {
        if (normalizeOrderTypeGroup(tr.dataset.rowType) !== 'immediate') return;
        const liveRow = liveTab.combos?.[tr.dataset.combinationKey]?.rows?.[tr.dataset.rowId];
        if (!liveRow) return;
        liveRow.inventoryWindowByVariantId = windowsFor(tr);
      });
      return;
    }

    const record = this.cartRecord;
    if (!record || typeof record !== 'object') return;
    let changed = false;
    rows.forEach((tr) => {
      if (normalizeOrderTypeGroup(tr.dataset.rowType) !== 'immediate') return;
      const resolved = resolveCartComboForDomKey(record, tr.dataset.combinationKey);
      const row = resolved?.combo?.rows?.find((r) => r.rowId === tr.dataset.rowId);
      if (!row) return;
      const windows = windowsFor(tr);
      if (JSON.stringify(row.inventoryWindowByVariantId ?? {}) === JSON.stringify(windows)) return;
      row.inventoryWindowByVariantId = windows;
      changed = true;
    });
    if (!changed) return;
    const handle = this.#storageHandle;
    if (!handle) return;
    this.#pendingSavePromise = saveProductRecord(handle, record, { productId: this.productJSON?.id })
      .catch((err) => console.error('b2b-plus: failed to store per-variant inventory windows', err));
  }

  /**
   * Build the v2 record for the current tab.
   * On cart: delegates to #buildCurrentRecordFromDom (DOM is the source of truth; auto-save path).
   * On PDP: serialises #currentLiveState to the v2 record shape via product-table-sdk.
   * @returns {object}
   */
  #buildCurrentRecord() {
    if (this.isCart) return this.#buildCurrentRecordFromDom();
    return buildRecordForTab(this.#currentLiveState);
  }

  /**
   * Snapshot the current DOM state into a v2 record object.
   * Iterates every <tr data-row-id data-combination-key> in the shadow DOM,
   * grouped by combination key.  Empty non-base rows are pruned.
   * Does NOT write to IDB — callers must call saveProductRecord when needed.
   * @returns {object}
   */
  #buildCurrentRecordFromDom() {
    const record = {};
    const tbody = this.#inventoryTbody();
    if (!tbody) return record;

    tbody.querySelectorAll('[data-b2b-plus-inventory-row][data-row-id][data-combination-key]').forEach((tr) => {
      const rowId    = tr.dataset.rowId;
      const comboKey = tr.dataset.combinationKey;
      if (!comboKey) return;

      const existingCombo = resolveCartComboForDomKey(this.cartRecord, comboKey)?.combo
        ?? this.cartRecord[comboKey];
      const existingRow   = existingCombo?.rows?.find((r) => r.rowId === rowId);

      if (!record[comboKey]) {
        const firstInput = tr.querySelector('[data-qty-input]');
        record[comboKey] = {
          type:              firstInput?.dataset.type      ?? existingCombo?.type      ?? '',
          season:            firstInput?.dataset.season    ?? existingCombo?.season    ?? '',
          warehouse:         firstInput?.dataset.warehouse ?? existingCombo?.warehouse ?? '',
          // Group dates stay on the bucket. An Immediate input's data-start is
          // that variant's own window, so the first cell must not become the
          // combination key.
          startDate:         tr.dataset.rowStart ?? existingCombo?.startDate ?? '',
          endDate:           tr.dataset.rowEnd   ?? existingCombo?.endDate   ?? '',
          totalQtyByVariant: null,
          rows:              [],
        };
      }

      const combo = record[comboKey];
      if (!Array.isArray(combo.rows)) combo.rows = [];

      let row = combo.rows.find((r) => r.rowId === rowId);
      if (!row) {
        row = {
          rowId,
          byVariantId:     { ...(existingRow?.byVariantId ?? {}) },
          deliveryWindow:  existingRow?.deliveryWindow ?? '',
          note:            existingRow?.note           ?? '',
          poNumber:        existingRow?.poNumber       ?? '',
          shipmentName:    existingRow?.shipmentName   ?? '',
          extraProperties: { ...(existingRow?.extraProperties ?? {}) },
          ...(existingRow?.[PAST_DATE_ADJUSTED_KEY]
            ? { [PAST_DATE_ADJUSTED_KEY]: existingRow[PAST_DATE_ADJUSTED_KEY] }
            : {}),
        };
        combo.rows.push(row);
      }

      const byVariantId = { ...(row.byVariantId ?? {}) };
      const trackVariantWindows = normalizeOrderTypeGroup(combo.type) === 'immediate';
      const inventoryWindowByVariantId = trackVariantWindows ? {} : null;
      tr.querySelectorAll('[data-qty-input]').forEach((input) => {
        const vid = String(input.dataset.variantId ?? '');
        if (!vid) return;
        const qty = Number(input.value);
        if (qty > 0) byVariantId[vid] = qty;
        else delete byVariantId[vid];
        if (inventoryWindowByVariantId) {
          inventoryWindowByVariantId[vid] = {
            startDate: String(input.dataset.start ?? '').trim(),
            endDate:   String(input.dataset.end ?? '').trim(),
          };
        }
      });
      row.byVariantId = byVariantId;
      if (inventoryWindowByVariantId) row.inventoryWindowByVariantId = inventoryWindowByVariantId;

      const datePicker = tr.querySelector('[data-delivery-date-picker]');
      if (datePicker?.dataset.selectedStart && datePicker?.dataset.selectedEnd) {
        row.deliveryWindow = joinDeliveryWindow(datePicker.dataset.selectedStart, datePicker.dataset.selectedEnd);
      } else if (datePicker?.dataset.selectedStart) {
        // Partial / single-day selection stored on dataset only (display value is not ISO).
        row.deliveryWindow = datePicker.dataset.selectedStart;
      }
      // No `datePicker.value` fallback: the input's visible text is display-only
      // ("May 20" / "May 20, 2027"), so persisting it would put a non-canonical window into
      // IndexedDB and break every consumer that keys on "YYYY-MM-DD". When the
      // dataset has no selection there is nothing canonical to save — keep
      // whatever window the row already carried.

      const noteFromDom = tr.querySelector('[data-row-note]')?.value?.trim() ?? '';
      if (noteFromDom) row.note = noteFromDom;
      row.poNumber = existingRow?.poNumber ?? row.poNumber ?? '';
      if (rowId !== 'base') {
        const nameFromDom = tr.querySelector('[data-shipment-name]')?.value?.trim() ?? '';
        if (nameFromDom) row.shipmentName = nameFromDom;
      }
      row.extraProperties = { ...(existingRow?.extraProperties ?? {}), ...(row.extraProperties ?? {}) };
    });

    // Guarantee each combo has at least the base row.
    for (const [ck, combo] of Object.entries(record)) {
      if (!combo.rows.some((r) => r.rowId === 'base')) {
        const existingBase = this.cartRecord[ck]?.rows?.find((r) => r.rowId === 'base');
        let prefillStart = combo.startDate ?? '';
        let prefillEnd   = combo.endDate ?? '';
        if (isLockedShipmentWindowEnabled(combo.type)) {
          const today = startOfLocalDay(new Date());
          const resolved = resolveInventoryDateWindow(
            { startDate: prefillStart, endDate: prefillEnd },
            today,
          );
          prefillStart = toISODate(resolved.start);
          const cappedEnd = atsImmediatePickerEnd(resolved.end, resolved.start);
          prefillEnd = cappedEnd ? toISODate(cappedEnd) : '';
        }
        const p = isLockedShipmentWindowEnabled(combo.type)
          ? this.#lockedPrefillDeliveryDates(prefillStart, prefillEnd)
          : (() => {
            const sharedParts = isPrePopulatedShipmentDateEnabled(combo.type)
              ? parseDeliveryWindowParts(getSharedShipmentDateForTypeSync(combo.type))
              : null;
            return sharedParts
              ? { selectedStart: sharedParts.start, selectedEnd: sharedParts.end }
              : this.#defaultPrefillDeliveryDates(prefillStart, prefillEnd);
          })();
        const baseDw = p ? joinDeliveryWindow(p.selectedStart, p.selectedEnd) : '';
        combo.rows.unshift({
          rowId: 'base',
          byVariantId: {},
          deliveryWindow: baseDw,
          note: '',
          poNumber: existingBase?.poNumber ?? '',
        });
      }
    }

    return record;
  }

  /**
   * Recalculate and apply data-max-qty for every variant input across all rows
   * that share the same combination key.  Prebook variants (null cap) are skipped.
   * With a single row, restores the full inventory cap.
   *
   * Multi-shipment rows: walks inputs in DOM order with a **running** per-variant total so
   * when the sum exceeds the API cap (e.g. inventory dropped), values clamp to the full cap
   * instead of under-filling (a single snapshot of totals before clamping caused that bug).
   *
   * When the combined quantity across shipments initially exceeds the cap, every quantity
   * input for that variant shows the same “inventory updated” copy (not only the last cell).
   *
   * @param {string} comboKey
   * @param {object} [options]
   * @param {boolean} [options.clearErrorsWhenValid=false] - When true, removes qty error UI for
   *   inputs that are within the new cap (use after add/remove shipment). When false (default),
   *   leaves error state alone so “exceeded max” feedback from typing survives the follow-up cap sync.
   * @returns {boolean} True if any quantity value was changed (caller may sync IndexedDB).
   */
  #updateSiblingCaps(comboKey, { clearErrorsWhenValid = false } = {}) {
    if (!comboKey) return false;
    const caps = this.#combinationCaps.get(comboKey);
    if (!caps) return false;

    const tbody = this.#inventoryTbody();
    if (!tbody) return false;

    const rows = [...tbody.querySelectorAll(
      `[data-b2b-plus-inventory-row][data-combination-key="${CSS.escape(comboKey)}"]`,
    )];
    if (!rows.length) return false;

    // Build an ORDERED list of cap-eligible inputs (filtered the same way the
    // pre-extraction code did). Each entry pairs the DOM element with the
    // snapshot the SDK sees, so the apply layer can map correction.index
    // back to an input.
    const capInputs = [];
    rows.forEach((tr) => {
      tr.querySelectorAll('[data-qty-input]').forEach((input) => {
        if (input.disabled || input.hasAttribute('data-unavailable')) return;
        const vid = input.dataset.variantId;
        if (caps[vid] == null) return;
        capInputs.push({
          input,
          snapshot: { variantId: vid, currentValue: Number(input.value) || 0 },
        });
      });
    });
    if (!capInputs.length) return false;

    const corrections = computeSiblingCapCorrections(
      capInputs.map((c) => c.snapshot),
      caps,
      { rowCount: rows.length, clearErrorsWhenValid },
    );

    return this.#applySiblingCapCorrections(capInputs, corrections);
  }

  /**
   * Apply sibling-cap Correction descriptors to the DOM and to liveRow.qtys.
   * Returns true when at least one `set-qty` correction was applied (the
   * historical "mutated" signal — `set-max-qty` / `set-error` / `clear-error`
   * are cosmetic and must NOT count toward needsSave).
   *
   * @param {Array<{ input: HTMLInputElement, snapshot: object }>} capInputs
   * @param {Array<{ kind: string, index: number, value?: number, errorCode?: string, errorParams?: object }>} corrections
   * @returns {boolean}
   */
  #applySiblingCapCorrections(capInputs, corrections) {
    let mutated = false;
    for (const c of corrections) {
      const { input } = capInputs[c.index] ?? {};
      if (!input) continue;
      switch (c.kind) {
        case 'set-qty': {
          input.value = String(c.value);
          mutated = true;
          if (c.errorCode) {
            input.classList.add('b2b-plus-quantity-input--error');
            const errEl = input.parentElement?.querySelector('[data-qty-error]');
            if (errEl) {
              errEl.textContent = this.#localizeError(c.errorCode, c.errorParams);
              errEl.hidden = false;
            }
          }
          const sibTr   = input.closest('[data-b2b-plus-inventory-row]');
          const liveRow = this.#currentLiveState?.combos[sibTr?.dataset.combinationKey]?.rows[sibTr?.dataset.rowId];
          if (liveRow) {
            if (c.value > 0) liveRow.qtys[input.dataset.variantId] = c.value;
            else             delete liveRow.qtys[input.dataset.variantId];
          }
          break;
        }
        case 'set-max-qty':
          input.dataset.maxQty = String(c.value);
          break;
        case 'set-error': {
          input.classList.add('b2b-plus-quantity-input--error');
          const errEl = input.parentElement?.querySelector('[data-qty-error]');
          if (errEl) {
            errEl.textContent = this.#localizeError(c.errorCode, c.errorParams);
            errEl.hidden = false;
          }
          break;
        }
        case 'clear-error': {
          input.classList.remove('b2b-plus-quantity-input--error');
          const errEl = input.parentElement?.querySelector('[data-qty-error]');
          if (errEl) errEl.hidden = true;
          break;
        }
      }
    }
    // A `set-qty` correction rewrites a SIBLING row's input with no `input`
    // event, and the caller's own refresh (if any) may already have run for the
    // row being typed in — re-total here so the product-details slots never lag
    // behind a clamp.
    if (mutated) this.#updateProductLineTotalsFromInputs();
    return mutated;
  }

  /**
   * Wire all interactive behavior for a dynamically added shipment <tr>.
   * Covers: qty validation + sibling caps, line totals, ATC state, debounced
   * auto-save, lazy Flatpickr date picker, note toggle, and remove button.
   * @param {HTMLElement} tr
   */
  #wireRowBehavior(tr) {
    const comboKey = tr.dataset.combinationKey;
    const rowId    = tr.dataset.rowId;

    tr.querySelectorAll('[data-qty-input]').forEach((input) => {
      const errorEl = input.parentElement?.querySelector('[data-qty-error]');

      // Same Safari numeric hardening as init rows; registered first so the
      // sanitiser strips non-digits before the live-state handler reads the value.
      this.#applyNumericHardening(input);

      this.#on(input, 'input', () => {
        this.#validateQtyInput(input, errorEl);
        if (comboKey) this.#syncCapsAfterChange(comboKey);
        this.#updateProductLineTotalsFromInputs();
        this.#userHasInteracted = true;
        this.#updateButtonState?.();
        this.#triggerSave();
        this.#writeInputQtyToLiveState(input);
      });
      this.#on(input, 'blur', () => {
        input.classList.remove('b2b-plus-quantity-input--error');
        if (errorEl) errorEl.hidden = true;
        this.#validateQtyRuleMin(input, errorEl);
      });
    });

    const datePicker = tr.querySelector('[data-delivery-date-picker]');
    if (datePicker) this.#initDatePickerForInput(datePicker);

    if (window.b2bPlus?.config?.enableItemNotes) this.#wireNoteToggleForRow(tr);

    const shipmentNameInput = tr.querySelector('[data-shipment-name]');
    if (shipmentNameInput) {
      this.#on(shipmentNameInput, 'input', () => {
        this.#triggerSave();
        const liveRow = this.#currentLiveState?.combos[comboKey]?.rows[rowId];
        if (liveRow) liveRow.shipmentName = shipmentNameInput.value.trim();
      });
    }

    this.#wireRemoveShipmentRow(tr);
  }

  /**
   * Wire the remove-shipment button inside a <tr>.
   * No-ops for base rows (no [data-remove-shipment-row] button present).
   * @param {HTMLElement} tr
   */
  #wireRemoveShipmentRow(tr) {
    const btn = tr.querySelector('[data-remove-shipment-row]');
    if (!btn) return;

    this.#on(btn, 'click', async () => {
      const rowId    = tr.dataset.rowId;
      const comboKey = tr.dataset.combinationKey;
      if (!rowId || rowId === 'base' || !comboKey) return;

      try {
        // Qty/date edits auto-save from a DOM snapshot. If that save is still
        // debounced or in flight when Remove is clicked, it can rewrite the
        // deleted row into IndexedDB — the row disappears from the table but its
        // delivery-window date tab (shipment box) stays. Cancel + mutate first so
        // any in-flight save's epoch-mismatch corrective write sees the row gone.
        clearTimeout(this.#debounceTimer);
        this.#debounceTimer = null;
        this.#saveEpoch += 1;

        const liveCombo = this.#currentLiveState?.combos[comboKey];
        if (liveCombo) {
          delete liveCombo.rows[rowId];
          liveCombo.rowIds = liveCombo.rowIds.filter((id) => id !== rowId);
        }

        // Drop the DOM row before awaiting/persisting so DOM-based snapshots
        // cannot resurrect it.
        tr.remove();

        if (this.#pendingSavePromise) {
          try { await this.#pendingSavePromise; } catch { /* ignore prior save errors */ }
        }

        // PDP/quickview: in-memory only — the removal is not persisted to IDB
        // until the user clicks Add/Update Order. Cart: the cart IS the committed
        // state, so remove from IDB immediately.
        if (this.isCart) {
          const handle = this.#storageHandle;
          if (handle) {
            this.cartRecord = this.#buildCurrentRecord();
            const productEmpty = await removeShipmentRow({ handle, combinationKeyStr: comboKey, rowId, record: this.cartRecord });
            this.cartData = flattenProductRecordToLineItems(this.cartRecord);
            // The removed shipment held the last of this product's quantity — the
            // record is gone from IDB, so the table has to go with it.
            // `composed: true` crosses the cart's closed shadow boundary.
            if (productEmpty) {
              this.dispatchEvent(new CustomEvent('b2b:product-removed', {
                bubbles:  true,
                composed: true,
                detail:   { handle },
              }));
              return;
            }
            this.dispatchEvent(new CustomEvent('b2b:cart-saved', { bubbles: true }));
          }
        }

        this.#syncCapsAfterChange(comboKey, { clearErrorsWhenValid: true });
        this.#updateProductLineTotalsFromInputs();
        this.#updateButtonState?.();
        this.#renderOrderSummary();
      } catch (err) {
        console.error('b2b-plus: failed to remove shipment row', err);
      }
    });
  }

  /**
   * Wire remove-shipment buttons for all rows rendered at init time.
   * Dynamically added rows are wired by #wireRowBehavior.
   */
  #initRemoveShipmentRows() {
    this.#inventoryTbody()?.querySelectorAll('[data-b2b-plus-inventory-row][data-row-id]').forEach((tr) => this.#wireRemoveShipmentRow(tr));
  }

  /**
   * Wire the "Add Shipment" button and combination picker rendered below the table.
   */
  #initAddShipmentButton() {
    const container = this.#shadow.querySelector('[data-add-shipment-container]');
    if (!container) return;

    const btn = container.querySelector('[data-add-shipment-btn]');
    if (!btn) return;

    // Single-combination fast path: click directly adds the row, no picker shown.
    if (btn.dataset.autoAddKey) {
      const comboKey = btn.dataset.autoAddKey;
      this.#on(btn, 'click', (e) => {
        e.stopPropagation();
        this.#addShipmentRow(comboKey);
      });
      return;
    }

    const picker = container.querySelector('[data-shipment-picker]');
    if (!picker) return;

    this.#on(btn, 'click', (e) => {
      // The shadow-root closer runs on the same bubble. Stop here so the click
      // that opens the picker is not also treated as an outside click.
      e.stopPropagation();
      picker.hidden = !picker.hidden;
      if (!picker.hidden) {
        picker.value = '';
        picker.focus();
      }
    });

    // Native <select> change event — fires when the user picks an option.
    this.#on(picker, 'change', () => {
      const comboKey = picker.value;
      picker.hidden = true;
      picker.value  = '';
      if (comboKey) this.#addShipmentRow(comboKey);
    });

    // Close dropdown when the user clicks outside the container.
    this.#on(this.#shadow, 'click', (e) => {
      if (!picker.hidden && !container.contains(e.target)) {
        picker.hidden = true;
        picker.value  = '';
      }
    });

    this.#on(this.#shadow, 'keydown', (e) => {
      if (e.key !== 'Escape' || picker.hidden) return;
      e.preventDefault();
      picker.hidden = true;
      picker.value  = '';
      btn.focus();
    });
  }

  /**
   * Dynamically insert a new shipment row for the given combination key.
   * The new <tr> is appended after the last existing row for that combination.
   * Wires all behavior, updates this.cartRecord, and persists to IDB.
   * @param {string} comboKey
   */
  /**
   * Create and insert a shipment `<tr>` for the given comboKey with the given rowId.
   * Inserts after the current last sibling row and wires interactive behaviour.
   * Does NOT update cartRecord or persist to IndexedDB — callers own that step.
   * @param {string} comboKey
   * @param {string} rowId
   * @returns {HTMLElement|null} The inserted `<tr>`, or null if it could not be created.
   */
  #insertShipmentRow(comboKey, rowId) {
    const tbody = this.#inventoryTbody();
    if (!tbody) return null;

    const siblingRows = [...tbody.querySelectorAll(
      `[data-b2b-plus-inventory-row][data-combination-key="${CSS.escape(comboKey)}"]`,
    )];
    if (!siblingRows.length) return null;

    const lastRow        = siblingRows[siblingRows.length - 1];
    const shipmentIndex  = siblingRows.length;
    const rowSeason      = lastRow.dataset.rowSeason ?? '';
    const rowType        = lastRow.dataset.rowType   ?? '';
    const rowStartStr    = lastRow.dataset.rowStart  ?? '';
    const rowEndStr      = lastRow.dataset.rowEnd    ?? '';

    const baseRow        = tbody.querySelector(
      `[data-combination-key="${CSS.escape(comboKey)}"][data-row-id="base"]`,
    ) ?? siblingRows[0];
    const refPicker      = lastRow.querySelector('[data-delivery-date-picker]')
      ?? baseRow.querySelector('[data-delivery-date-picker]');
    let pickerStartStr   = (refPicker?.dataset.rowStart ?? '').trim();
    let pickerEndStr     = (refPicker?.dataset.rowEnd ?? '').trim();
    if (!pickerStartStr) pickerStartStr = rowStartStr.trim() || toISODate(new Date());

    const isPrebookRow   = isPrebookOrderType(rowType);
    const caps           = this.#combinationCaps.get(comboKey) ?? {};
    const thresholdRules = this.inventoryData?.inventory_threshold_rules ?? [];
    const config         = b2bConfig();
    // Use the ACTIVE tab's rendered variant ids — not the full product — so a
    // shipment row has exactly the same columns as the base rows. With
    // split-by-option on, the full product spans every option group (e.g. all
    // colours); rendering all of them here added extra, misaligned columns.
    const variantIds     = this.#renderedVariantIds.length
      ? [...this.#renderedVariantIds]
      : (this.productJSON?.variants ?? []).map((v) => String(v.id));

    // Quantity already entered on the existing rows of this window. The new
    // shipment shares that stock, so its available number is the cap minus
    // what those rows have already taken (700 with 100 selected → 600).
    const takenByVariant = new Map();
    siblingRows.forEach((tr) => {
      tr.querySelectorAll('[data-qty-input][data-variant-id]').forEach((input) => {
        const id = String(input.dataset.variantId ?? '');
        if (!id) return;
        const qty = Number(input.value) || 0;
        if (qty > 0) takenByVariant.set(id, (takenByVariant.get(id) ?? 0) + qty);
      });
    });

    // Only include variants that have an active (non-disabled) input in the base row
    // so disabled variant columns stay disabled in the new row too.
    const byVariantId = new Map();
    variantIds.forEach((vid) => {
      const baseInput = baseRow.querySelector(
        `[data-qty-input][data-variant-id="${CSS.escape(vid)}"]`,
      );
      if (!baseInput) return;
      const totalCap = caps[vid] ?? null;
      const capNum = totalCap == null ? null : Number(totalCap);
      const taken = takenByVariant.get(String(vid)) ?? 0;
      const remaining = capNum != null && Number.isFinite(capNum)
        ? Math.max(0, Math.trunc(capNum) - taken)
        : totalCap;
      byVariantId.set(vid, {
        quantity:  isPrebookRow ? null : remaining,
        season:    rowSeason,
        type:      rowType,
        // Carry the base cell's own window so an added shipment row does not
        // stamp every size with the group date.
        startDate: baseInput.dataset.start ?? '',
        endDate:   baseInput.dataset.end ?? '',
      });
    });

    // Unique suffix for this row's input `id` attributes. Use a UUID so two
    // "Add shipment" clicks in the same millisecond can't collide on element ids.
    const rowIndex = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const tpl = inventoryRowTemplate({
      row:              { season: rowSeason, type: rowType, byVariantId },
      rowIndex,
      variantIds,
      rowStartStr,
      rowEndStr,
      pickerStartStr,
      pickerEndStr,
      isPrebookRow,
      thresholdRules,
      enableItemNotes:  !!config.enableItemNotes,
      rowId,
      shipmentIndex,
      combinationKeyStr: comboKey,
      isCart: this.isCart,
      quantityRules: this.#getQuantityRules(),
      dateLocked: isLockedShipmentWindowEnabled(rowType),
      productPrice: this.productJSON?.price ?? null,
      seasonPrices: seasonPricesFromVariants(
        (this.productJSON?.variants ?? []).filter((v) => variantIds.includes(String(v.id))),
      ),
    });

    const temp = document.createElement('div');
    render(html`${tpl}`, temp);
    const newTr = temp.querySelector('[data-b2b-plus-inventory-row]');
    if (!newTr) return null;

    lastRow.after(newTr);
    const liveTab = this.#currentLiveState;
    if (liveTab?.combos[comboKey]) {
      const baseWindows = liveTab.combos[comboKey].rows?.base?.inventoryWindowByVariantId;
      const liveRow = emptyLiveRow();
      if (baseWindows && typeof baseWindows === 'object') {
        liveRow.inventoryWindowByVariantId = { ...baseWindows };
      }
      liveTab.combos[comboKey].rowIds.push(rowId);
      liveTab.combos[comboKey].rows[rowId] = liveRow;
    }
    this.#wireRowBehavior(newTr);
    return newTr;
  }

  async #addShipmentRow(comboKey) {
    if (b2bConfig().enableAddShipment === false) return;
    const comboType = String(comboKey ?? '').split('|')[0] ?? '';
    if (normalizeOrderTypeGroup(comboType) === 'immediate') return;
    // Same comparison as cart-lock row disabling (lowercase, strip separators).
    // A Future lock can only add Future shipments; a Prebook lock, only Prebook.
    const lockedType = String(this.dataset.cartLockedType ?? '').trim();
    if (lockedType) {
      const norm = (type) => String(type ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
      if (norm(comboType) !== norm(lockedType)) return;
    }
    const tbody = this.#inventoryTbody();
    if (!tbody) return;

    const siblingRows = [...tbody.querySelectorAll(
      `[data-b2b-plus-inventory-row][data-combination-key="${CSS.escape(comboKey)}"]`,
    )];
    if (!siblingRows.length) return;

    // Read metadata before #insertShipmentRow changes the DOM.
    const lastRow     = siblingRows[siblingRows.length - 1];
    const rowType     = lastRow.dataset.rowType   ?? '';
    const rowSeason   = lastRow.dataset.rowSeason ?? '';
    const rowStartStr = lastRow.dataset.rowStart  ?? '';
    const rowEndStr   = lastRow.dataset.rowEnd    ?? '';
    const rowId       = newShipmentRowId();

    // Prefill: shared shipment date for this inventory type wins; else default window.
    const refPicker = lastRow.querySelector('[data-delivery-date-picker]');
    const prefillStart = (refPicker?.dataset.rowStart ?? '').trim() || rowStartStr;
    const prefillEnd   = (refPicker?.dataset.rowEnd ?? '').trim() || rowEndStr;
    const lockedDates = isLockedShipmentWindowEnabled(rowType)
      ? this.#lockedPrefillDeliveryDates(prefillStart, prefillEnd)
      : null;
    const sharedParts = !lockedDates && isPrePopulatedShipmentDateEnabled(rowType)
      ? parseDeliveryWindowParts(getSharedShipmentDateForTypeSync(rowType))
      : null;
    const prefillDates = lockedDates
      || (sharedParts
        ? { selectedStart: sharedParts.start, selectedEnd: sharedParts.end }
        : this.#defaultPrefillDeliveryDates(prefillStart, prefillEnd));
    const defaultDeliveryWindow = prefillDates
      ? joinDeliveryWindow(prefillDates.selectedStart, prefillDates.selectedEnd)
      : '';

    const liveTab = this.#currentLiveState;
    if (liveTab) {
      if (!liveTab.combos[comboKey]) {
        liveTab.combos[comboKey] = emptyLiveCombo({
          type:      rowType,
          season:    rowSeason,
          startDate: rowStartStr,
          endDate:   rowEndStr,
          baseDeliveryWindow: defaultDeliveryWindow,
        });
      }
    }

    if (!this.#insertShipmentRow(comboKey, rowId)) return;

    const newLiveRow = this.#currentLiveState?.combos[comboKey]?.rows[rowId];
    if (newLiveRow && defaultDeliveryWindow) newLiveRow.deliveryWindow = defaultDeliveryWindow;

    this.#syncCapsAfterChange(comboKey, { clearErrorsWhenValid: true });
    this.#renderOrderSummary();

    // PDP/quickview: the new shipment row lives in #liveStates only — it is not
    // persisted to IDB until the user clicks Add/Update Order. Cart: the cart is
    // the committed state, so persist the added row immediately (auto-save model).
    if (this.isCart) {
      this.cartRecord = this.#buildCurrentRecord();
      const handle = this.#storageHandle;
      try {
        if (handle) {
          await saveProductRecord(handle, this.cartRecord, { productId: this.productJSON?.id });
          this.cartData = flattenProductRecordToLineItems(this.cartRecord);
          this.dispatchEvent(new CustomEvent('b2b:cart-saved', { bubbles: true }));
        }
      } catch (err) {
        console.error('b2b-plus: failed to persist new shipment row', err);
      }
    }
  }

  // ── Controller host interface ─────────────────────────────────────────────

  /**
   * Builds the narrow host-interface object passed to createBulkApplyController.
   * All private-field access is centralised here so the factory function has no
   * `#` references and can be read, tested, and eventually extracted independently.
   * @returns {Parameters<typeof createBulkApplyController>[0]}
   */
  #buildFiltersHost() {
    const h = this;
    return Object.freeze({
      get shadow()    { return h.#shadow; },
      inventoryTbody: () => h.#inventoryTbody(),
      on:             (...a) => h.#on(...a),
      // Row visibility drives the product-details totals, so the controller must
      // re-total after every filter pass.
      updateLineTotals: () => h.#updateProductLineTotalsFromInputs(),
    });
  }

  #buildBulkApplyHost() {
    const h = this;
    return Object.freeze({
      element:           h,
      get shadow()       { return h.#shadow; },
      get productJSON()  { return h.productJSON; },
      inventoryTbody:    () => h.#inventoryTbody(),
      on:                (...a) => h.#on(...a),
      setUserInteracted: () => { h.#userHasInteracted = true; },
      updateButtonState: () => h.#updateButtonState?.(),
      updateLineTotals:   () => h.#updateProductLineTotalsFromInputs(),
      // Bulk-apply writes many inputs at once — route through the shared sync so
      // the pooled caps settle after the batch too.
      updateSiblingCaps:  (ck) => h.#syncCapsAfterChange(ck),
      renderOrderSummary: () => h.#renderOrderSummary(),
      readBulkApplyMeta:  () => h.#readCartBulkApplyMetaForTemplate(),
      scheduleAutoSave:  () => h.#triggerSave({ debounce: false }),
    });
  }
}

customElements.define('b2b-plus-product-table', B2BPlusProductTable);
